import React, { useEffect, useMemo, useState } from "react";
import ReactDOM from "react-dom";
import { createRoot } from "react-dom/client";
import { AgGridReact } from "ag-grid-react";
import { themeQuartz } from "ag-grid-community";
import type { ColDef, ICellRendererParams } from "ag-grid-community";
import parsePhoneNumberFromString from "libphonenumber-js";
import { DRIVER_SCHEMAS, getDriverSchema } from "../sms/provider-fields";
import "./ag-setup";
import { measureSms } from "./sms_encoding";

function normalizePhone(input: string | null | undefined): string | null {
  if (!input) return null;
  const trimmed = String(input).trim();
  if (!trimmed) return null;
  const parsed = parsePhoneNumberFromString(trimmed);
  if (parsed && parsed.isValid()) return parsed.number.replace(/^\+/, "");
  const digits = trimmed.replace(/\D/g, "");
  return digits.length >= 6 ? digits : null;
}

const gridThemeLight = themeQuartz.withParams({
  accentColor: "#2563eb",
  borderRadius: 6,
  headerBackgroundColor: "#f9fafb",
  headerFontWeight: 600,
  fontFamily: "inherit",
});

const gridThemeDark = themeQuartz.withParams({
  accentColor: "#3b82f6",
  borderRadius: 6,
  backgroundColor: "#0f172a",
  foregroundColor: "#e5e7eb",
  headerBackgroundColor: "#1e293b",
  headerTextColor: "#e5e7eb",
  headerFontWeight: 600,
  fontFamily: "inherit",
  borderColor: "#1f2937",
  rowHoverColor: "#1e293b",
  oddRowBackgroundColor: "#0b1220",
});

type ThemeMode = "light" | "dark";
const ThemeContext = React.createContext<ThemeMode>("light");
const useTheme = () => React.useContext(ThemeContext);

type Provider = {
  id: number;
  name: string;
  login: string | null;
  password: string | null;
  api: Record<string, unknown>;
  created_at: string;
  updated_at: string;
};

type Account = {
  id: number;
  provider_id: number;
  provider_name?: string;
  provider_driver?: string;
  key: string;
  username: string | null;
  password: string | null;
  hlr_url: string | null;
  balance_url: string | null;
  balance: string | null;
  balance_currency: string | null;
  balance_updated_at: string | null;
  balance_low_threshold: string | null;
  created_at: string;
  updated_at: string;
};

type Phone = {
  id: number;
  phone: string;
  name: string | null;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  import_batch: string | null;
  hlr_status: string | null;
  hlr_operator: string | null;
  hlr_country: string | null;
  hlr_cost: string | null;
  hlr_checked_at: string | null;
  created_at: string;
  updated_at: string;
};

type UrlDestination = {
  id: number;
  url: string;
  name: string | null;
  description: string | null;
  countries: string[];
  created_at: string;
  updated_at: string;
};

type Country = {
  id: number;
  iso2: string;
  iso3: string;
  name: string;
  phone_code: string;
};

type Language = {
  id: number;
  code: string;
  name: string;
  native_name: string | null;
};

type Route = {
  id: number;
  provider: string;
  external_id: string;
  name: string;
  country_iso2: string | null;
  external_country_id: string | null;
  external_country_name: string | null;
  price: number | null;
  active: boolean;
  created_at: string;
  updated_at: string;
};

type Campaign = {
  id: number;
  name: string;
  sms_text: string;
  urls: number[];
  url_details?: { id: number; url: string; name: string | null }[];
  account_id: number | null;
  account_key?: string;
  provider_id?: number;
  provider_name?: string;
  pending_count: number;
  ready_count: number;
  error_count: number;
  total_count: number;
  country_id: number | null;
  country_iso2?: string | null;
  country_name?: string | null;
  language_id: number | null;
  language_code?: string | null;
  language_name?: string | null;
  sms_ai_variants: { active: boolean; text: string }[];
  sender_id: string | null;
  created_at: string;
  updated_at: string;
};

type SmsEdgeRoute = {
  id: number;
  name: string;
  country: string;
  countryId: number;
  price: number;
};

type CampaignPhone = {
  id: number;
  campaign_id: number;
  phone_id: number;
  status: "pending" | "ready" | "sent_provider" | "error" | "error_provider";
  error_message: string | null;
  sent_at: string | null;
  provider_message_id: string | null;
  created_at: string;
  updated_at: string;
  campaign_name?: string;
  phone?: string;
  short_domain?: string;
  slug?: string;
  route_id?: number | null;
  route_name?: string | null;
  route_external_id?: string | null;
};

type Tab =
  | "providers"
  | "accounts"
  | "phones"
  | "url_destinations"
  | "campaigns"
  | "domains"
  | "short_domains"
  | "white_domains"
  | "actions"
  | "callbacks"
  | "users"
  | "routes";

const TABS: { id: Tab; label: string; adminOnly?: boolean }[] = [
  { id: "campaigns", label: "Campaigns", adminOnly: true },
  { id: "actions", label: "Actions" },
  { id: "callbacks", label: "Callbacks", adminOnly: true },
  { id: "domains", label: "Domains", adminOnly: true },
  { id: "short_domains", label: "Short Domains", adminOnly: true },
  { id: "white_domains", label: "Candidate White Domains", adminOnly: true },
  { id: "providers", label: "Providers", adminOnly: true },
  { id: "accounts", label: "Accounts", adminOnly: true },
  { id: "phones", label: "Phones", adminOnly: true },
  { id: "url_destinations", label: "URL Destinations", adminOnly: true },
  { id: "routes", label: "Routes", adminOnly: true },
  { id: "users", label: "Users", adminOnly: true },
];

type ProviderCallback = {
  id: number;
  provider: string;
  method: string;
  url: string;
  query: Record<string, unknown>;
  headers: Record<string, unknown>;
  body: unknown;
  body_text: string | null;
  ip: string | null;
  matched: boolean;
  matched_status: string | null;
  message_id: string | null;
  created_at: string;
  updated_at: string;
};

type ActionRow = {
  id: number;
  type: "click" | "lead" | "deposit" | "sent";
  slug: string | null;
  campaign_phone_id: number | null;
  campaign_id: number | null;
  campaign_name: string | null;
  phone: string | null;
  sent_text: string | null;
  route_id: number | null;
  route_name: string | null;
  route_external_id: string | null;
  route_country_iso2: string | null;
  device: string | null;
  browser: string | null;
  cost: number;
  domain: string | null;
  url_destination_id: number | null;
  url_destination_name: string | null;
  url_destination_url: string | null;
  payload: Record<string, unknown>;
  ip: string | null;
  user_agent: string | null;
  created_at: string;
  updated_at: string;
};

type FunnelStat = {
  campaign_id: number | null;
  campaign_name: string | null;
  sent: number;
  clicks: number;
  leads: number;
  deposits: number;
  cost: number;
};

type FunnelStatByRoute = {
  route_id: number | null;
  route_name: string | null;
  route_external_id: string | null;
  route_country_iso2: string | null;
  sent: number;
  clicks: number;
  unique_clicks: number;
  leads: number;
  deposits: number;
  cost: number;
};

type ActionsStats = {
  totals: {
    sent: number;
    clicks: number;
    unique_clicks: number;
    leads: number;
    deposits: number;
    cost_sms: number;
    cost_hlr: number;
  };
  by_campaign: FunnelStat[];
  by_route: FunnelStatByRoute[];
};

type ShortDomain = {
  id: number;
  domain: string;
  provider: string | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
};

type Domain = {
  id: number;
  domain: string;
  tld: string;
  label: string;
  status: "DISCOVERED" | "CHECKING" | "CHECKED" | "REJECTED" | "ERROR";
  decision: "BUY_CANDIDATE" | "WATCHLIST" | "REJECT" | null;
  last_score: number | null;
  last_reason: string | null;
  last_checked_at: string | null;
  last_price: string | null;
  last_purchase_url: string | null;
  listing_type: string | null;
  ttf_topic: string | null;
  tf: number | null;
  cf: number | null;
  da: number | null;
  backlinks: number | null;
  wayback_snapshots: number | null;
  web_risk_status: "CLEAN" | "UNSAFE" | "UNKNOWN" | null;
  auction_end_date: string | null;
  auto_bid_armed: boolean;
  auto_bid_max: string | null;
  purchase_status:
    | "watching"
    | "armed"
    | "bidding"
    | "winning"
    | "won"
    | "lost"
    | "bought"
    | "failed";
  purchase_amount: string | null;
  purchased_at: string | null;
  cloudflare_zone_id: string | null;
  cloudflare_zone_status: string | null;
  cloudflare_provisioned_at: string | null;
  cloudflare_last_error: string | null;
  created_at: string;
  updated_at: string;
};

type DomainBid = {
  id: number;
  domain_id: number;
  amount: number;
  current_high_bid: number | null;
  status: "PLACED" | "DRY_RUN" | "SKIPPED" | "FAILED" | "WON" | "LOST";
  message: string | null;
  created_at: string;
};

const TAB_IDS = new Set<Tab>(TABS.map((t) => t.id));

type AppRoute = { tab: Tab; sub: string | null };

function readRouteFromHash(): AppRoute {
  const raw = window.location.hash.replace(/^#/, "");
  const [t, ...rest] = raw.split("/");
  const tab = (TAB_IDS.has(t as Tab) ? t : "campaigns") as Tab;
  const sub = rest.length > 0 ? rest.join("/") : null;
  return { tab, sub };
}

function setHash(tab: Tab, sub: string | null = null) {
  const next = sub ? `${tab}/${sub}` : tab;
  if (window.location.hash.replace(/^#/, "") !== next) {
    window.location.hash = next;
  }
}

type AuthUser = {
  email: string;
  name?: string;
  picture?: string;
  role: "admin" | "analyst";
  labels: string[];
};

declare global {
  interface Window {
    google?: {
      accounts?: {
        id?: {
          initialize: (opts: any) => void;
          renderButton: (parent: HTMLElement, opts: any) => void;
          prompt: () => void;
          cancel: () => void;
          disableAutoSelect: () => void;
        };
      };
    };
  }
}

// Global 401 handler — wraps fetch so any unauthorized response from a protected
// endpoint triggers a redirect to the login screen.
const originalFetch = window.fetch.bind(window);
let onUnauthorized: (() => void) | null = null;
const wrappedFetch = async (...args: Parameters<typeof originalFetch>) => {
  const res = await originalFetch(...args);
  if (res.status === 401) {
    const url = typeof args[0] === "string" ? args[0] : (args[0] as Request).url;
    if (!url.includes("/api/auth/me") && !url.includes("/api/auth/google")) {
      onUnauthorized?.();
    }
  }
  return res;
};
(window as any).fetch = wrappedFetch;

function LoginScreen({ onSuccess }: { onSuccess: (user: AuthUser) => void }) {
  const btnRef = React.useRef<HTMLDivElement>(null);
  const [error, setError] = useState<string | null>(null);
  const [config, setConfig] = useState<{ google_client_id: string } | null>(null);

  useEffect(() => {
    fetch("/api/auth/config")
      .then((r) => r.json())
      .then(setConfig)
      .catch(() => setError("Failed to load auth config"));
  }, []);

  useEffect(() => {
    if (!config?.google_client_id || !btnRef.current) return;
    let cancelled = false;
    const init = () => {
      if (cancelled) return;
      const gid = window.google?.accounts?.id;
      if (!gid) {
        setTimeout(init, 100);
        return;
      }
      gid.initialize({
        client_id: config.google_client_id,
        callback: async (response: { credential: string }) => {
          setError(null);
          try {
            const res = await originalFetch("/api/auth/google", {
              method: "POST",
              headers: { "content-type": "application/json" },
              body: JSON.stringify({ credential: response.credential }),
            });
            if (!res.ok) {
              const text = await res.text();
              setError(text || "Login failed");
              return;
            }
            const user = (await res.json()) as AuthUser;
            onSuccess(user);
          } catch (e: any) {
            setError(String(e?.message ?? e));
          }
        },
      });
      gid.renderButton(btnRef.current!, {
        theme: "outline",
        size: "large",
        text: "signin_with",
        shape: "rectangular",
        logo_alignment: "left",
      });
    };
    init();
    return () => {
      cancelled = true;
    };
  }, [config, onSuccess]);

  return (
    <div className="flex h-full items-center justify-center bg-gray-50 dark:bg-gray-950">
      <div className="w-[400px] bg-white dark:bg-gray-900 rounded-xl shadow-lg border border-gray-200 dark:border-gray-800 p-8">
        <h1 className="text-2xl font-semibold mb-1 dark:text-gray-100">SMS Sendout</h1>
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
          Sign in with your authorized Google account.
        </p>
        <div className="flex justify-center">
          <div ref={btnRef} />
        </div>
        {!config && (
          <div className="mt-4 text-xs text-gray-400 text-center">Loading...</div>
        )}
        {error && (
          <div className="mt-4 px-3 py-2 rounded text-xs bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/30 text-red-700 dark:text-red-300">
            {error}
          </div>
        )}
      </div>
    </div>
  );
}

function Root() {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    onUnauthorized = () => setUser(null);
    return () => {
      onUnauthorized = null;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    originalFetch("/api/auth/me")
      .then((r) => (r.ok ? r.json() : null))
      .then((u) => {
        if (!cancelled) {
          setUser(u);
          setChecking(false);
        }
      })
      .catch(() => {
        if (!cancelled) setChecking(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (checking) {
    return (
      <div className="h-full flex items-center justify-center text-gray-400">
        Loading...
      </div>
    );
  }

  if (!user) {
    return <LoginScreen onSuccess={setUser} />;
  }

  return <App user={user} onLogout={() => setUser(null)} />;
}

function App({ user, onLogout }: { user: AuthUser; onLogout: () => void }) {
  const isAdmin = user.role === "admin";
  const visibleTabs = isAdmin ? TABS : TABS.filter((t) => !t.adminOnly);
  const allowedTabIds = new Set(visibleTabs.map((t) => t.id));

  const [route, setRoute] = useState<AppRoute>(() => readRouteFromHash());
  // Force-redirect a non-admin trying to land on an admin tab to "actions".
  const tab: Tab = allowedTabIds.has(route.tab) ? route.tab : "actions";

  const setTab = (next: Tab) => setHash(next, null);

  useEffect(() => {
    const onHashChange = () => setRoute(readRouteFromHash());
    window.addEventListener("hashchange", onHashChange);
    return () => window.removeEventListener("hashchange", onHashChange);
  }, []);

  useEffect(() => {
    if (!window.location.hash) window.location.hash = tab;
    // If the user is on a hidden tab via URL, rewrite to actions.
    if (!allowedTabIds.has(route.tab)) setHash("actions", null);
  }, []);

  const [theme, setTheme] = useState<ThemeMode>(() => {
    const saved = localStorage.getItem("theme") as ThemeMode | null;
    if (saved === "dark" || saved === "light") return saved;
    return window.matchMedia?.("(prefers-color-scheme: dark)").matches
      ? "dark"
      : "light";
  });

  useEffect(() => {
    document.documentElement.classList.toggle("dark", theme === "dark");
    localStorage.setItem("theme", theme);
  }, [theme]);

  return (
    <ThemeContext.Provider value={theme}>
      <div className="flex flex-col h-full">
        <header className="flex items-center gap-2 px-6 py-3 bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800">
          <h1 className="text-lg font-semibold mr-4 dark:text-gray-100">SMS</h1>
          <nav className="flex gap-1">
            {visibleTabs.map((t) => (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                className={
                  "px-4 py-1.5 rounded-md text-sm font-medium transition cursor-pointer " +
                  (tab === t.id
                    ? "bg-blue-50 text-blue-700 dark:bg-blue-500/15 dark:text-blue-300"
                    : "text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-800")
                }
              >
                {t.label}
              </button>
            ))}
          </nav>
          <div className="ml-auto flex items-center gap-2">
            <button
              onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
              className="p-2 rounded-md text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-800 cursor-pointer transition"
              title={theme === "dark" ? "Switch to light theme" : "Switch to dark theme"}
              aria-label="Toggle theme"
            >
              <Icon name={theme === "dark" ? "sun" : "moon"} />
            </button>
            <div className="flex items-center gap-2 pl-2 ml-1 border-l border-gray-200 dark:border-gray-800">
              {user.picture && (
                <img
                  src={user.picture}
                  alt=""
                  className="w-7 h-7 rounded-full"
                  referrerPolicy="no-referrer"
                />
              )}
              <span className="text-xs text-gray-600 dark:text-gray-400 hidden sm:block">
                {user.email}
              </span>
              <span
                className={
                  "text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded " +
                  (user.role === "admin"
                    ? "bg-purple-100 dark:bg-purple-500/15 text-purple-700 dark:text-purple-300"
                    : "bg-blue-100 dark:bg-blue-500/15 text-blue-700 dark:text-blue-300")
                }
                title={
                  user.role === "analyst"
                    ? `Analyst — scoped to labels: ${user.labels.join(", ") || "(none)"}`
                    : "Administrator"
                }
              >
                {user.role}
              </span>
              <button
                onClick={async () => {
                  await originalFetch("/api/auth/logout", { method: "POST" });
                  window.google?.accounts?.id?.disableAutoSelect();
                  onLogout();
                }}
                className="px-3 py-1.5 rounded-md text-sm text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-800 cursor-pointer"
                title="Logout"
              >
                Logout
              </button>
            </div>
          </div>
        </header>
        <main className="flex-1 overflow-hidden p-6">
          {tab === "campaigns" && <CampaignsTab sub={route.sub} />}
          {tab === "actions" && <ActionsTab />}
          {tab === "callbacks" && <CallbacksTab />}
          {tab === "routes" && <RoutesTab />}
          {tab === "domains" && <DomainsTab />}
          {tab === "short_domains" && <ShortDomainsTab />}
          {tab === "white_domains" && <CandidateWhiteDomainsTab />}
          {tab === "providers" && <ProvidersTab />}
          {tab === "accounts" && <AccountsTab />}
          {tab === "phones" && <PhonesTab />}
          {tab === "url_destinations" && <UrlDestinationsTab />}
          {tab === "users" && <UsersTab />}
        </main>
      </div>
    </ThemeContext.Provider>
  );
}

function useAsync<T>(loader: () => Promise<T>, deps: unknown[] = []) {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [tick, setTick] = useState(0);
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    loader().then((d) => {
      if (!cancelled) {
        setData(d);
        setLoading(false);
      }
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, tick]);
  return { data, loading, reload: () => setTick((t) => t + 1) };
}

function PageShell({
  title,
  total,
  onAdd,
  addLabel = "Add",
  extraHeader,
  children,
}: {
  title: string;
  total: number;
  onAdd?: () => void;
  addLabel?: string;
  extraHeader?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col h-full bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-800 overflow-hidden">
      <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200 dark:border-gray-800">
        <h2 className="text-xl font-semibold dark:text-gray-100">{title}</h2>
        <div className="flex items-center gap-2">
          <div className="px-3 py-1.5 rounded-md border border-gray-200 dark:border-gray-700 text-sm">
            <span className="font-semibold text-blue-600 dark:text-blue-400 mr-1">{total}</span>
            <span className="text-gray-500 dark:text-gray-400">Total</span>
          </div>
          {extraHeader}
          {onAdd && (
            <button
              onClick={onAdd}
              className="inline-flex items-center gap-1 px-4 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-md text-sm font-medium cursor-pointer transition"
            >
              <Icon name="plus" /> {addLabel}
            </button>
          )}
        </div>
      </div>
      <div className="flex-1 overflow-hidden min-h-0">{children}</div>
    </div>
  );
}

type ExtraAction<T> = {
  icon: React.ReactNode;
  onClick: (row: T) => void;
  title: string;
  className?: string;
};

function ActionsCellRenderer<T extends { id: number }>(
  onEdit: ((row: T) => void) | null,
  onDelete: ((row: T) => void) | null,
  extras?: (row: T) => ExtraAction<T>[],
) {
  return (params: ICellRendererParams<T>) => {
    const row = params.data;
    if (!row) return null;
    const extraActions = extras?.(row) ?? [];
    return (
      <div className="inline-flex gap-1 items-center h-full">
        {extraActions.map((a, i) => (
          <button
            key={i}
            onClick={() => a.onClick(row)}
            className={
              "p-1.5 rounded cursor-pointer transition " +
              (a.className ?? "text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-800")
            }
            title={a.title}
            aria-label={a.title}
          >
            {a.icon}
          </button>
        ))}
        {onEdit && (
          <button
            onClick={() => onEdit(row)}
            className="p-1.5 rounded text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-500/10 cursor-pointer transition"
            title="Edit"
            aria-label="Edit"
          >
            <Icon name="edit" />
          </button>
        )}
        {onDelete && (
          <button
            onClick={() => onDelete(row)}
            className="p-1.5 rounded text-red-600 hover:bg-red-50 dark:hover:bg-red-500/10 cursor-pointer transition"
            title="Delete"
            aria-label="Delete"
          >
            <Icon name="trash" />
          </button>
        )}
      </div>
    );
  };
}

// Module-scoped so the same reference is passed to ag-grid every render —
// changing the aggFuncs prop reference can trigger column state resets.
const AG_AGG_FUNCS = {
  uniqueCount: (params: any) => {
    const out = new Set<unknown>();
    for (const v of params.values ?? []) {
      if (v instanceof Set) {
        for (const x of v) out.add(x);
      } else if (v != null) {
        out.add(v);
      }
    }
    return out;
  },
};

function AgTable<T extends { id: number }>({
  rows,
  columns,
  onEdit,
  onDelete,
  extraActions,
  actionsWidth = 120,
  autoGroupExpand = 1,
  persistKey,
  hideId = false,
  selection,
  onSelectionChange,
  groupable = false,
  filterResetKey = 0,
}: {
  rows: T[];
  columns: ColDef<T>[];
  onEdit?: (row: T) => void;
  onDelete?: (row: T) => void;
  extraActions?: (row: T) => ExtraAction<T>[];
  actionsWidth?: number;
  autoGroupExpand?: number;
  persistKey?: string;
  hideId?: boolean;
  selection?: "checkbox";
  onSelectionChange?: (rows: T[]) => void;
  /** Show the row-group panel so users can drag columns to group by. */
  groupable?: boolean;
  /** Bump this number to clear all in-grid filters from the parent. */
  filterResetKey?: number;
}) {
  const defaultColDef = useMemo<ColDef<T>>(
    () => ({
      sortable: true,
      filter: true,
      resizable: true,
      floatingFilter: false,
      flex: 1,
      minWidth: 100,
      // When the table is groupable, every column becomes draggable into the
      // group panel. Individual columns can still opt out via enableRowGroup:false.
      enableRowGroup: groupable,
    }),
    [groupable],
  );

  // Stable presence flags — only flip when caller toggles a callback
  // defined→undefined. Inline arrow churn doesn't affect these.
  const hasEdit = !!onEdit;
  const hasDelete = !!onDelete;
  const hasExtras = !!extraActions;
  const hasActions = hasEdit || hasDelete || hasExtras;

  // Hold callbacks in refs so parents passing inline arrows
  // (`onEdit={(r) => setEditing(r)}`) don't churn the colDefs ref on every
  // render. Inline arrows are a new function each render; the refs absorb
  // that, and the Actions cellRenderer reads from the refs at click time.
  const onEditRef = React.useRef(onEdit);
  const onDeleteRef = React.useRef(onDelete);
  const extraActionsRef = React.useRef(extraActions);
  React.useEffect(() => {
    onEditRef.current = onEdit;
  }, [onEdit]);
  React.useEffect(() => {
    onDeleteRef.current = onDelete;
  }, [onDelete]);
  React.useEffect(() => {
    extraActionsRef.current = extraActions;
  }, [extraActions]);

  const colDefs = useMemo<ColDef<T>[]>(() => {
    const cols: ColDef<T>[] = [];
    if (!hideId) {
      cols.push({
        field: "id" as any,
        headerName: "ID",
        width: 90,
        flex: 0,
        filter: "agNumberColumnFilter",
        pinned: "left",
      });
    }
    cols.push(...columns);
    if (hasActions) {
      cols.push({
        headerName: "Actions",
        width: actionsWidth,
        flex: 0,
        pinned: "right",
        sortable: false,
        filter: false,
        resizable: false,
        suppressMovable: true,
        cellRenderer: ActionsCellRenderer<T>(
          hasEdit ? (row) => onEditRef.current?.(row) : null,
          hasDelete ? (row) => onDeleteRef.current?.(row) : null,
          hasExtras ? (row) => extraActionsRef.current?.(row) ?? [] : undefined,
        ),
        cellStyle: { display: "flex", alignItems: "center", justifyContent: "flex-end" },
      });
    }
    return cols;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columns, hasEdit, hasDelete, hasExtras, actionsWidth, hideId, selection]);

  // Hold the live grid api so we can re-apply persisted column state whenever
  // colDefs changes (caller passes a new `columns` array literal on every
  // render — ag-grid then re-creates columns and resets widths). Re-applying
  // saved widths/order/filters from localStorage fixes that.
  const gridApiRef = React.useRef<any>(null);
  const applySavedState = React.useCallback(
    (api: any) => {
      if (!persistKey || !api) return;
      try {
        const raw = localStorage.getItem(`ag.${persistKey}`);
        if (!raw) return;
        const saved = JSON.parse(raw);
        if (saved.filter) api.setFilterModel(saved.filter);
        if (saved.columns) {
          api.applyColumnState({
            state: saved.columns,
            applyOrder: true,
          });
        }
      } catch {
        /* ignore corrupt state */
      }
    },
    [persistKey],
  );
  const onGridReady = (params: any) => {
    gridApiRef.current = params.api;
    applySavedState(params.api);
  };
  // Re-apply ONLY when the structural shape of the columns changes (field /
  // headerName / colId). Parent components inline `columns={[...]}` literals
  // on every render — same content, fresh array ref — and the old check on
  // colDefs ref caused applyColumnState to fire on every poll tick, which
  // ag-grid renders as a visible flash. Hashing the structural part filters
  // out the noise: we only reapply state on the rare event of new/removed/
  // renamed columns.
  const colShapeKey = useMemo(
    () =>
      colDefs
        .map((c) => `${c.field ?? ""}|${c.colId ?? ""}|${c.headerName ?? ""}`)
        .join("§"),
    [colDefs],
  );
  // Allow the parent to clear all in-grid filters via a "bump" counter.
  // Persist the cleared state to localStorage so the reset survives reloads.
  const initialFilterResetKey = React.useRef(filterResetKey);
  useEffect(() => {
    if (filterResetKey === initialFilterResetKey.current) return;
    const api = gridApiRef.current;
    if (!api) return;
    api.setFilterModel(null);
    if (persistKey) {
      try {
        const raw = localStorage.getItem(`ag.${persistKey}`);
        const saved = raw ? JSON.parse(raw) : {};
        saved.filter = null;
        localStorage.setItem(`ag.${persistKey}`, JSON.stringify(saved));
      } catch {
        /* ignore */
      }
    }
  }, [filterResetKey, persistKey]);

  const lastAppliedShapeRef = React.useRef<string | null>(null);
  useEffect(() => {
    if (!gridApiRef.current) return;
    if (lastAppliedShapeRef.current === colShapeKey) return;
    lastAppliedShapeRef.current = colShapeKey;
    applySavedState(gridApiRef.current);
  }, [colShapeKey, applySavedState]);

  const persistState = (api: any) => {
    if (!persistKey) return;
    try {
      const state = {
        filter: api.getFilterModel(),
        columns: api.getColumnState(),
      };
      localStorage.setItem(`ag.${persistKey}`, JSON.stringify(state));
    } catch {
      /* ignore quota errors */
    }
  };

  return (
    <div className="h-full w-full">
      <AgGridReact<T>
        theme={useTheme() === "dark" ? gridThemeDark : gridThemeLight}
        rowData={rows}
        columnDefs={colDefs}
        defaultColDef={defaultColDef}
        aggFuncs={AG_AGG_FUNCS}
        onGridReady={onGridReady}
        onFilterChanged={(e) => persistState(e.api)}
        onSortChanged={(e) => persistState(e.api)}
        onColumnMoved={(e) => persistState(e.api)}
        onColumnVisible={(e) => persistState(e.api)}
        onColumnResized={(e) => persistState(e.api)}
        onColumnRowGroupChanged={(e) => persistState(e.api)}
        onColumnValueChanged={(e) => persistState(e.api)}
        onColumnPivotChanged={(e) => persistState(e.api)}
        onColumnPinned={(e) => persistState(e.api)}
        // Catch-all — fires whenever the set of displayed columns changes for
        // ANY reason (including Columns sidebar checkbox toggles, which some
        // ag-grid builds don't surface via onColumnVisible).
        onDisplayedColumnsChanged={(e) => persistState(e.api)}
        animateRows
        rowSelection={
          selection === "checkbox"
            ? {
                mode: "multiRow",
                checkboxes: true,
                headerCheckbox: true,
                selectAll: "filtered", // header toggles only the visible/filtered rows
                enableClickSelection: false,
              }
            : { mode: "multiRow", checkboxes: false, headerCheckbox: false }
        }
        onSelectionChanged={(e) => {
          if (selection === "checkbox" && onSelectionChange) {
            const sel = (e.api.getSelectedRows?.() ?? []) as T[];
            onSelectionChange(sel);
          }
        }}
        cellSelection
        pagination
        paginationPageSize={50}
        paginationPageSizeSelector={[20, 50, 100, 200]}
        groupDefaultExpanded={autoGroupExpand}
        rowGroupPanelShow={groupable ? "always" : "never"}
        suppressDragLeaveHidesColumns={groupable}
        sideBar={{
          toolPanels: [
            { id: "columns", labelDefault: "Columns", labelKey: "columns", iconKey: "columns", toolPanel: "agColumnsToolPanel" },
            { id: "filters", labelDefault: "Filters", labelKey: "filters", iconKey: "filter", toolPanel: "agFiltersToolPanel" },
          ],
        }}
        getRowId={(p) => String(p.data.id)}
      />
    </div>
  );
}

function Drawer({
  title,
  open,
  onClose,
  children,
  onSave,
  saving,
  width = 440,
  saveLabel = "Save",
  showSave = true,
}: {
  title: string;
  open: boolean;
  onClose: () => void;
  children: React.ReactNode;
  onSave: () => void;
  saving: boolean;
  width?: number;
  saveLabel?: string;
  showSave?: boolean;
}) {
  const [mounted, setMounted] = useState(false);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (open) {
      setMounted(true);
      let r2 = 0;
      const r1 = requestAnimationFrame(() => {
        r2 = requestAnimationFrame(() => setVisible(true));
      });
      return () => {
        cancelAnimationFrame(r1);
        cancelAnimationFrame(r2);
      };
    } else if (mounted) {
      setVisible(false);
      const t = setTimeout(() => setMounted(false), 300);
      return () => clearTimeout(t);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!mounted) return null;

  // Render the modal via a portal anchored on document.body so frequent
  // re-renders of the parent component (e.g. CampaignEditor polling
  // liveStatus every 2s) don't reconcile the drawer's DOM and cause its
  // slide-in animation to replay or visibly jitter.
  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-50">
      <div
        className={
          "absolute inset-0 bg-black/30 transition-opacity duration-300 " +
          (visible ? "opacity-100" : "opacity-0")
        }
        onClick={onClose}
      />
      <div
        style={{ width: `${width}px` }}
        className={
          "drawer-panel absolute top-0 right-0 bottom-0 bg-white dark:bg-gray-900 shadow-2xl flex flex-col " +
          (visible ? "is-open" : "")
        }
      >
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200 dark:border-gray-800">
          <h3 className="text-lg font-semibold dark:text-gray-100">{title}</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 cursor-pointer p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-800"
            aria-label="Close"
          >
            <Icon name="x" />
          </button>
        </div>
        <div className="flex-1 overflow-auto p-5 space-y-4">{children}</div>
        <div className="px-5 py-3 border-t border-gray-200 dark:border-gray-800 flex justify-end gap-2">
          <button
            onClick={onClose}
            className="px-4 py-1.5 rounded-md border border-gray-300 dark:border-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 text-sm cursor-pointer"
          >
            {showSave ? "Cancel" : "Close"}
          </button>
          {showSave && (
            <button
              onClick={onSave}
              disabled={saving}
              className="px-4 py-1.5 rounded-md bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-medium cursor-pointer"
            >
              {saving ? `${saveLabel}…` : saveLabel}
            </button>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}

function ConfirmDialog({
  open,
  title,
  message,
  confirmLabel = "Delete",
  onConfirm,
  onCancel,
  destructive = true,
}: {
  open: boolean;
  title: string;
  message: React.ReactNode;
  confirmLabel?: string;
  onConfirm: () => void;
  onCancel: () => void;
  destructive?: boolean;
}) {
  const [mounted, setMounted] = useState(false);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (open) {
      setMounted(true);
      let r2 = 0;
      const r1 = requestAnimationFrame(() => {
        r2 = requestAnimationFrame(() => setVisible(true));
      });
      return () => {
        cancelAnimationFrame(r1);
        cancelAnimationFrame(r2);
      };
    } else if (mounted) {
      setVisible(false);
      const t = setTimeout(() => setMounted(false), 200);
      return () => clearTimeout(t);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onCancel();
      if (e.key === "Enter") onConfirm();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onCancel, onConfirm]);

  if (!mounted) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center">
      <div
        className={
          "absolute inset-0 bg-black/40 transition-opacity duration-200 " +
          (visible ? "opacity-100" : "opacity-0")
        }
        onClick={onCancel}
      />
      <div
        className={
          "relative bg-white dark:bg-gray-900 rounded-lg shadow-2xl w-[420px] transform transition-all duration-200 " +
          (visible ? "opacity-100 scale-100" : "opacity-0 scale-95")
        }
      >
        <div className="p-5 flex gap-4">
          <div
            className={
              "shrink-0 w-10 h-10 rounded-full flex items-center justify-center " +
              (destructive
                ? "bg-red-100 dark:bg-red-500/15 text-red-600 dark:text-red-400"
                : "bg-blue-100 dark:bg-blue-500/15 text-blue-600 dark:text-blue-400")
            }
          >
            <Icon name="alert" />
          </div>
          <div className="flex-1">
            <h3 className="text-base font-semibold mb-1 dark:text-gray-100">{title}</h3>
            <div className="text-sm text-gray-600 dark:text-gray-400">{message}</div>
          </div>
        </div>
        <div className="px-5 py-3 border-t border-gray-200 dark:border-gray-800 flex justify-end gap-2">
          <button
            onClick={onCancel}
            className="px-4 py-1.5 rounded-md border border-gray-300 dark:border-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 text-sm cursor-pointer"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            className={
              "px-4 py-1.5 rounded-md text-white text-sm font-medium cursor-pointer " +
              (destructive
                ? "bg-red-600 hover:bg-red-700"
                : "bg-blue-600 hover:bg-blue-700")
            }
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

function Icon({ name }: { name: "edit" | "trash" | "x" | "alert" | "plus" | "sun" | "moon" | "phone" | "send" }) {
  const common = {
    width: 16,
    height: 16,
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 2,
    strokeLinecap: "round" as const,
    strokeLinejoin: "round" as const,
  };
  switch (name) {
    case "edit":
      return (
        <svg {...common}>
          <path d="M12 20h9" />
          <path d="M16.5 3.5a2.121 2.121 0 1 1 3 3L7 19l-4 1 1-4 12.5-12.5z" />
        </svg>
      );
    case "trash":
      return (
        <svg {...common}>
          <polyline points="3 6 5 6 21 6" />
          <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
          <path d="M10 11v6" />
          <path d="M14 11v6" />
          <path d="M9 6V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2" />
        </svg>
      );
    case "x":
      return (
        <svg {...common}>
          <line x1="18" y1="6" x2="6" y2="18" />
          <line x1="6" y1="6" x2="18" y2="18" />
        </svg>
      );
    case "alert":
      return (
        <svg {...common} width={20} height={20}>
          <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
          <line x1="12" y1="9" x2="12" y2="13" />
          <line x1="12" y1="17" x2="12.01" y2="17" />
        </svg>
      );
    case "plus":
      return (
        <svg {...common}>
          <line x1="12" y1="5" x2="12" y2="19" />
          <line x1="5" y1="12" x2="19" y2="12" />
        </svg>
      );
    case "sun":
      return (
        <svg {...common} width={18} height={18}>
          <circle cx="12" cy="12" r="4" />
          <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41" />
        </svg>
      );
    case "moon":
      return (
        <svg {...common} width={18} height={18}>
          <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
        </svg>
      );
    case "phone":
      return (
        <svg {...common}>
          <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.37 1.9.72 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.91.35 1.85.59 2.81.72A2 2 0 0 1 22 16.92z" />
        </svg>
      );
    case "send":
      return (
        <svg {...common}>
          <line x1="22" y1="2" x2="11" y2="13" />
          <polygon points="22 2 15 22 11 13 2 9 22 2" />
        </svg>
      );
  }
}

function Field({
  label,
  required,
  error,
  children,
}: {
  label: string;
  required?: boolean;
  error?: string | null;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className="block text-xs font-semibold tracking-wide text-gray-500 dark:text-gray-400 uppercase mb-1">
        {label}
        {required && <span className="text-red-500 ml-0.5">*</span>}
      </span>
      {children}
      {error && (
        <span className="block mt-1 text-xs text-red-600 dark:text-red-400">{error}</span>
      )}
    </label>
  );
}

const inputBaseCls =
  "w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 dark:bg-gray-800 dark:text-gray-100 placeholder:text-gray-400 dark:placeholder:text-gray-500 placeholder:font-normal";
const inputCls =
  inputBaseCls +
  " border-gray-300 dark:border-gray-700 focus:ring-blue-500";
const inputErrorCls =
  inputBaseCls +
  " border-red-400 dark:border-red-500 focus:ring-red-500";

interface ComboOption {
  value: string;
  label: string;
  searchKey: string;
}

/**
 * Searchable single-select combobox. Type to filter, click to pick, Esc to
 * cancel. Replaces native <select> for long lists (countries, languages).
 */
function Combobox({
  value,
  onChange,
  options,
  placeholder = "— select —",
  allowClear = true,
}: {
  value: string;
  onChange: (next: string) => void;
  options: ComboOption[];
  placeholder?: string;
  allowClear?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const wrapRef = React.useRef<HTMLDivElement>(null);
  const inputRef = React.useRef<HTMLInputElement>(null);

  const selected = useMemo(
    () => options.find((o) => o.value === value) ?? null,
    [options, value],
  );

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false);
        setQuery("");
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) => o.searchKey.toLowerCase().includes(q));
  }, [options, query]);

  const displayValue = open ? query : selected?.label ?? "";

  return (
    <div ref={wrapRef} className="relative">
      <input
        ref={inputRef}
        type="text"
        className={inputCls + " pr-8"}
        value={displayValue}
        placeholder={placeholder}
        onFocus={() => setOpen(true)}
        onChange={(e) => {
          setQuery(e.target.value);
          if (!open) setOpen(true);
        }}
        onKeyDown={(e) => {
          if (e.key === "Escape") {
            setOpen(false);
            setQuery("");
            (e.target as HTMLInputElement).blur();
          } else if (e.key === "Enter" && open && filtered[0]) {
            e.preventDefault();
            onChange(filtered[0].value);
            setOpen(false);
            setQuery("");
          }
        }}
      />
      {selected && allowClear && !open && (
        <button
          type="button"
          onClick={() => onChange("")}
          className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 text-sm cursor-pointer"
          tabIndex={-1}
          title="Clear"
        >
          ×
        </button>
      )}
      {!selected && !open && (
        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none">
          ▾
        </span>
      )}
      {open && (
        <div className="absolute left-0 right-0 top-full mt-1 z-30 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md shadow-lg max-h-[260px] overflow-y-auto">
          {filtered.length === 0 ? (
            <div className="p-2 text-xs text-gray-400">No matches</div>
          ) : (
            filtered.map((o) => (
              <button
                key={o.value}
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => {
                  onChange(o.value);
                  setOpen(false);
                  setQuery("");
                }}
                className={
                  "block w-full text-left px-3 py-1.5 text-sm cursor-pointer transition " +
                  (o.value === value
                    ? "bg-blue-50 dark:bg-blue-500/15 text-blue-700 dark:text-blue-300"
                    : "hover:bg-gray-50 dark:hover:bg-gray-700 dark:text-gray-200")
                }
              >
                {o.label}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}

// ---------- Providers ----------

function ProvidersTab() {
  const { data, loading, reload } = useAsync<Provider[]>(() =>
    fetch("/api/providers").then((r) => r.json() as Promise<Provider[]>),
  );
  const [editing, setEditing] = useState<Provider | "new" | null>(null);
  const [deleting, setDeleting] = useState<Provider | null>(null);

  return (
    <PageShell
      title="Providers"
      total={data?.length ?? 0}
      onAdd={() => setEditing("new")}
    >
      {loading ? (
        <div className="p-6 text-gray-400">Loading...</div>
      ) : (
        <AgTable<Provider>
          rows={data ?? []}
          columns={[
            {
              field: "name",
              headerName: "Name",
              cellRenderer: (p: ICellRendererParams<Provider>) => (
                <span className="font-medium">{p.value}</span>
              ),
            },
            {
              field: "login",
              headerName: "Login",
              valueFormatter: (p) => p.value ?? "—",
            },
            {
              headerName: "API Keys",
              colId: "apiKeys",
              valueGetter: (p) => Object.keys(p.data?.api ?? {}).length,
              valueFormatter: (p) => `${p.value} field(s)`,
              filter: "agNumberColumnFilter",
            },
            {
              field: "created_at",
              headerName: "Created",
              filter: "agDateColumnFilter",
              valueFormatter: (p) =>
                p.value ? new Date(p.value).toLocaleString() : "",
            },
          ]}
          onEdit={(r) => setEditing(r)}
          onDelete={(r) => setDeleting(r)}
        />
      )}
      {editing && (
        <ProviderDrawer
          provider={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            reload();
          }}
        />
      )}
      <ConfirmDialog
        open={!!deleting}
        title="Delete provider"
        message={
          deleting && (
            <>
              Are you sure you want to delete provider{" "}
              <span className="font-semibold text-gray-900 dark:text-gray-100">"{deleting.name}"</span>?
              This will also remove its accounts.
            </>
          )
        }
        onCancel={() => setDeleting(null)}
        onConfirm={async () => {
          if (!deleting) return;
          await fetch(`/api/providers/${deleting.id}`, { method: "DELETE" });
          setDeleting(null);
          reload();
        }}
      />
    </PageShell>
  );
}

function ProviderDrawer({
  provider,
  onClose,
  onSaved,
}: {
  provider: Provider | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const initialApi = (provider?.api ?? {}) as Record<string, unknown>;
  const [name, setName] = useState(provider?.name ?? "");
  const [login, setLogin] = useState(provider?.login ?? "");
  const [password, setPassword] = useState(provider?.password ?? "");
  const [driver, setDriver] = useState<string>(
    (initialApi.driver as string | undefined) ?? "",
  );
  const [fieldValues, setFieldValues] = useState<Record<string, string>>(() => {
    const out: Record<string, string> = {};
    for (const [k, v] of Object.entries(initialApi)) {
      if (k === "driver" || v == null) continue;
      out[k] = String(v);
    }
    return out;
  });
  // Raw JSON fallback for custom / unknown drivers.
  const [apiText, setApiText] = useState(
    JSON.stringify(provider?.api ?? {}, null, 2),
  );
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [nameError, setNameError] = useState<string | null>(null);
  const [apiError, setApiError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string | null>>({});

  const schema = getDriverSchema(driver);

  function setField(key: string, value: string) {
    setFieldValues((s) => ({ ...s, [key]: value }));
    if (fieldErrors[key]) setFieldErrors((e) => ({ ...e, [key]: null }));
  }

  async function save() {
    setFormError(null);
    setNameError(null);
    setApiError(null);
    setFieldErrors({});

    let hasError = false;
    if (!name.trim()) {
      setNameError("Name is required");
      hasError = true;
    }

    let api: Record<string, unknown> = {};
    if (schema) {
      // Preserve any keys not described by the schema.
      const known = new Set(schema.fields.map((f) => f.key));
      const extras: Record<string, unknown> = {};
      for (const [k, v] of Object.entries(initialApi)) {
        if (k !== "driver" && !known.has(k)) extras[k] = v;
      }
      api = { ...extras, driver };

      const nextFieldErrors: Record<string, string | null> = {};
      for (const f of schema.fields) {
        const raw = (fieldValues[f.key] ?? "").trim();
        if (!raw) {
          if (f.required) {
            nextFieldErrors[f.key] = `${f.label} is required`;
            hasError = true;
          }
          continue;
        }
        if (f.type === "number") {
          const n = Number(raw);
          if (Number.isNaN(n)) {
            nextFieldErrors[f.key] = "Must be a number";
            hasError = true;
          } else api[f.key] = n;
        } else {
          api[f.key] = raw;
        }
      }
      setFieldErrors(nextFieldErrors);
    } else {
      try {
        api = apiText.trim() ? JSON.parse(apiText) : {};
      } catch {
        setApiError("Must be valid JSON");
        hasError = true;
      }
    }
    if (hasError) return;

    setSaving(true);
    const body = JSON.stringify({ name, login, password, api });
    const res = provider
      ? await fetch(`/api/providers/${provider.id}`, {
          method: "PUT",
          headers: { "content-type": "application/json" },
          body,
        })
      : await fetch("/api/providers", {
          method: "POST",
          headers: { "content-type": "application/json" },
          body,
        });
    setSaving(false);
    if (!res.ok) {
      setFormError(await res.text());
      return;
    }
    onSaved();
  }

  return (
    <Drawer
      title={provider ? `Edit Provider #${provider.id}` : "New Provider"}
      open={true}
      onClose={onClose}
      onSave={save}
      saving={saving}
    >
      {formError && (
        <div className="bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/30 text-red-700 dark:text-red-300 text-sm px-3 py-2 rounded">
          {formError}
        </div>
      )}
      <Field label="Name" required error={nameError}>
        <input
          className={nameError ? inputErrorCls : inputCls}
          value={name}
          onChange={(e) => {
            setName(e.target.value);
            if (nameError) setNameError(null);
          }}
          placeholder="e.g. smsc"
          autoFocus
        />
      </Field>
      <Field label="Login">
        <input
          className={inputCls}
          value={login ?? ""}
          onChange={(e) => setLogin(e.target.value)}
          placeholder="username"
        />
      </Field>
      <Field label="Password">
        <input
          className={inputCls}
          value={password ?? ""}
          onChange={(e) => setPassword(e.target.value)}
          type="text"
          placeholder="password"
        />
      </Field>

      <Field label="Driver">
        <select
          className={inputCls}
          value={driver}
          onChange={(e) => setDriver(e.target.value)}
        >
          {DRIVER_SCHEMAS.map((s) => (
            <option key={s.driver} value={s.driver}>
              {s.label}
            </option>
          ))}
          <option value="">Custom (raw JSON)</option>
        </select>
      </Field>

      {schema ? (
        <>
          <div className="text-xs rounded-md border border-blue-200 dark:border-blue-500/30 bg-blue-50 dark:bg-blue-500/10 text-blue-800 dark:text-blue-200 px-3 py-2">
            <span className="font-semibold">Credentials</span> go on a linked{" "}
            <span className="font-semibold">Account</span>:
            {schema.account.usernameLabel
              ? ` username = ${schema.account.usernameLabel},`
              : ""}{" "}
            password = {schema.account.passwordLabel}.
            {schema.account.help ? ` ${schema.account.help}` : ""}
          </div>
          {schema.fields.map((f) => (
            <Field key={f.key} label={f.label} required={f.required} error={fieldErrors[f.key]}>
              <input
                className={fieldErrors[f.key] ? inputErrorCls : inputCls}
                value={fieldValues[f.key] ?? ""}
                onChange={(e) => setField(f.key, e.target.value)}
                placeholder={f.placeholder}
                inputMode={f.type === "number" ? "numeric" : undefined}
              />
              {f.help && (
                <p className="text-xs text-gray-400 mt-1">{f.help}</p>
              )}
            </Field>
          ))}
        </>
      ) : (
        <Field label="API (JSON)" error={apiError}>
          <textarea
            className={(apiError ? inputErrorCls : inputCls) + " font-mono min-h-[180px]"}
            value={apiText}
            onChange={(e) => {
              setApiText(e.target.value);
              if (apiError) setApiError(null);
            }}
            placeholder='{ "endpoint": "https://...", "sender": "INFO" }'
          />
        </Field>
      )}
    </Drawer>
  );
}

function AccountTestDrawer({
  account,
  provider,
  onClose,
}: {
  account: Account;
  provider: Provider | null;
  onClose: () => void;
}) {
  const driver = provider?.api?.driver as string | undefined;
  const isSmsEdge = driver === "smsedge";

  const [to, setTo] = useState("");
  const [text, setText] = useState("Test message");
  const [sender, setSender] = useState("");
  const [shortenLink, setShortenLink] = useState(false);
  const [routes, setRoutes] = useState<SmsEdgeRoute[]>([]);
  const [routeKey, setRouteKey] = useState("");
  const [sending, setSending] = useState(false);
  const [errors, setErrors] = useState<Record<string, string | null>>({});
  const [result, setResult] = useState<{
    ok: boolean;
    status: number;
    error?: string;
    providerResponse?: unknown;
  } | null>(null);

  useEffect(() => {
    if (!isSmsEdge) return;
    fetch("/api/smsedge-routes")
      .then((r) => r.json() as Promise<SmsEdgeRoute[]>)
      .then((d) => setRoutes(Array.isArray(d) ? d : []))
      .catch(() => {});
  }, [isSmsEdge]);

  const selectedRoute = useMemo(
    () => routes.find((r) => String(r.id) === routeKey) ?? null,
    [routes, routeKey],
  );

  async function send() {
    setResult(null);
    const next: Record<string, string | null> = {};
    if (!to.trim()) next.to = "Phone number is required";
    if (!text.trim()) next.text = "Text is required";
    setErrors(next);
    if (Object.values(next).some(Boolean)) return;

    setSending(true);
    try {
      const res = await fetch("/api/sms/send", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          provider_id: account.provider_id,
          account_id: account.id,
          to: to.trim(),
          text,
          sender_id: sender.trim() || null,
          shorten_link: shortenLink,
          route_id: selectedRoute ? String(selectedRoute.id) : null,
        }),
      });
      const raw = await res.text();
      let parsed: any = raw;
      try {
        parsed = JSON.parse(raw);
      } catch {}
      if (!res.ok) {
        setResult({
          ok: false,
          status: res.status,
          error:
            typeof parsed === "string"
              ? parsed
              : parsed?.error ?? `HTTP ${res.status}`,
        });
      } else {
        setResult(parsed);
      }
    } catch (e: any) {
      setResult({ ok: false, status: 0, error: String(e?.message ?? e) });
    } finally {
      setSending(false);
    }
  }

  return (
    <Drawer
      title={`Test account #${account.id}`}
      open={true}
      onClose={onClose}
      onSave={send}
      saving={sending}
      saveLabel="Send test"
    >
      <div className="text-xs text-gray-500 dark:text-gray-400">
        Sends a one-off SMS using this account's credentials via{" "}
        <span className="font-semibold">{provider?.name ?? "its provider"}</span>
        . Sender / shorten / routing below mimic a campaign send.
      </div>

      <Field label="Phone number" required error={errors.to}>
        <input
          className={errors.to ? inputErrorCls : inputCls}
          value={to}
          onChange={(e) => {
            setTo(e.target.value);
            setErrors((x) => ({ ...x, to: null }));
          }}
          placeholder="e.g. 12127778899"
          autoFocus
        />
        <p className="text-xs text-gray-400 mt-1">
          International format; the driver normalizes digits as needed.
        </p>
      </Field>

      <Field label="Text" required error={errors.text}>
        <textarea
          className={(errors.text ? inputErrorCls : inputCls) + " min-h-[70px]"}
          value={text}
          maxLength={300}
          onChange={(e) => {
            setText(e.target.value);
            setErrors((x) => ({ ...x, text: null }));
          }}
          placeholder="Test message"
        />
      </Field>

      <Field label="Sender name">
        <input
          className={inputCls}
          value={sender}
          onChange={(e) => setSender(e.target.value)}
          placeholder="Leave blank to use provider default"
        />
      </Field>

      <label className="flex items-center gap-2 text-sm cursor-pointer">
        <input
          type="checkbox"
          checked={shortenLink}
          onChange={(e) => setShortenLink(e.target.checked)}
        />
        Shorten links
      </label>

      {isSmsEdge && (
        <Field label="SMSEdge Routing">
          <select
            className={inputCls}
            value={routeKey}
            onChange={(e) => setRouteKey(e.target.value)}
          >
            <option value="">— no specific route —</option>
            {routes.map((r) => (
              <option key={r.id} value={String(r.id)}>
                {r.country} · {r.name}
              </option>
            ))}
          </select>
        </Field>
      )}

      {result && (
        <div
          className={
            "text-sm rounded-md border px-3 py-2 " +
            (result.ok
              ? "bg-green-50 dark:bg-green-500/10 border-green-200 dark:border-green-500/30 text-green-800 dark:text-green-200"
              : "bg-red-50 dark:bg-red-500/10 border-red-200 dark:border-red-500/30 text-red-700 dark:text-red-300")
          }
        >
          <div className="font-semibold">
            {result.ok ? "✓ Sent" : "✗ Failed"}
            {result.status ? ` (HTTP ${result.status})` : ""}
          </div>
          {result.error && <div className="mt-1">{result.error}</div>}
          {result.providerResponse != null && (
            <pre className="mt-2 max-h-48 overflow-auto text-xs whitespace-pre-wrap break-all opacity-80">
              {typeof result.providerResponse === "string"
                ? result.providerResponse
                : JSON.stringify(result.providerResponse, null, 2)}
            </pre>
          )}
        </div>
      )}
    </Drawer>
  );
}

// ---------- Accounts ----------

function AccountsTab() {
  const { data, loading, reload } = useAsync<Account[]>(() =>
    fetch("/api/accounts").then((r) => r.json() as Promise<Account[]>),
  );
  const providers = useAsync<Provider[]>(() =>
    fetch("/api/providers").then((r) => r.json() as Promise<Provider[]>),
  );
  const [editing, setEditing] = useState<Account | "new" | null>(null);
  const [deleting, setDeleting] = useState<Account | null>(null);
  const [testing, setTesting] = useState<Account | null>(null);
  const [refreshingBalances, setRefreshingBalances] = useState(false);

  async function refreshAllBalances() {
    setRefreshingBalances(true);
    try {
      await fetch("/api/accounts/refresh-balances", { method: "POST" });
      reload();
    } finally {
      setRefreshingBalances(false);
    }
  }

  async function refreshOneBalance(id: number) {
    await fetch(`/api/accounts/${id}/refresh-balance`, { method: "POST" });
    reload();
  }

  // Total across all accounts with a known balance. Mixed-currency accounts
  // are summed naively — fine while everyone is USD, surface a warning in the
  // tooltip if/when mixed currencies appear.
  const balanceSummary = useMemo(() => {
    const rows = data ?? [];
    let total = 0;
    let counted = 0;
    const currencies = new Set<string>();
    for (const a of rows) {
      if (a.balance == null) continue;
      const v = Number(a.balance);
      if (!Number.isFinite(v)) continue;
      total += v;
      counted++;
      if (a.balance_currency) currencies.add(a.balance_currency);
    }
    return {
      total,
      counted,
      currencies: Array.from(currencies),
      missing: rows.length - counted,
    };
  }, [data]);

  return (
    <PageShell
      title="Accounts"
      total={data?.length ?? 0}
      onAdd={() => setEditing("new")}
      extraHeader={
        <>
          {balanceSummary.counted > 0 && (
            <span
              className="inline-flex items-center gap-2 px-2 py-1 rounded-md bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-300"
              title={
                balanceSummary.currencies.length > 1
                  ? `Mixed currencies: ${balanceSummary.currencies.join(", ")} — summed naively`
                  : `Total across ${balanceSummary.counted} accounts${balanceSummary.missing > 0 ? ` (${balanceSummary.missing} without balance)` : ""}`
              }
            >
              <span className="text-xs opacity-70">Total balance</span>
              <span className="font-bold">
                ${balanceSummary.total.toFixed(2)}
              </span>
              {balanceSummary.currencies.length > 1 && (
                <span className="text-xs text-amber-700 dark:text-amber-400">
                  mixed
                </span>
              )}
            </span>
          )}
          <button
            onClick={refreshAllBalances}
            disabled={refreshingBalances}
            className="px-3 py-1.5 rounded-md border border-gray-300 dark:border-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 text-sm cursor-pointer disabled:opacity-50"
            title="Pull current balance for every account with a Balance endpoint URL"
          >
            {refreshingBalances ? "Refreshing…" : "↻ Refresh balances"}
          </button>
        </>
      }
    >
      {loading ? (
        <div className="p-6 text-gray-400">Loading...</div>
      ) : (
        <AgTable<Account>
          rows={data ?? []}
          columns={[
            {
              headerName: "Provider",
              colId: "provider",
              valueGetter: (p) => p.data?.provider_name ?? p.data?.provider_id,
              cellRenderer: (p: ICellRendererParams<Account>) => (
                <span className="font-medium">{p.value}</span>
              ),
            },
            {
              field: "key",
              headerName: "Key",
              cellRenderer: (p: ICellRendererParams<Account>) => (
                <code className="text-xs">{p.value}</code>
              ),
            },
            {
              field: "balance",
              headerName: "Balance",
              width: 140,
              flex: 0,
              filter: "agNumberColumnFilter",
              valueGetter: (p) =>
                p.data?.balance == null ? null : Number(p.data.balance),
              cellRenderer: (p: ICellRendererParams<Account>) => {
                const v = p.value as number | null;
                if (v == null)
                  return <span className="text-gray-400 text-xs">—</span>;
                const threshold = p.data?.balance_low_threshold
                  ? Number(p.data.balance_low_threshold)
                  : null;
                const cls =
                  threshold != null && v < threshold
                    ? "text-red-700 dark:text-red-400 font-bold"
                    : v < 1
                      ? "text-amber-700 dark:text-amber-400 font-semibold"
                      : "text-emerald-700 dark:text-emerald-400 font-semibold";
                const ccy = p.data?.balance_currency ?? "USD";
                return (
                  <span className={cls}>
                    {ccy === "USD" ? "$" : `${ccy} `}
                    {v.toFixed(2)}
                  </span>
                );
              },
            },
            {
              field: "balance_updated_at",
              headerName: "Updated",
              width: 130,
              flex: 0,
              filter: "agDateColumnFilter",
              cellRenderer: (p: ICellRendererParams<Account>) => {
                const v = p.value as string | null;
                if (!v) return <span className="text-gray-400 text-xs">never</span>;
                const ago = Date.now() - new Date(v).getTime();
                const m = Math.floor(ago / 60_000);
                const h = Math.floor(m / 60);
                const txt =
                  m < 1 ? "just now" : h < 1 ? `${m}m ago` : `${h}h ago`;
                return (
                  <span className="text-xs text-gray-600 dark:text-gray-400" title={new Date(v).toLocaleString()}>
                    {txt}
                  </span>
                );
              },
            },
            {
              field: "created_at",
              headerName: "Created",
              filter: "agDateColumnFilter",
              valueFormatter: (p) =>
                p.value ? new Date(p.value).toLocaleString() : "",
              hide: true,
            },
          ]}
          onEdit={(r) => setEditing(r)}
          onDelete={(r) => setDeleting(r)}
          extraActions={(r) => [
            {
              icon: <span className="text-xs font-semibold">↻</span>,
              title: "Refresh balance",
              className:
                "text-emerald-600 hover:bg-emerald-50 dark:text-emerald-400 dark:hover:bg-emerald-500/10",
              onClick: () => refreshOneBalance(r.id),
            },
            {
              icon: <Icon name="send" />,
              title: "Send test SMS",
              className:
                "text-green-600 hover:bg-green-50 dark:text-green-400 dark:hover:bg-green-500/10",
              onClick: () => setTesting(r),
            },
          ]}
          actionsWidth={180}
        />
      )}
      {editing && (
        <AccountDrawer
          account={editing === "new" ? null : editing}
          providers={providers.data ?? []}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            reload();
          }}
        />
      )}
      {testing && (
        <AccountTestDrawer
          account={testing}
          provider={
            (providers.data ?? []).find((p) => p.id === testing.provider_id) ?? null
          }
          onClose={() => setTesting(null)}
        />
      )}
      <ConfirmDialog
        open={!!deleting}
        title="Delete account"
        message={
          deleting && (
            <>
              Are you sure you want to delete account{" "}
              <span className="font-semibold text-gray-900 dark:text-gray-100">#{deleting.id}</span>?
            </>
          )
        }
        onCancel={() => setDeleting(null)}
        onConfirm={async () => {
          if (!deleting) return;
          await fetch(`/api/accounts/${deleting.id}`, { method: "DELETE" });
          setDeleting(null);
          reload();
        }}
      />
    </PageShell>
  );
}

function AccountDrawer({
  account,
  providers,
  onClose,
  onSaved,
}: {
  account: Account | null;
  providers: Provider[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const [providerId, setProviderId] = useState<number | "">(
    account?.provider_id ?? providers[0]?.id ?? "",
  );
  const [username, setUsername] = useState(account?.username ?? "");
  const [password, setPassword] = useState(account?.password ?? "");
  const [key, setKey] = useState(account?.key ?? "");
  const [hlrUrl, setHlrUrl] = useState(account?.hlr_url ?? "");
  const [balanceUrl, setBalanceUrl] = useState(account?.balance_url ?? "");
  const [balanceLow, setBalanceLow] = useState(
    account?.balance_low_threshold ?? "",
  );
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [providerError, setProviderError] = useState<string | null>(null);

  const selectedProvider = providers.find((p) => p.id === providerId);
  const schema = getDriverSchema(selectedProvider?.api?.driver as string | undefined);

  async function save() {
    setFormError(null);
    setProviderError(null);

    if (!providerId) {
      setProviderError("Provider is required");
      return;
    }

    setSaving(true);
    const body = JSON.stringify({
      provider_id: providerId,
      key,
      username,
      password,
      hlr_url: hlrUrl.trim() || null,
      balance_url: balanceUrl.trim() || null,
      balance_low_threshold:
        String(balanceLow).trim() === ""
          ? null
          : Number(String(balanceLow).replace(",", ".")),
    });
    const res = account
      ? await fetch(`/api/accounts/${account.id}`, {
          method: "PUT",
          headers: { "content-type": "application/json" },
          body,
        })
      : await fetch("/api/accounts", {
          method: "POST",
          headers: { "content-type": "application/json" },
          body,
        });
    setSaving(false);
    if (!res.ok) {
      setFormError(await res.text());
      return;
    }
    onSaved();
  }

  return (
    <Drawer
      title={account ? `Edit Account #${account.id}` : "New Account"}
      open={true}
      onClose={onClose}
      onSave={save}
      saving={saving}
    >
      {formError && (
        <div className="bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/30 text-red-700 dark:text-red-300 text-sm px-3 py-2 rounded">
          {formError}
        </div>
      )}
      <Field label="Provider" required error={providerError}>
        <select
          className={providerError ? inputErrorCls : inputCls}
          value={providerId}
          onChange={(e) => {
            setProviderId(Number(e.target.value));
            if (providerError) setProviderError(null);
          }}
        >
          <option value="">— select provider —</option>
          {providers.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}
            </option>
          ))}
        </select>
      </Field>
      <Field label={schema?.account.usernameLabel ?? "Username"}>
        <input
          className={inputCls}
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          placeholder={schema?.account.usernameLabel ?? "username (if the driver needs one)"}
        />
      </Field>
      <Field label={schema?.account.passwordLabel ?? "Password"}>
        <input
          className={inputCls}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder={schema?.account.passwordLabel ?? "API key / token / password"}
        />
      </Field>
      {schema?.account.help && (
        <p className="text-xs text-gray-400 -mt-2">{schema.account.help}</p>
      )}
      <Field label="HLR endpoint URL">
        <input
          className={inputCls}
          value={hlrUrl}
          onChange={(e) => setHlrUrl(e.target.value)}
          placeholder="https://api.smsedge.com/v1/verify/number-hlr/"
        />
        <p className="text-xs text-gray-400 mt-1">
          Base URL only. <code>api_key</code> (from this account's
          password/key) and <code>number</code> are appended automatically per
          request.
        </p>
      </Field>
      <Field label="Balance endpoint URL">
        <input
          className={inputCls}
          value={balanceUrl}
          onChange={(e) => setBalanceUrl(e.target.value)}
          placeholder="https://api.smsedge.com/v1/user/details/"
        />
        <p className="text-xs text-gray-400 mt-1">
          Polled every 15 min. <code>api_key</code> appended automatically.
        </p>
      </Field>
      <Field label="Low balance threshold ($)">
        <input
          type="number"
          step="0.01"
          className={inputCls}
          value={balanceLow == null ? "" : String(balanceLow)}
          onChange={(e) => setBalanceLow(e.target.value)}
          placeholder="e.g. 5 — logged warning when balance drops below"
        />
      </Field>
      <Field label="Key (legacy)">
        <input
          className={inputCls}
          value={key}
          onChange={(e) => setKey(e.target.value)}
          placeholder="optional — used only if password is empty"
        />
      </Field>
    </Drawer>
  );
}

// ---------- Phones ----------

type HlrStatusResponse = {
  running: boolean;
  account_id: number | null;
  total: number;
  checked: number;
  cost_total: number;
  started_at: string | null;
  finished_at: string | null;
  last_error: string | null;
};

function PhonesTab() {
  const { data, loading, reload } = useAsync<Phone[]>(() =>
    fetch("/api/phones").then((r) => r.json() as Promise<Phone[]>),
  );
  const [editing, setEditing] = useState<Phone | "new" | null>(null);
  const [deleting, setDeleting] = useState<Phone | null>(null);
  const [importing, setImporting] = useState(false);
  const [hlrPickerOpen, setHlrPickerOpen] = useState(false);
  const [filterResetKey, setFilterResetKey] = useState(0);
  const [hlrStatus, setHlrStatus] = useState<HlrStatusResponse | null>(null);
  const [hlrCountsLive, setHlrCountsLive] = useState<{
    good: number;
    bad: number;
    waiting: number;
    error: number;
    unchecked: number;
    cost_sum: number;
  } | null>(null);

  // Poll HLR badges + job status every 2s. The phones grid (ag-grid) is NEVER
  // touched by this — only setHlrCountsLive / setHlrStatus update, which feed
  // the header chips. The grid stays put; use the ↻ Refresh button to reload
  // the phones list itself.
  useEffect(() => {
    let stopped = false;
    const tick = async () => {
      try {
        const [statusRes, countsRes] = await Promise.all([
          fetch("/api/phones/hlr/status"),
          fetch("/api/phones/hlr/counts"),
        ]);
        if (stopped) return;
        if (statusRes.ok) {
          setHlrStatus((await statusRes.json()) as HlrStatusResponse);
        }
        if (countsRes.ok) {
          setHlrCountsLive(await countsRes.json());
        }
      } catch {
        /* ignore */
      }
    };
    tick();
    const t = setInterval(tick, 2_000);
    return () => {
      stopped = true;
      clearInterval(t);
    };
  }, []);

  async function stopHlr() {
    await fetch("/api/phones/hlr/stop", { method: "POST" });
  }

  // Aggregate HLR breakdown for the header chips. Prefer the live /hlr/counts
  // endpoint (polled every 2s) so badges update without redrawing the grid.
  // Falls back to client-side computation from `data` on the first render.
  const hlrCounts = useMemo(() => {
    if (hlrCountsLive) {
      return {
        good: hlrCountsLive.good,
        bad: hlrCountsLive.bad,
        waiting: hlrCountsLive.waiting,
        error: hlrCountsLive.error,
        unchecked: hlrCountsLive.unchecked,
        costSum: hlrCountsLive.cost_sum,
      };
    }
    const rows = data ?? [];
    let good = 0, bad = 0, waiting = 0, errCnt = 0, unchecked = 0, costSum = 0;
    for (const p of rows) {
      const s = p.hlr_status;
      if (s === "good") good++;
      else if (s === "bad") bad++;
      else if (s === "waiting") waiting++;
      else if (s === "error") errCnt++;
      else unchecked++;
      if (p.hlr_cost != null) costSum += Number(p.hlr_cost) || 0;
    }
    return { good, bad, waiting, error: errCnt, unchecked, costSum };
  }, [hlrCountsLive, data]);

  // Columns are stable for this tab — memoise so the AgTable receives the
  // same reference across re-renders triggered by the 2s HLR badge polling.
  // ag-grid's React wrapper would otherwise schedule internal updates on
  // every columnDefs prop change, even if nothing structural changed.
  const phoneColumns = useMemo<ColDef<Phone>[]>(
    () => [
      {
        field: "first_name",
        headerName: "First Name",
        valueFormatter: (p) => p.value ?? "—",
      },
      {
        field: "last_name",
        headerName: "Last Name",
        valueFormatter: (p) => p.value ?? "—",
      },
      {
        field: "email",
        headerName: "Email",
        valueFormatter: (p) => p.value ?? "—",
      },
      {
        field: "import_batch",
        headerName: "Batch",
        valueFormatter: (p) => p.value ?? "—",
        width: 180,
        flex: 0,
        cellRenderer: (p: ICellRendererParams<Phone>) =>
          p.value ? (
            <span className="px-2 py-0.5 rounded bg-gray-100 dark:bg-gray-700 text-xs font-mono">
              {p.value}
            </span>
          ) : (
            <span className="text-gray-400">—</span>
          ),
      },
      {
        field: "name",
        headerName: "Name",
        valueFormatter: (p) => p.value ?? "—",
        hide: true,
      },
      {
        field: "phone",
        headerName: "Phone",
        cellRenderer: (p: ICellRendererParams<Phone>) => (
          <span className="font-medium">{p.value}</span>
        ),
      },
      {
        field: "hlr_status",
        headerName: "HLR",
        width: 100,
        flex: 0,
        cellRenderer: (p: ICellRendererParams<Phone>) => {
          const v = p.value as string | null;
          if (!v) return <span className="text-gray-400">—</span>;
          const cls =
            v === "good"
              ? "bg-emerald-100 dark:bg-emerald-500/15 text-emerald-700 dark:text-emerald-300"
              : v === "bad"
                ? "bg-red-100 dark:bg-red-500/15 text-red-700 dark:text-red-300"
                : v === "error"
                  ? "bg-purple-100 dark:bg-purple-500/15 text-purple-700 dark:text-purple-300"
                  : "bg-amber-100 dark:bg-amber-500/15 text-amber-700 dark:text-amber-300";
          return (
            <span className={`px-2 py-0.5 rounded text-xs font-semibold ${cls}`}>
              {v}
            </span>
          );
        },
      },
      {
        field: "hlr_operator",
        headerName: "Operator",
        valueFormatter: (p) => p.value ?? "—",
        width: 150,
        flex: 0,
      },
      {
        field: "hlr_country",
        headerName: "Country",
        valueFormatter: (p) => p.value ?? "—",
        width: 130,
        flex: 0,
        hide: true,
      },
      {
        field: "hlr_cost",
        headerName: "HLR Cost",
        filter: "agNumberColumnFilter",
        valueGetter: (p) =>
          p.data?.hlr_cost == null ? null : Number(p.data.hlr_cost),
        valueFormatter: (p) =>
          p.value == null ? "—" : `$${Number(p.value).toFixed(4)}`,
        width: 100,
        flex: 0,
        hide: true,
      },
      {
        field: "hlr_checked_at",
        headerName: "HLR Checked",
        filter: "agDateColumnFilter",
        valueFormatter: (p) =>
          p.value ? new Date(p.value).toLocaleString() : "—",
        width: 170,
        flex: 0,
        hide: true,
      },
      {
        field: "created_at",
        headerName: "Created",
        filter: "agDateColumnFilter",
        valueFormatter: (p) =>
          p.value ? new Date(p.value).toLocaleString() : "",
      },
      {
        field: "updated_at",
        headerName: "Updated",
        filter: "agDateColumnFilter",
        valueFormatter: (p) =>
          p.value ? new Date(p.value).toLocaleString() : "",
      },
    ],
    [],
  );

  return (
    <PageShell
      title="Phones"
      total={data?.length ?? 0}
      onAdd={() => setEditing("new")}
      extraHeader={
        <>
          {hlrCounts.good + hlrCounts.bad + hlrCounts.waiting > 0 && (
            <div className="inline-flex items-center gap-1 text-xs">
              {hlrCounts.good > 0 && (
                <span
                  className="px-2 py-1 rounded-md bg-emerald-100 dark:bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 font-medium"
                  title="HLR good"
                >
                  good {hlrCounts.good}
                </span>
              )}
              {hlrCounts.bad > 0 && (
                <span
                  className="px-2 py-1 rounded-md bg-red-100 dark:bg-red-500/15 text-red-700 dark:text-red-300 font-medium"
                  title="HLR bad"
                >
                  bad {hlrCounts.bad}
                </span>
              )}
              {hlrCounts.waiting > 0 && (
                <span
                  className="px-2 py-1 rounded-md bg-amber-100 dark:bg-amber-500/15 text-amber-700 dark:text-amber-300 font-medium"
                  title="HLR waiting (retry to resolve)"
                >
                  waiting {hlrCounts.waiting}
                </span>
              )}
              {hlrCounts.error > 0 && (
                <span
                  className="px-2 py-1 rounded-md bg-purple-100 dark:bg-purple-500/15 text-purple-700 dark:text-purple-300 font-medium"
                  title="HLR error (e.g. 404 — number unsupported)"
                >
                  error {hlrCounts.error}
                </span>
              )}
              {hlrCounts.unchecked > 0 && (
                <span
                  className="px-2 py-1 rounded-md bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 font-medium"
                  title="never checked"
                >
                  — {hlrCounts.unchecked}
                </span>
              )}
              <span
                className="px-2 py-1 rounded-md bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300"
                title="Total spent on HLR across all phones"
              >
                ${hlrCounts.costSum.toFixed(4)}
              </span>
            </div>
          )}
          <button
            onClick={() => {
              reload();
              refreshHlrPanels();
            }}
            disabled={loading}
            className="px-3 py-1.5 rounded-md border border-gray-300 dark:border-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 text-sm cursor-pointer disabled:opacity-50 inline-flex items-center gap-1.5"
            title="Reload phones list, HLR badges, and HLR job status — nothing on this tab auto-refreshes"
          >
            {loading ? (
              <>
                <span className="inline-block w-3 h-3 border-2 border-gray-400 border-t-blue-500 rounded-full animate-spin" />
                Loading…
              </>
            ) : (
              "↻ Refresh"
            )}
          </button>
          <button
            onClick={() => setImporting(true)}
            className="px-3 py-1.5 rounded-md border border-gray-300 dark:border-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 text-sm cursor-pointer"
          >
            Import CSV
          </button>
          <button
            onClick={() => setHlrPickerOpen(true)}
            disabled={hlrStatus?.running}
            className="px-3 py-1.5 rounded-md border border-gray-300 dark:border-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 text-sm cursor-pointer disabled:opacity-50"
            title="Run HLR verification on phones with no hlr_status yet"
          >
            HLR
          </button>
          {hlrStatus?.running && (
            <span
              className="inline-flex items-center gap-2 px-2 py-1 rounded-md bg-blue-100 dark:bg-blue-500/15 text-blue-700 dark:text-blue-300 text-xs"
              title={hlrStatus.last_error ?? ""}
            >
              <span className="animate-pulse">⏳</span>
              <span>
                HLR {hlrStatus.checked}/{hlrStatus.total} · ${hlrStatus.cost_total.toFixed(4)}
              </span>
              <button
                onClick={stopHlr}
                className="text-blue-900 dark:text-blue-100 hover:underline"
              >
                stop
              </button>
            </span>
          )}
          {!hlrStatus?.running && hlrStatus?.finished_at && hlrStatus.checked > 0 && (
            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-emerald-100 dark:bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 text-xs">
              ✓ HLR done: {hlrStatus.checked} checked · ${hlrStatus.cost_total.toFixed(4)}
            </span>
          )}
          <ResetFiltersButton onClick={() => setFilterResetKey((n) => n + 1)} />
        </>
      }
    >
      {loading && data === null ? (
        <div className="p-6 text-gray-400">Loading...</div>
      ) : (
        <AgTable<Phone>
          persistKey="phones"
          filterResetKey={filterResetKey}
          rows={data ?? []}
          columns={phoneColumns}
          onEdit={(r) => setEditing(r)}
          onDelete={(r) => setDeleting(r)}
        />
      )}
      {editing && (
        <PhoneDrawer
          phone={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            reload();
          }}
        />
      )}
      {importing && (
        <ImportPhonesCsvDrawer
          onClose={() => setImporting(false)}
          onImported={() => {
            setImporting(false);
            reload();
          }}
        />
      )}
      {hlrPickerOpen && (
        <HlrAccountPickerDialog
          onClose={() => setHlrPickerOpen(false)}
          onStarted={(s) => {
            setHlrStatus(s);
            setHlrPickerOpen(false);
          }}
        />
      )}
      <ConfirmDialog
        open={!!deleting}
        title="Delete phone"
        message={
          deleting && (
            <>
              Are you sure you want to delete phone{" "}
              <span className="font-semibold text-gray-900 dark:text-gray-100">{deleting.phone}</span>?
            </>
          )
        }
        onCancel={() => setDeleting(null)}
        onConfirm={async () => {
          if (!deleting) return;
          await fetch(`/api/phones/${deleting.id}`, { method: "DELETE" });
          setDeleting(null);
          reload();
        }}
      />
    </PageShell>
  );
}

function HlrAccountPickerDialog({
  onClose,
  onStarted,
}: {
  onClose: () => void;
  onStarted: (status: HlrStatusResponse) => void;
}) {
  const { data, loading } = useAsync<Account[]>(() =>
    fetch("/api/phones/hlr/accounts").then((r) => r.json() as Promise<Account[]>),
  );
  const [accountId, setAccountId] = useState<number | "">("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function start() {
    if (!accountId) {
      setError("Pick an account first");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const res = await fetch("/api/phones/hlr/start", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ account_id: accountId }),
      });
      const text = await res.text();
      if (!res.ok) {
        // Backend returns plain-text errors via the `err()` helper, not JSON.
        throw new Error(text || `HTTP ${res.status}`);
      }
      const json = JSON.parse(text);
      if (json?.error) throw new Error(String(json.error));
      onStarted(json as HlrStatusResponse);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white dark:bg-gray-900 rounded-lg shadow-xl w-full max-w-md p-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold dark:text-gray-100">
            Run HLR verification
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
          >
            <Icon name="x" />
          </button>
        </div>
        <p className="text-sm text-gray-600 dark:text-gray-300 mb-3">
          Checks every phone where <code>hlr_status</code> is empty (or stuck
          on <code>waiting</code>) via the picked account's HLR endpoint.
          Runs in background — close this dialog anytime.
        </p>
        <Field label="Account (must have hlr_url)">
          <select
            className={inputCls}
            value={accountId}
            onChange={(e) =>
              setAccountId(e.target.value === "" ? "" : Number(e.target.value))
            }
            disabled={loading}
          >
            <option value="">{loading ? "Loading…" : "— select —"}</option>
            {(data ?? []).map((a) => (
              <option key={a.id} value={a.id}>
                {a.provider_name ? `${a.provider_name} — ${a.key}` : a.key}
              </option>
            ))}
          </select>
        </Field>
        {!loading && (data ?? []).length === 0 && (
          <div className="mt-2 text-xs text-amber-700 dark:text-amber-400">
            No accounts with hlr_url configured. Edit an account in the
            Accounts tab and set the HLR endpoint URL.
          </div>
        )}
        {error && <div className="mt-2 text-xs text-red-600">{error}</div>}
        <div className="flex justify-end gap-2 mt-5">
          <button
            onClick={onClose}
            className="px-3 py-1.5 text-sm rounded border border-gray-300 dark:border-gray-600 dark:text-gray-200"
          >
            Cancel
          </button>
          <button
            onClick={start}
            disabled={busy || !accountId}
            className="px-3 py-1.5 text-sm rounded bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50"
          >
            {busy ? "Starting…" : "Start"}
          </button>
        </div>
      </div>
    </div>
  );
}

function PhoneDrawer({
  phone,
  onClose,
  onSaved,
}: {
  phone: Phone | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [phoneValue, setPhoneValue] = useState(phone?.phone ?? "");
  const [nameValue, setNameValue] = useState(phone?.name ?? "");
  const [firstNameValue, setFirstNameValue] = useState(phone?.first_name ?? "");
  const [lastNameValue, setLastNameValue] = useState(phone?.last_name ?? "");
  const [emailValue, setEmailValue] = useState(phone?.email ?? "");
  const [saving, setSaving] = useState(false);
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  async function save() {
    setFormError(null);
    setFieldError(null);
    if (!phoneValue.trim()) {
      setFieldError("Phone is required");
      return;
    }
    const normalized = normalizePhone(phoneValue);
    if (!normalized) {
      setFieldError("Invalid phone number");
      return;
    }
    setSaving(true);
    const body = JSON.stringify({
      phone: normalized,
      name: nameValue.trim() || null,
      first_name: firstNameValue.trim() || null,
      last_name: lastNameValue.trim() || null,
      email: emailValue.trim() || null,
    });
    const res = phone
      ? await fetch(`/api/phones/${phone.id}`, {
          method: "PUT",
          headers: { "content-type": "application/json" },
          body,
        })
      : await fetch("/api/phones", {
          method: "POST",
          headers: { "content-type": "application/json" },
          body,
        });
    setSaving(false);
    if (!res.ok) {
      setFormError(await res.text());
      return;
    }
    onSaved();
  }

  return (
    <Drawer
      title={phone ? `Edit Phone #${phone.id}` : "New Phone"}
      open={true}
      onClose={onClose}
      onSave={save}
      saving={saving}
    >
      {formError && (
        <div className="bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/30 text-red-700 dark:text-red-300 text-sm px-3 py-2 rounded">
          {formError}
        </div>
      )}
      <Field label="First Name">
        <input
          className={inputCls}
          value={firstNameValue}
          onChange={(e) => setFirstNameValue(e.target.value)}
          placeholder="John (optional)"
        />
      </Field>
      <Field label="Last Name">
        <input
          className={inputCls}
          value={lastNameValue}
          onChange={(e) => setLastNameValue(e.target.value)}
          placeholder="Doe (optional)"
        />
      </Field>
      <Field label="Email">
        <input
          type="email"
          className={inputCls}
          value={emailValue}
          onChange={(e) => setEmailValue(e.target.value)}
          placeholder="john@example.com (optional)"
        />
      </Field>
      <Field label="Name">
        <input
          className={inputCls}
          value={nameValue}
          onChange={(e) => setNameValue(e.target.value)}
          placeholder="Display name (optional)"
        />
      </Field>
      <Field label="Phone" required error={fieldError}>
        <input
          className={fieldError ? inputErrorCls : inputCls}
          value={phoneValue}
          onChange={(e) => {
            setPhoneValue(e.target.value);
            if (fieldError) setFieldError(null);
          }}
          placeholder="+1234567890"
          autoFocus
        />
      </Field>
    </Drawer>
  );
}

// ---------- URL Destinations ----------

function UrlDestinationsTab() {
  const { data, loading, reload } = useAsync<UrlDestination[]>(() =>
    fetch("/api/url-destinations").then((r) => r.json() as Promise<UrlDestination[]>),
  );
  const [editing, setEditing] = useState<UrlDestination | "new" | null>(null);
  const [deleting, setDeleting] = useState<UrlDestination | null>(null);

  return (
    <PageShell
      title="URL Destinations"
      total={data?.length ?? 0}
      onAdd={() => setEditing("new")}
    >
      {loading ? (
        <div className="p-6 text-gray-400">Loading...</div>
      ) : (
        <AgTable<UrlDestination>
          rows={data ?? []}
          columns={[
            {
              field: "name",
              headerName: "Name",
              valueFormatter: (p) => p.value ?? "—",
              cellRenderer: (p: ICellRendererParams<UrlDestination>) =>
                p.value ? <span className="font-medium">{p.value}</span> : (
                  <span className="text-gray-400">—</span>
                ),
            },
            {
              field: "url",
              headerName: "URL",
              cellRenderer: (p: ICellRendererParams<UrlDestination>) => (
                <a
                  href={p.value}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 dark:text-blue-400 hover:underline"
                >
                  {p.value}
                </a>
              ),
              flex: 2,
            },
            {
              field: "description",
              headerName: "Description",
              valueFormatter: (p) => p.value ?? "—",
              flex: 2,
            },
            {
              field: "countries",
              headerName: "Countries",
              valueGetter: (p) => p.data?.countries?.length ?? 0,
              filter: "agNumberColumnFilter",
              cellRenderer: (p: ICellRendererParams<UrlDestination>) => {
                const list = p.data?.countries ?? [];
                if (list.length === 0) {
                  return <span className="text-gray-400 text-xs">—</span>;
                }
                const preview = list.slice(0, 6).join(", ");
                const rest = list.length > 6 ? ` +${list.length - 6}` : "";
                return (
                  <span className="text-xs font-mono" title={list.join(", ")}>
                    {preview}{rest}
                  </span>
                );
              },
              width: 240,
              flex: 0,
            },
            {
              field: "created_at",
              headerName: "Created",
              filter: "agDateColumnFilter",
              valueFormatter: (p) =>
                p.value ? new Date(p.value).toLocaleString() : "",
            },
          ]}
          onEdit={(r) => setEditing(r)}
          onDelete={(r) => setDeleting(r)}
        />
      )}
      {editing && (
        <UrlDestinationDrawer
          urlDestination={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            reload();
          }}
        />
      )}
      <ConfirmDialog
        open={!!deleting}
        title="Delete URL destination"
        message={
          deleting && (
            <>
              Are you sure you want to delete{" "}
              <span className="font-semibold text-gray-900 dark:text-gray-100">{deleting.url}</span>?
            </>
          )
        }
        onCancel={() => setDeleting(null)}
        onConfirm={async () => {
          if (!deleting) return;
          await fetch(`/api/url-destinations/${deleting.id}`, { method: "DELETE" });
          setDeleting(null);
          reload();
        }}
      />
    </PageShell>
  );
}

function UrlDestinationDrawer({
  urlDestination,
  onClose,
  onSaved,
}: {
  urlDestination: UrlDestination | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [url, setUrl] = useState(urlDestination?.url ?? "");
  const [name, setName] = useState(urlDestination?.name ?? "");
  const [description, setDescription] = useState(urlDestination?.description ?? "");
  const [countries, setCountries] = useState<string[]>(urlDestination?.countries ?? []);
  const [saving, setSaving] = useState(false);
  const [urlError, setUrlError] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  const allCountries = useAsync<Country[]>(() =>
    fetch("/api/countries").then((r) => r.json() as Promise<Country[]>),
  );

  const [countryQuery, setCountryQuery] = useState("");
  const selectedSet = useMemo(() => new Set(countries), [countries]);
  const filteredAvailable = useMemo(() => {
    const list = allCountries.data ?? [];
    const q = countryQuery.trim().toLowerCase();
    return list.filter(
      (c) =>
        !selectedSet.has(c.iso2) &&
        (q === "" ||
          c.name.toLowerCase().includes(q) ||
          c.iso2.toLowerCase().includes(q) ||
          c.iso3.toLowerCase().includes(q)),
    );
  }, [allCountries.data, selectedSet, countryQuery]);

  const countryByIso2 = useMemo(() => {
    const m = new Map<string, Country>();
    for (const c of allCountries.data ?? []) m.set(c.iso2, c);
    return m;
  }, [allCountries.data]);

  function addCountry(iso2: string) {
    if (!selectedSet.has(iso2)) setCountries([...countries, iso2]);
  }
  function removeCountry(iso2: string) {
    setCountries(countries.filter((c) => c !== iso2));
  }

  async function save() {
    setFormError(null);
    setUrlError(null);
    const trimmed = url.trim();
    if (!trimmed) {
      setUrlError("URL is required");
      return;
    }
    try {
      new URL(trimmed);
    } catch {
      setUrlError("Must be a valid URL (e.g. https://example.com)");
      return;
    }

    setSaving(true);
    const body = JSON.stringify({
      url: trimmed,
      name: name.trim() || null,
      description: description.trim() || null,
      countries,
    });
    const res = urlDestination
      ? await fetch(`/api/url-destinations/${urlDestination.id}`, {
          method: "PUT",
          headers: { "content-type": "application/json" },
          body,
        })
      : await fetch("/api/url-destinations", {
          method: "POST",
          headers: { "content-type": "application/json" },
          body,
        });
    setSaving(false);
    if (!res.ok) {
      setFormError(await res.text());
      return;
    }
    onSaved();
  }

  return (
    <Drawer
      title={urlDestination ? `Edit URL Destination #${urlDestination.id}` : "New URL Destination"}
      open={true}
      onClose={onClose}
      onSave={save}
      saving={saving}
    >
      {formError && (
        <div className="bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/30 text-red-700 dark:text-red-300 text-sm px-3 py-2 rounded">
          {formError}
        </div>
      )}
      <Field label="Name">
        <input
          className={inputCls}
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. Summer landing"
          autoFocus
        />
      </Field>
      <Field label="URL" required error={urlError}>
        <input
          className={urlError ? inputErrorCls : inputCls}
          value={url}
          onChange={(e) => {
            setUrl(e.target.value);
            if (urlError) setUrlError(null);
          }}
          placeholder="https://example.com/landing"
          type="url"
        />
      </Field>
      <Field label="Description">
        <textarea
          className={inputCls + " min-h-[70px]"}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="What is this URL for?"
        />
      </Field>

      <Field label={`Countries (${countries.length})`}>
        <div className="space-y-2">
          <div className="flex flex-wrap gap-1 min-h-[36px] p-2 border border-gray-200 dark:border-gray-700 rounded-md bg-gray-50 dark:bg-gray-800">
            {countries.length === 0 && (
              <span className="text-xs text-gray-400 self-center">
                No countries selected
              </span>
            )}
            {countries.map((iso2) => {
              const c = countryByIso2.get(iso2);
              return (
                <span
                  key={iso2}
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-blue-100 dark:bg-blue-500/20 text-blue-800 dark:text-blue-200"
                >
                  <span>{iso2}</span>
                  {c && <span className="text-blue-700/70 dark:text-blue-300/70">— {c.name}</span>}
                  <button
                    onClick={() => removeCountry(iso2)}
                    className="ml-0.5 text-blue-700/70 hover:text-blue-900 dark:text-blue-300/70 dark:hover:text-blue-100 cursor-pointer"
                    title="Remove"
                  >
                    ×
                  </button>
                </span>
              );
            })}
          </div>
          <input
            className={inputCls}
            value={countryQuery}
            onChange={(e) => setCountryQuery(e.target.value)}
            placeholder="Filter countries..."
          />
          <div className="border border-gray-200 dark:border-gray-700 rounded-md max-h-[240px] overflow-y-auto">
            {allCountries.loading && (
              <div className="p-2 text-xs text-gray-400">Loading...</div>
            )}
            {!allCountries.loading && filteredAvailable.length === 0 && (
              <div className="p-2 text-xs text-gray-400">
                {countryQuery ? "No matches" : "All countries selected"}
              </div>
            )}
            {filteredAvailable.map((c) => (
              <button
                key={c.iso2}
                onClick={() => addCountry(c.iso2)}
                className="block w-full text-left px-3 py-1.5 text-xs hover:bg-blue-50 dark:hover:bg-blue-500/10 cursor-pointer border-b border-gray-100 dark:border-gray-800 last:border-b-0"
              >
                <span className="font-mono font-semibold mr-2">{c.iso2}</span>
                <span className="dark:text-gray-200">{c.name}</span>
                <span className="ml-2 text-gray-400">{c.phone_code}</span>
              </button>
            ))}
          </div>
        </div>
      </Field>
    </Drawer>
  );
}

// ---------- Campaigns ----------

function CampaignsTab({ sub }: { sub: string | null }) {
  const { data, loading, reload } = useAsync<Campaign[]>(() =>
    fetch("/api/campaigns").then((r) => r.json() as Promise<Campaign[]>),
  );
  const urlDestinations = useAsync<UrlDestination[]>(() =>
    fetch("/api/url-destinations").then(
      (r) => r.json() as Promise<UrlDestination[]>,
    ),
  );
  const accounts = useAsync<Account[]>(() =>
    fetch("/api/accounts").then((r) => r.json() as Promise<Account[]>),
  );
  const [deleting, setDeleting] = useState<Campaign | null>(null);
  const [viewing, setViewing] = useState<Campaign | null>(null);
  const [evaluating, setEvaluating] = useState<Campaign | null>(null);

  // Sub-routes:
  //   #campaigns          → list
  //   #campaigns/new      → editor for new
  //   #campaigns/<id>     → editor for existing
  if (sub === "new" || (sub && /^\d+$/.test(sub))) {
    // Wait for campaigns + accounts + url destinations to load before mounting
    // the editor — otherwise its useState fields lock in empty defaults and
    // never re-init when the real campaign arrives async.
    if (sub !== "new" && (data === null || accounts.data === null || urlDestinations.data === null)) {
      return <div className="p-6 text-gray-400">Loading...</div>;
    }
    const editingCampaign =
      sub === "new"
        ? null
        : (data ?? []).find((c) => c.id === Number(sub)) ?? null;
    if (sub !== "new" && !editingCampaign) {
      return <div className="p-6 text-gray-400">Campaign #{sub} not found.</div>;
    }
    return (
      <CampaignEditPage
        // Force remount when switching between campaigns so all field state
        // is re-initialized from the loaded campaign data.
        key={sub === "new" ? "new" : `c-${editingCampaign?.id}`}
        campaign={editingCampaign}
        isNew={sub === "new"}
        urlDestinations={urlDestinations.data ?? []}
        accounts={accounts.data ?? []}
        onCancel={() => setHash("campaigns")}
        onSaved={() => {
          reload();
          setHash("campaigns");
        }}
      />
    );
  }

  return (
    <PageShell
      title="Campaigns"
      total={data?.length ?? 0}
      onAdd={() => setHash("campaigns", "new")}
    >
      {loading ? (
        <div className="p-6 text-gray-400">Loading...</div>
      ) : (
        <AgTable<Campaign>
          rows={data ?? []}
          columns={[
            {
              field: "name",
              headerName: "Name",
              cellRenderer: (p: ICellRendererParams<Campaign>) => (
                <span className="font-medium">{p.value}</span>
              ),
            },
            {
              field: "sms_text",
              headerName: "SMS Text",
              flex: 2,
              tooltipField: "sms_text",
            },
            {
              headerName: "Country",
              colId: "country",
              valueGetter: (p) =>
                p.data?.country_iso2
                  ? `${p.data.country_iso2} — ${p.data.country_name ?? ""}`
                  : "—",
              width: 160,
              flex: 0,
            },
            {
              headerName: "Account",
              colId: "account",
              valueGetter: (p) =>
                p.data?.provider_name && p.data?.account_id
                  ? `${p.data.provider_name} #${p.data.account_id}`
                  : p.data?.account_id
                    ? `#${p.data.account_id}`
                    : "—",
            },
            {
              headerName: "Destinations",
              colId: "destinations",
              valueGetter: (p) => p.data?.urls?.length ?? 0,
              filter: "agNumberColumnFilter",
              cellRenderer: (p: ICellRendererParams<Campaign>) => {
                const list = p.data?.url_details ?? [];
                if (list.length === 0) {
                  return <span className="text-gray-400 text-xs">—</span>;
                }
                const tip = list.map((u) => u.url).join("\n");
                return (
                  <span className="text-xs" title={tip}>
                    <span className="font-semibold text-blue-600 dark:text-blue-400 mr-2">
                      {list.length}
                    </span>
                    <span className="text-gray-600 dark:text-gray-400">
                      {list[0]?.name ?? list[0]?.url}
                      {list.length > 1 ? ` +${list.length - 1}` : ""}
                    </span>
                  </span>
                );
              },
              flex: 2,
            },
            {
              field: "total_count",
              headerName: "Total",
              filter: "agNumberColumnFilter",
              width: 90,
              flex: 0,
              cellRenderer: (p: ICellRendererParams<Campaign>) =>
                p.value > 0 ? (
                  <span className="font-semibold">{p.value}</span>
                ) : (
                  <span className="text-gray-400">0</span>
                ),
            },
            {
              field: "pending_count",
              headerName: "Pending",
              filter: "agNumberColumnFilter",
              width: 100,
              flex: 0,
              cellRenderer: (p: ICellRendererParams<Campaign>) =>
                p.value > 0 ? (
                  <span className="text-yellow-700 dark:text-yellow-400 font-medium">{p.value}</span>
                ) : (
                  <span className="text-gray-400">0</span>
                ),
            },
            {
              field: "ready_count",
              headerName: "Ready",
              filter: "agNumberColumnFilter",
              width: 90,
              flex: 0,
              cellRenderer: (p: ICellRendererParams<Campaign>) =>
                p.value > 0 ? (
                  <span className="text-emerald-700 dark:text-emerald-400 font-medium">{p.value}</span>
                ) : (
                  <span className="text-gray-400">0</span>
                ),
            },
            {
              field: "error_count",
              headerName: "Error",
              filter: "agNumberColumnFilter",
              width: 90,
              flex: 0,
              cellRenderer: (p: ICellRendererParams<Campaign>) =>
                p.value > 0 ? (
                  <span className="text-red-700 dark:text-red-400 font-medium">{p.value}</span>
                ) : (
                  <span className="text-gray-400">0</span>
                ),
            },
            {
              field: "created_at",
              headerName: "Created",
              filter: "agDateColumnFilter",
              valueFormatter: (p) =>
                p.value ? new Date(p.value).toLocaleString() : "",
            },
          ]}
          onEdit={(r) => setHash("campaigns", String(r.id))}
          onDelete={(r) => setDeleting(r)}
          actionsWidth={170}
          extraActions={(row) => [
            {
              icon: (
                <span className="inline-flex items-center justify-center w-4 h-4 rounded-full border border-current text-[10px] font-bold leading-none">
                  i
                </span>
              ),
              onClick: () => setViewing(row),
              title: "Sends details",
              className: "text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-800",
            },
            {
              icon: <span className="text-sm leading-none">🧪</span>,
              onClick: () => setEvaluating(row),
              title: "Evaluate — per-route CTR / CPL verdict",
              className:
                "text-purple-700 dark:text-purple-300 hover:bg-purple-100 dark:hover:bg-purple-500/20",
            },
          ]}
        />
      )}
      {viewing && (
        <CampaignSendsDrawer
          campaign={viewing}
          onClose={() => setViewing(null)}
        />
      )}
      {evaluating && (
        <EvaluateDialog
          campaignId={evaluating.id}
          onClose={() => setEvaluating(null)}
        />
      )}
      <ConfirmDialog
        open={!!deleting}
        title="Delete campaign"
        message={
          deleting && (
            <>
              Are you sure you want to delete campaign{" "}
              <span className="font-semibold text-gray-900 dark:text-gray-100">"{deleting.name}"</span>?
            </>
          )
        }
        onCancel={() => setDeleting(null)}
        onConfirm={async () => {
          if (!deleting) return;
          await fetch(`/api/campaigns/${deleting.id}`, { method: "DELETE" });
          setDeleting(null);
          reload();
        }}
      />
    </PageShell>
  );
}

function CampaignEditPage({
  campaign,
  isNew,
  urlDestinations,
  accounts,
  onCancel,
  onSaved,
}: {
  campaign: Campaign | null;
  isNew: boolean;
  urlDestinations: UrlDestination[];
  accounts: Account[];
  onCancel: () => void;
  onSaved: () => void;
}) {
  // Local copy of the campaign — updated after each successful save so the
  // page stays open in "edit" mode (showing +Phones/Start/Pause buttons)
  // immediately after creating a new one.
  const [currentCampaign, setCurrentCampaign] = useState<Campaign | null>(campaign);
  const [savedFlash, setSavedFlash] = useState<string | null>(null);

  const [name, setName] = useState(campaign?.name ?? "");
  const [smsText, setSmsText] = useState(campaign?.sms_text ?? "");
  const [aiVariants, setAiVariantsState] = useState<{ active: boolean; text: string }[]>(
    campaign?.sms_ai_variants ?? [],
  );
  const [urls, setUrls] = useState<number[]>(campaign?.urls ?? []);
  const [accountId, setAccountId] = useState<number | "">(
    campaign?.account_id ?? accounts[0]?.id ?? "",
  );
  const [countryId, setCountryId] = useState<number | "">(
    campaign?.country_id ?? "",
  );
  const [languageId, setLanguageId] = useState<number | "">(
    campaign?.language_id ?? "",
  );
  const [senderId, setSenderId] = useState(campaign?.sender_id ?? "");

  // Hard re-sync of form fields from the freshly-loaded campaign whenever
  // we land on a different campaign id (or it gets re-loaded with new data
  // after save). useState init alone runs only once; this catches the case
  // where the initial render saw a partial/empty campaign.
  useEffect(() => {
    if (!campaign?.id) return;
    setName(campaign.name ?? "");
    setSmsText(campaign.sms_text ?? "");
    setAiVariantsState(campaign.sms_ai_variants ?? []);
    setUrls(campaign.urls ?? []);
    setAccountId(campaign.account_id ?? "");
    setCountryId(campaign.country_id ?? "");
    setLanguageId(campaign.language_id ?? "");
    setSenderId(campaign.sender_id ?? "");
  }, [campaign?.id]);

  // AI generation state
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState<string | null>(null);

  // Share-variants modal (formatted "1. text\n2. text\n..." for moderation)
  const [shareOpen, setShareOpen] = useState(false);
  const [shareCopied, setShareCopied] = useState(false);
  const shareText = useMemo(
    () => aiVariants.map((v, i) => `${i + 1}. ${v.text}`).join("\n"),
    [aiVariants],
  );

  // Phone picker / bulk status
  const [phonesPickerOpen, setPhonesPickerOpen] = useState(false);
  const [sendsOpen, setSendsOpen] = useState(false);
  const [evalOpen, setEvalOpen] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkMsg, setBulkMsg] = useState<string | null>(null);

  // Live worker status (polls every 2s while the campaign is open). Shows
  // counts per status + last phone the worker touched, so the user can
  // visually confirm the worker is processing after pressing Start / Pause.
  type LiveStatus = {
    counts: Record<string, number>;
    total: number;
    last: {
      phone: string | null;
      status: string;
      error_message: string | null;
      updated_at: string;
    } | null;
    account_frozen?: {
      balance: number | null;
      threshold: number;
      frozen: boolean;
    } | null;
  };
  const [liveStatus, setLiveStatus] = useState<LiveStatus | null>(null);
  useEffect(() => {
    if (!currentCampaign?.id) {
      setLiveStatus(null);
      return;
    }
    // Pause polling while the phones picker / sends drawer / evaluate dialog
    // is open — frequent setLiveStatus re-renders this editor, which in turn
    // re-renders the dialog and resets in-flight ag-grid filter popups.
    if (phonesPickerOpen || sendsOpen || evalOpen) return;
    let stop = false;
    const tick = async () => {
      try {
        const r = await fetch(
          `/api/campaigns/${currentCampaign.id}/live-status`,
        );
        if (!r.ok) return;
        const j = (await r.json()) as LiveStatus;
        if (!stop) setLiveStatus(j);
      } catch {
        /* ignore — next tick will retry */
      }
    };
    tick();
    const t = setInterval(tick, 2000);
    return () => {
      stop = true;
      clearInterval(t);
    };
  }, [currentCampaign?.id, phonesPickerOpen, sendsOpen, evalOpen]);

  // Count of phones already attached to this campaign — shown as a badge
  // on the +Phones button. Refreshed every 5s + after picker / bulk-status
  // actions so the count tracks live worker progress too.
  const [phonesCount, setPhonesCount] = useState<number | null>(null);
  const reloadPhonesCount = React.useCallback(async () => {
    if (!currentCampaign?.id) {
      setPhonesCount(null);
      return;
    }
    try {
      const res = await fetch(
        `/api/campaigns-phones?campaign_id=${currentCampaign.id}`,
      );
      if (!res.ok) return;
      const rows = (await res.json()) as unknown[];
      setPhonesCount(Array.isArray(rows) ? rows.length : 0);
    } catch {
      // ignore — badge will just stay stale
    }
  }, [currentCampaign?.id]);
  useEffect(() => {
    reloadPhonesCount();
    const t = setInterval(reloadPhonesCount, 5_000);
    return () => clearInterval(t);
  }, [reloadPhonesCount]);

  async function bulkStatus(from: string[], to: string, label: string) {
    if (!currentCampaign?.id) {
      setBulkMsg("Save the campaign first.");
      return;
    }
    setBulkBusy(true);
    setBulkMsg(`${label}…`);
    try {
      const res = await fetch(`/api/campaigns/${currentCampaign.id}/phones/bulk-status`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ from, to }),
      });
      if (!res.ok) {
        setBulkMsg(`${label} failed: ${await res.text()}`);
      } else {
        const data = (await res.json()) as { updated: number };
        setBulkMsg(`${label}: ${data.updated} phones updated`);
      }
    } catch (e: any) {
      setBulkMsg(String(e?.message ?? e));
    } finally {
      setBulkBusy(false);
      setTimeout(() => setBulkMsg(null), 4000);
    }
  }

  async function generateAiTexts() {
    setAiError(null);
    if (!smsText.trim()) {
      setAiError("Enter base SMS text first.");
      return;
    }
    if (selectedShortDomainIds.length === 0) {
      setAiError(
        "Pick at least one Short Domain first (needed to compute SMS length).",
      );
      return;
    }
    if (!languageId) {
      setAiError("Select Language first.");
      return;
    }
    const longestDomain = selectedShortDomainIds
      .map((id) => shortDomainById.get(id)?.domain ?? "")
      .reduce((a, b) => (b.length > a.length ? b : a), "");
    setAiLoading(true);
    try {
      const res = await fetch("/api/ai/generate-texts", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          base_text: smsText,
          count: 5,
          language_id: languageId,
          short_domain: longestDomain,
        }),
      });
      if (!res.ok) {
        setAiError(await res.text());
        return;
      }
      const data = (await res.json()) as { variants: string[] };
      // Save all 5 to campaign — none active initially (awaiting moderation).
      setAiVariantsState(data.variants.map((text) => ({ active: false, text })));
    } catch (e: any) {
      setAiError(String(e?.message ?? e));
    } finally {
      setAiLoading(false);
    }
  }

  function activateVariant(idx: number) {
    setAiVariantsState((vs) =>
      vs.map((v, i) => ({ ...v, active: i === idx ? !v.active : false })),
    );
  }
  function removeVariant(idx: number) {
    setAiVariantsState((vs) => vs.filter((_, i) => i !== idx));
  }

  const [countries, setCountries] = useState<Country[]>([]);
  const [languages, setLanguages] = useState<Language[]>([]);
  useEffect(() => {
    fetch("/api/countries")
      .then((r) => r.json() as Promise<Country[]>)
      .then((data) => setCountries(Array.isArray(data) ? data : []))
      .catch(() => {});
    fetch("/api/languages")
      .then((r) => r.json() as Promise<Language[]>)
      .then((data) => setLanguages(Array.isArray(data) ? data : []))
      .catch(() => {});
  }, []);

  const countryIso2 = useMemo(
    () => countries.find((c) => c.id === countryId)?.iso2 ?? null,
    [countries, countryId],
  );

  // Available routes (filtered by selected country).
  const [routes, setRoutes] = useState<Route[]>([]);
  const [selectedRouteIds, setSelectedRouteIds] = useState<number[]>([]);
  const [routesLoading, setRoutesLoading] = useState(false);

  // Load campaign's currently selected routes on mount.
  // Load campaign's currently selected routes ONCE per id — depending on the
  // whole `campaign` object reference would re-fire whenever the parent re-renders
  // with a fresh array from useAsync.reload(), and clobber unsaved picks with
  // whatever the server currently has.
  useEffect(() => {
    if (!campaign?.id) return;
    fetch(`/api/campaigns/${campaign.id}/routes`)
      .then((r) => r.json() as Promise<Route[]>)
      .then((rs) => setSelectedRouteIds(Array.isArray(rs) ? rs.map((r) => r.id) : []))
      .catch(() => {});
  }, [campaign?.id]);

  // Short domains (multi-select, round-robin'd on assign).
  const [shortDomains, setShortDomains] = useState<ShortDomain[]>([]);
  const [selectedShortDomainIds, setSelectedShortDomainIds] = useState<number[]>([]);
  useEffect(() => {
    fetch("/api/short-domains")
      .then((r) => r.json() as Promise<ShortDomain[]>)
      .then((data) => setShortDomains(Array.isArray(data) ? data : []))
      .catch(() => {});
  }, []);
  useEffect(() => {
    if (!campaign?.id) return;
    fetch(`/api/campaigns/${campaign.id}/short-domains`)
      .then((r) => r.json() as Promise<{ short_domain_id: number }[]>)
      .then((rows) =>
        setSelectedShortDomainIds(
          Array.isArray(rows) ? rows.map((r) => r.short_domain_id) : [],
        ),
      )
      .catch(() => {});
  }, [campaign?.id]);
  const shortDomainById = useMemo(() => {
    const m = new Map<number, ShortDomain>();
    for (const d of shortDomains) m.set(d.id, d);
    return m;
  }, [shortDomains]);
  const shortDomainSet = useMemo(
    () => new Set(selectedShortDomainIds),
    [selectedShortDomainIds],
  );
  function toggleShortDomain(id: number) {
    setSelectedShortDomainIds((ids) =>
      ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id],
    );
  }
  const [shortDomainQuery, setShortDomainQuery] = useState("");
  const filteredAvailableShortDomains = useMemo(() => {
    const q = shortDomainQuery.trim().toLowerCase();
    return shortDomains.filter(
      (d) =>
        !shortDomainSet.has(d.id) &&
        (q === "" ||
          d.domain.toLowerCase().includes(q) ||
          (d.provider ?? "").toLowerCase().includes(q)),
    );
  }, [shortDomains, shortDomainSet, shortDomainQuery]);

  // Routes are filtered by BOTH country and provider driver of the chosen
  // account. Any change to either resets the selection — the previously
  // chosen routes may not be valid for the new combo.
  const selectedAccount = useMemo(
    () => accounts.find((a) => a.id === accountId) ?? null,
    [accounts, accountId],
  );
  const providerDriver = selectedAccount?.provider_driver ?? null;

  useEffect(() => {
    if (!countryIso2 || !providerDriver) {
      setRoutes([]);
      return;
    }
    setRoutesLoading(true);
    const qs = new URLSearchParams({
      country: countryIso2,
      provider: providerDriver,
      active: "true",
    });
    fetch(`/api/routes?${qs.toString()}`)
      .then((r) => r.json() as Promise<Route[]>)
      .then((data) => setRoutes(Array.isArray(data) ? data : []))
      .catch(() => setRoutes([]))
      .finally(() => setRoutesLoading(false));
    // NOTE: do not auto-clear selectedRouteIds on country/provider change —
    // it was the source of "routes disappear on save" bugs (races with the
    // load-existing-routes effect, weird ordering with async account fetch,
    // etc.). If the user picks a new combo, stale routes simply won't be
    // in the available list any more; they can remove the chip manually.
  }, [countryIso2, providerDriver]);

  const selectedRouteSet = useMemo(
    () => new Set(selectedRouteIds),
    [selectedRouteIds],
  );
  const routeById = useMemo(() => {
    const m = new Map<number, Route>();
    for (const r of routes) m.set(r.id, r);
    return m;
  }, [routes]);
  const filteredAvailableRoutes = useMemo(
    () => routes.filter((r) => !selectedRouteSet.has(r.id)),
    [routes, selectedRouteSet],
  );

  function addRoute(id: number) {
    if (!selectedRouteSet.has(id)) setSelectedRouteIds([...selectedRouteIds, id]);
  }
  function removeRoute(id: number) {
    setSelectedRouteIds(selectedRouteIds.filter((x) => x !== id));
  }

  const [urlQuery, setUrlQuery] = useState("");
  const selectedUrlSet = useMemo(() => new Set(urls), [urls]);
  const filteredAvailableUrls = useMemo(() => {
    const q = urlQuery.trim().toLowerCase();
    return urlDestinations.filter(
      (u) =>
        !selectedUrlSet.has(u.id) &&
        (q === "" ||
          (u.name ?? "").toLowerCase().includes(q) ||
          u.url.toLowerCase().includes(q)),
    );
  }, [urlDestinations, selectedUrlSet, urlQuery]);
  const urlById = useMemo(() => {
    const m = new Map<number, UrlDestination>();
    for (const u of urlDestinations) m.set(u.id, u);
    return m;
  }, [urlDestinations]);

  function addUrl(id: number) {
    if (!selectedUrlSet.has(id)) setUrls([...urls, id]);
  }
  function removeUrl(id: number) {
    setUrls(urls.filter((u) => u !== id));
  }
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [errors, setErrors] = useState<Record<string, string | null>>({});

  function setFieldError(key: string, value: string | null) {
    setErrors((e) => ({ ...e, [key]: value }));
  }

  async function save() {
    setFormError(null);
    const next: Record<string, string | null> = {};
    if (!name.trim()) next.name = "Name is required";
    if (!smsText.trim()) next.smsText = "SMS text is required";
    if (selectedShortDomainIds.length === 0)
      next.shortDomains = "At least one Short Domain is required";
    if (urls.length === 0) next.urls = "At least one destination is required";
    if (!accountId) next.accountId = "Account is required";
    setErrors(next);
    if (Object.values(next).some(Boolean)) return;

    setSaving(true);
    const body = JSON.stringify({
      name: name.trim(),
      sms_text: smsText,
      sms_ai_variants: aiVariants,
      urls,
      account_id: accountId,
      country_id: countryId || null,
      language_id: languageId || null,
      sender_id: senderId.trim() || null,
    });
    const res = currentCampaign?.id
      ? await fetch(`/api/campaigns/${currentCampaign.id}`, {
          method: "PUT",
          headers: { "content-type": "application/json" },
          body,
        })
      : await fetch("/api/campaigns", {
          method: "POST",
          headers: { "content-type": "application/json" },
          body,
        });
    if (!res.ok) {
      setSaving(false);
      setFormError(await res.text());
      return;
    }
    const saved = (await res.json()) as Campaign;
    if (saved?.id) {
      await fetch(`/api/campaigns/${saved.id}/routes`, {
        method: "PUT",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ route_ids: selectedRouteIds }),
      });
      await fetch(`/api/campaigns/${saved.id}/short-domains`, {
        method: "PUT",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ short_domain_ids: selectedShortDomainIds }),
      });
    }
    setSaving(false);
    setCurrentCampaign(saved);
    // Update hash so refresh keeps us on the same record + bookmarkable url.
    if (saved?.id && location.hash !== `#campaigns/${saved.id}`) {
      history.replaceState(null, "", `#campaigns/${saved.id}`);
    }
    setSavedFlash(`Saved — campaign #${saved.id}`);
    setTimeout(() => setSavedFlash(null), 3000);
  }

  const title = !currentCampaign?.id
    ? "New Campaign"
    : `Edit Campaign #${currentCampaign.id} — ${currentCampaign.name ?? name}`;

  return (
    <div className="flex flex-col h-full bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-800 overflow-hidden">
      <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200 dark:border-gray-800">
        <div className="flex items-center gap-2 text-sm min-w-0">
          <button
            onClick={onCancel}
            className="text-blue-600 dark:text-blue-400 hover:underline cursor-pointer"
          >
            Campaigns
          </button>
          <span className="text-gray-400">/</span>
          <span className="font-semibold dark:text-gray-100 truncate">{title}</span>
          {savedFlash && (
            <span className="ml-3 px-2 py-0.5 rounded text-xs font-medium bg-emerald-100 dark:bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 whitespace-nowrap">
              ✓ {savedFlash}
            </span>
          )}
          {bulkMsg && (
            <span className="ml-3 px-2 py-0.5 rounded text-xs font-medium bg-blue-100 dark:bg-blue-500/15 text-blue-700 dark:text-blue-300 whitespace-nowrap truncate max-w-[40ch]" title={bulkMsg}>
              {bulkBusy ? "⏳ " : "→ "}{bulkMsg}
            </span>
          )}
          {bulkBusy && !bulkMsg && (
            <span className="ml-3 px-2 py-0.5 rounded text-xs font-medium bg-blue-100 dark:bg-blue-500/15 text-blue-700 dark:text-blue-300 whitespace-nowrap">
              ⏳ Working…
            </span>
          )}
          {liveStatus && liveStatus.total > 0 && (
            <LiveStatusBar status={liveStatus} />
          )}
        </div>
        <div className="flex items-center gap-2">
          {currentCampaign?.id && (
            <>
              <button
                onClick={() => setSendsOpen(true)}
                className="px-3 py-1.5 rounded-md border border-gray-300 dark:border-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 text-sm cursor-pointer inline-flex items-center gap-1.5"
                title="Open sends — phones, statuses, auto-refresh every 5s"
              >
                <span className="inline-flex items-center justify-center w-4 h-4 rounded-full border border-current text-[10px] font-bold leading-none">
                  i
                </span>
                Info
              </button>
              <button
                onClick={() => setPhonesPickerOpen(true)}
                className="px-3 py-1.5 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium cursor-pointer inline-flex items-center gap-1.5"
                title="Open phones picker — adds selected phones as paused"
              >
                + Phones
                {phonesCount != null && phonesCount > 0 && (
                  <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-white/25 text-white text-xs font-bold leading-none">
                    {phonesCount}
                  </span>
                )}
              </button>
              {(() => {
                const c = liveStatus?.counts ?? {};
                const pausedN = (c as any).paused ?? 0;
                const pendingN = (c as any).pending ?? 0;
                const errorsN =
                  ((c as any).error ?? 0) + ((c as any).error_provider ?? 0);
                const noStatusYet = liveStatus === null;
                return (
                  <>
                    <button
                      onClick={() =>
                        bulkStatus(
                          ["paused"],
                          "pending",
                          "Start (paused→pending)",
                        )
                      }
                      disabled={
                        bulkBusy || (!noStatusYet && pausedN === 0)
                      }
                      className="px-3 py-1.5 rounded-md bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm cursor-pointer"
                      title={
                        !noStatusYet && pausedN === 0
                          ? "Nothing to start — no paused phones"
                          : `Activate ${pausedN || "all"} paused phones — worker will start sending`
                      }
                    >
                      ▶ Start{!noStatusYet && pausedN > 0 ? ` (${pausedN})` : ""}
                    </button>
                    <button
                      onClick={() =>
                        bulkStatus(
                          ["pending"],
                          "paused",
                          "Pause (pending→paused)",
                        )
                      }
                      disabled={
                        bulkBusy || (!noStatusYet && pendingN === 0)
                      }
                      className="px-3 py-1.5 rounded-md border border-gray-300 dark:border-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed text-sm cursor-pointer"
                      title={
                        !noStatusYet && pendingN === 0
                          ? "Nothing to pause — no pending phones"
                          : `Pause ${pendingN || "all"} queued phones — worker stops picking them`
                      }
                    >
                      ⏸ Pause{!noStatusYet && pendingN > 0 ? ` (${pendingN})` : ""}
                    </button>
                    <button
                      onClick={() =>
                        bulkStatus(
                          ["error", "error_provider"],
                          "pending",
                          "Retry errors",
                        )
                      }
                      disabled={
                        bulkBusy || (!noStatusYet && errorsN === 0)
                      }
                      className="px-3 py-1.5 rounded-md border border-amber-300 dark:border-amber-500/40 text-amber-700 dark:text-amber-300 hover:bg-amber-50 dark:hover:bg-amber-500/10 disabled:opacity-50 disabled:cursor-not-allowed text-sm cursor-pointer"
                      title={
                        !noStatusYet && errorsN === 0
                          ? "No errored phones to retry"
                          : `Move ${errorsN || "all"} errored phones back to pending`
                      }
                    >
                      ↻ Retry errors{!noStatusYet && errorsN > 0 ? ` (${errorsN})` : ""}
                    </button>
                  </>
                );
              })()}
              <button
                onClick={() => setEvalOpen(true)}
                className="px-3 py-1.5 rounded-md border border-purple-300 dark:border-purple-500/40 text-purple-700 dark:text-purple-300 hover:bg-purple-50 dark:hover:bg-purple-500/10 text-sm cursor-pointer"
                title="Apply the evaluator (CTR / fail rate / CPL rules) to this campaign — read-only"
              >
                🧪 Evaluate
              </button>
              <span className="w-px h-6 bg-gray-200 dark:bg-gray-700 mx-1" />
            </>
          )}
          <button
            onClick={() => {
              if (currentCampaign?.id) onSaved();
              else onCancel();
            }}
            className="px-4 py-1.5 rounded-md border border-gray-300 dark:border-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 text-sm cursor-pointer"
          >
            {currentCampaign?.id ? "← Back to list" : "Cancel"}
          </button>
          <button
            onClick={save}
            disabled={saving}
            className="px-4 py-1.5 rounded-md bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-medium cursor-pointer"
          >
            {saving ? "Saving..." : "Save"}
          </button>
        </div>
      </div>
      <div className="flex-1 overflow-auto">
        <div className="p-4 space-y-3">
          {formError && (
            <div className="bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/30 text-red-700 dark:text-red-300 text-sm px-3 py-2 rounded">
              {formError}
            </div>
          )}

          {/* Top section — split: left=core fields (name/country/language/sender/account),
              right=short domains pool. */}
          <div className="grid grid-cols-2 gap-4">
            {/* Left column */}
            <div className="space-y-3">
              <Field label="Name" required error={errors.name}>
                <input
                  className={errors.name ? inputErrorCls : inputCls}
                  value={name}
                  onChange={(e) => {
                    setName(e.target.value);
                    if (errors.name) setFieldError("name", null);
                  }}
                  placeholder="Summer promo"
                  autoFocus
                />
              </Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Country">
                  <Combobox
                    value={countryId === "" ? "" : String(countryId)}
                    onChange={(next) => setCountryId(next ? Number(next) : "")}
                    placeholder="— any —"
                    options={countries.map((c) => ({
                      value: String(c.id),
                      label: `${c.name} (${c.iso2}) ${c.phone_code}`,
                      searchKey: `${c.name} ${c.iso2} ${c.iso3} ${c.phone_code}`,
                    }))}
                  />
                </Field>
                <Field label="Language">
                  <Combobox
                    value={languageId === "" ? "" : String(languageId)}
                    onChange={(next) => setLanguageId(next ? Number(next) : "")}
                    placeholder="— not set —"
                    options={languages.map((l) => ({
                      value: String(l.id),
                      label: `${l.name} (${l.code})${l.native_name ? ` — ${l.native_name}` : ""}`,
                      searchKey: `${l.name} ${l.code} ${l.native_name ?? ""}`,
                    }))}
                  />
                </Field>
              </div>
              <Field label="Sender">
                <input
                  className={inputCls}
                  value={senderId}
                  onChange={(e) => setSenderId(e.target.value)}
                  placeholder="INFO (≤11 chars) or numeric"
                  maxLength={15}
                />
              </Field>
              <Field label="Account" required error={errors.accountId}>
                <select
                  className={errors.accountId ? inputErrorCls : inputCls}
                  value={accountId}
                  onChange={(e) => {
                    setAccountId(Number(e.target.value));
                    if (errors.accountId) setFieldError("accountId", null);
                  }}
                >
                  <option value="">— select account —</option>
                  {accounts.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.provider_name ? `${a.provider_name} — ${a.key}` : a.key}
                    </option>
                  ))}
                </select>
              </Field>

              {/* Routes — depend on country + provider of the account above */}
              <Field label={`Routes (${selectedRouteIds.length})`}>
                <div className="space-y-2">
                  <div className="flex flex-wrap gap-1 min-h-[36px] p-2 border border-gray-200 dark:border-gray-700 rounded-md bg-gray-50 dark:bg-gray-800">
                    {selectedRouteIds.length === 0 && (
                      <span className="text-xs text-gray-400 self-center">
                        No routes selected
                      </span>
                    )}
                    {selectedRouteIds.map((id) => {
                      const r = routeById.get(id);
                      return (
                        <span
                          key={id}
                          className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-blue-100 dark:bg-blue-500/20 text-blue-800 dark:text-blue-200"
                          title={
                            r ? `${r.country_iso2 ?? ""} ${r.external_country_name ?? ""} ${r.price ?? ""}`.trim() : ""
                          }
                        >
                          <span>{r?.name ?? `#${id}`}</span>
                          {r && (
                            <span className="text-blue-700/70 dark:text-blue-300/70">
                              #{r.external_id}
                            </span>
                          )}
                          <button
                            onClick={() => removeRoute(id)}
                            className="ml-0.5 text-blue-700/70 hover:text-blue-900 dark:text-blue-300/70 dark:hover:text-blue-100 cursor-pointer"
                            title="Remove"
                          >
                            ×
                          </button>
                        </span>
                      );
                    })}
                  </div>
                  {(!countryIso2 || !providerDriver) && (
                    <div className="text-xs text-gray-400 p-2">
                      {!countryIso2 && !providerDriver
                        ? "Pick a country and account first to load routes."
                        : !countryIso2
                          ? "Pick a country first to load routes."
                          : "Pick an account first — routes depend on the provider."}
                    </div>
                  )}
                  {countryIso2 && providerDriver && (
                    <div className="border border-gray-200 dark:border-gray-700 rounded-md max-h-[220px] overflow-y-auto">
                      {routesLoading && (
                        <div className="p-2 text-xs text-gray-400">Loading routes...</div>
                      )}
                      {!routesLoading && filteredAvailableRoutes.length === 0 && (
                        <div className="p-2 text-xs text-gray-400">
                          {routes.length === 0
                            ? `No routes for ${providerDriver}/${countryIso2} — provider will use default routing.`
                            : "All available routes selected"}
                        </div>
                      )}
                      {filteredAvailableRoutes.map((r) => (
                        <button
                          key={r.id}
                          onClick={() => addRoute(r.id)}
                          className="block w-full text-left px-3 py-1.5 text-xs hover:bg-blue-50 dark:hover:bg-blue-500/10 cursor-pointer border-b border-gray-100 dark:border-gray-800 last:border-b-0"
                        >
                          <div className="flex items-center gap-2">
                            <span className="font-medium dark:text-gray-200">{r.name}</span>
                            <span className="text-gray-400 font-mono">#{r.external_id}</span>
                            {r.price != null && (
                              <span className="ml-auto text-gray-400">{r.price}</span>
                            )}
                          </div>
                          {(r.country_iso2 || r.external_country_name) && (
                            <div className="mt-0.5 text-[10px] text-gray-400 font-mono">
                              {r.country_iso2 ? `${r.country_iso2} — ` : ""}
                              {r.external_country_name ?? ""}
                            </div>
                          )}
                        </button>
                      ))}
                    </div>
                  )}
                  <p className="text-xs text-gray-400">
                    Routes rotate round-robin per message.
                  </p>
                </div>
              </Field>
            </div>

            {/* Right column — Short Domains + URL Destinations */}
            <div className="space-y-3">
            <Field
              label={`Short Domains (${selectedShortDomainIds.length}, round-robin)`}
              required
              error={errors.shortDomains}
            >
            <div className="space-y-2">
              <div className="flex flex-wrap gap-1 min-h-[36px] p-2 border border-gray-200 dark:border-gray-700 rounded-md bg-gray-50 dark:bg-gray-800">
                {selectedShortDomainIds.length === 0 && (
                  <span className="text-xs text-gray-400 self-center">
                    Pick at least one short domain — phones rotate through them.
                  </span>
                )}
                {selectedShortDomainIds.map((id, i) => {
                  const d = shortDomainById.get(id);
                  return (
                    <span
                      key={id}
                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-blue-100 dark:bg-blue-500/20 text-blue-800 dark:text-blue-200"
                      title={`Round-robin position ${i + 1}`}
                    >
                      <span className="text-blue-700/70 dark:text-blue-300/70">
                        #{i + 1}
                      </span>
                      <span className="font-mono">{d?.domain ?? `#${id}`}</span>
                      <button
                        onClick={() => toggleShortDomain(id)}
                        className="ml-0.5 text-blue-700/70 hover:text-blue-900 dark:text-blue-300/70 dark:hover:text-blue-100 cursor-pointer"
                        title="Remove"
                      >
                        ×
                      </button>
                    </span>
                  );
                })}
              </div>
              <input
                className={inputCls}
                value={shortDomainQuery}
                onChange={(e) => setShortDomainQuery(e.target.value)}
                placeholder={`Filter ${shortDomains.length} domains...`}
              />
              <div className="border border-gray-200 dark:border-gray-700 rounded-md max-h-[320px] overflow-y-auto">
                {shortDomains.length === 0 && (
                  <div className="p-2 text-xs text-gray-400">
                    No short domains in the system — add some on the Short Domains tab.
                  </div>
                )}
                {shortDomains.length > 0 && filteredAvailableShortDomains.length === 0 && (
                  <div className="p-2 text-xs text-gray-400">
                    {shortDomainQuery ? "No matches" : "All domains already selected"}
                  </div>
                )}
                {filteredAvailableShortDomains.map((d) => (
                  <button
                    key={d.id}
                    type="button"
                    onClick={() => toggleShortDomain(d.id)}
                    className="block w-full text-left px-3 py-1.5 text-xs hover:bg-blue-50 dark:hover:bg-blue-500/10 cursor-pointer border-b border-gray-100 dark:border-gray-800 last:border-b-0"
                  >
                    <span className="font-mono dark:text-gray-200">{d.domain}</span>
                    {d.provider && (
                      <span className="ml-2 text-gray-400">{d.provider}</span>
                    )}
                  </button>
                ))}
              </div>
              <p className="text-xs text-gray-400">
                When set, phones in this campaign rotate through these domains on
                Assign (round-robin).
              </p>
            </div>
          </Field>

          <Field label={`URL Destinations (${urls.length})`} required error={errors.urls}>
            <div className="space-y-2">
              <div className="flex flex-wrap gap-1 min-h-[36px] p-2 border border-gray-200 dark:border-gray-700 rounded-md bg-gray-50 dark:bg-gray-800">
                {urls.length === 0 && (
                  <span className="text-xs text-gray-400 self-center">
                    No destinations selected
                  </span>
                )}
                {urls.map((id) => {
                  const u = urlById.get(id);
                  return (
                    <span
                      key={id}
                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-blue-100 dark:bg-blue-500/20 text-blue-800 dark:text-blue-200"
                    >
                      <span>{u?.name ?? u?.url ?? `#${id}`}</span>
                      <button
                        onClick={() => {
                          removeUrl(id);
                          if (errors.urls) setFieldError("urls", null);
                        }}
                        className="ml-0.5 text-blue-700/70 hover:text-blue-900 dark:text-blue-300/70 dark:hover:text-blue-100 cursor-pointer"
                        title="Remove"
                      >
                        ×
                      </button>
                    </span>
                  );
                })}
              </div>
              <input
                className={inputCls}
                value={urlQuery}
                onChange={(e) => setUrlQuery(e.target.value)}
                placeholder="Filter destinations..."
              />
              <div className="border border-gray-200 dark:border-gray-700 rounded-md max-h-[200px] overflow-y-auto">
                {filteredAvailableUrls.length === 0 && (
                  <div className="p-2 text-xs text-gray-400">
                    {urlQuery ? "No matches" : "All destinations selected"}
                  </div>
                )}
                {filteredAvailableUrls.map((u) => (
                  <button
                    key={u.id}
                    onClick={() => {
                      addUrl(u.id);
                      if (errors.urls) setFieldError("urls", null);
                    }}
                    className="block w-full text-left px-3 py-1.5 text-xs hover:bg-blue-50 dark:hover:bg-blue-500/10 cursor-pointer border-b border-gray-100 dark:border-gray-800 last:border-b-0"
                  >
                    {u.name && (
                      <span className="font-semibold mr-2 dark:text-gray-200">{u.name}</span>
                    )}
                    <span className="text-gray-500 dark:text-gray-400">{u.url}</span>
                  </button>
                ))}
              </div>
            </div>
          </Field>
            </div>
          </div>

          {/* Row 3: SMS Text (base) + 5 AI variant cards once generated.
              Base text always 1/6 width; cards fill the right 5/6. */}
          <Field label="SMS Text (base)" required error={errors.smsText}>
            {(() => {
              // Hoist the URL-substitution length once — reused below for both
              // the base textarea counter and each AI variant card so they all
              // measure the same expanded form (slug is 9 chars + "https://").
              const longest = selectedShortDomainIds
                .map((id) => shortDomainById.get(id)?.domain ?? "")
                .reduce((a, b) => (b.length > a.length ? b : a), "");
              const urlLen = longest ? "https://".length + longest.length + 9 : 0;
              const expandedBase = urlLen
                ? smsText.replaceAll("{url}", "x".repeat(urlLen))
                : smsText;
              const mBase = measureSms(expandedBase);
              const overSingle = mBase.segments > 1;
              const remaining = mBase.singleCap - mBase.units;
              return (
                <div className="flex items-center gap-2 text-xs mb-1">
                  <span
                    className={
                      "px-1.5 py-0.5 rounded font-semibold " +
                      (mBase.encoding === "gsm7"
                        ? "bg-emerald-100 dark:bg-emerald-500/20 text-emerald-700 dark:text-emerald-300"
                        : "bg-amber-100 dark:bg-amber-500/20 text-amber-700 dark:text-amber-300")
                    }
                    title={
                      mBase.encoding === "gsm7"
                        ? "GSM-7 — ASCII / basic Latin, fits 160 per SMS"
                        : `UCS-2 (Unicode) — Hebrew/Cyrillic/CJK/emoji force 70 per SMS. Non-GSM chars: ${mBase.nonGsmChars.slice(0, 8).join(" ")}`
                    }
                  >
                    {mBase.encoding === "gsm7" ? "GSM-7" : "UCS-2"}
                  </span>
                  <span
                    className={
                      "font-mono " +
                      (overSingle
                        ? "text-red-700 dark:text-red-300 font-bold"
                        : remaining < 10
                          ? "text-amber-700 dark:text-amber-300"
                          : "text-gray-600 dark:text-gray-400")
                    }
                  >
                    {mBase.units}/{mBase.singleCap}
                    {overSingle ? (
                      <>
                        {" "}— <b>{mBase.segments} SMS</b> ⚠
                      </>
                    ) : (
                      <> ({remaining} left)</>
                    )}
                  </span>
                  {urlLen > 0 && smsText.includes("{url}") && (
                    <span className="text-gray-400" title="Counted as if {url} were already substituted with the longest selected short_domain">
                      url≈{urlLen}
                    </span>
                  )}
                  {urlLen === 0 && smsText.includes("{url}") && (
                    <span className="text-gray-400" title="Pick at least one Short Domain to make url length accurate">
                      pick short_domain →
                    </span>
                  )}
                </div>
              );
            })()}
            <div className="grid grid-cols-6 gap-2">
              <div className="col-span-1">
                {(() => {
                  // Compute again (cheap) in this inner scope so we can apply
                  // a red border to the textarea itself when over the 1-SMS cap.
                  const longest = selectedShortDomainIds
                    .map((id) => shortDomainById.get(id)?.domain ?? "")
                    .reduce((a, b) => (b.length > a.length ? b : a), "");
                  const urlLen = longest
                    ? "https://".length + longest.length + 9
                    : 0;
                  const expanded = urlLen
                    ? smsText.replaceAll("{url}", "x".repeat(urlLen))
                    : smsText;
                  const m = measureSms(expanded);
                  const overSingle = m.segments > 1;
                  const cls =
                    (errors.smsText || overSingle ? inputErrorCls : inputCls) +
                    " min-h-[70px]" +
                    (overSingle && !errors.smsText
                      ? " ring-2 ring-red-400 dark:ring-red-500/60"
                      : "");
                  return (
                    <textarea
                      className={cls}
                      value={smsText}
                      onChange={(e) => {
                        setSmsText(e.target.value);
                        if (errors.smsText) setFieldError("smsText", null);
                      }}
                      placeholder="Hello! Check out our offer: {url}"
                    />
                  );
                })()}
                <button
                  type="button"
                  onClick={generateAiTexts}
                  disabled={aiLoading}
                  className="mt-2 w-full px-3 py-1.5 rounded-md bg-purple-600 hover:bg-purple-700 disabled:opacity-50 text-white text-xs cursor-pointer"
                  title="Generate 5 AI variants with Claude"
                >
                  {aiLoading
                    ? "Generating..."
                    : aiVariants.length > 0
                      ? "✨ Regenerate"
                      : "✨ Generate 5 variants"}
                </button>
                <button
                  type="button"
                  onClick={() => setShareOpen(true)}
                  disabled={aiVariants.length === 0}
                  className="mt-1 w-full px-3 py-1.5 rounded-md border border-purple-300 dark:border-purple-500/40 text-purple-700 dark:text-purple-300 hover:bg-purple-50 dark:hover:bg-purple-500/10 disabled:opacity-40 disabled:cursor-not-allowed text-xs cursor-pointer"
                  title="Show all 5 variants as a numbered list (for sharing/moderation)"
                >
                  📋 Share list
                </button>
                {aiError && (
                  <div className="mt-2 text-xs text-red-600 dark:text-red-400">
                    {aiError}
                  </div>
                )}
              </div>
              {aiVariants.map((v, i) => {
                const longest = selectedShortDomainIds
                  .map((id) => shortDomainById.get(id)?.domain ?? "")
                  .reduce((a, b) => (b.length > a.length ? b : a), "");
                const urlLen = longest ? "https://".length + longest.length + 9 : 0;
                const expanded = urlLen
                  ? v.text.replaceAll("{url}", "x".repeat(urlLen))
                  : v.text;
                const m = measureSms(expanded);
                const isGsm7 = m.encoding === "gsm7";
                const cls = v.active
                  ? "border-emerald-500 ring-2 ring-emerald-400 bg-emerald-50 dark:bg-emerald-500/10"
                  : isGsm7
                    ? m.segments === 1
                      ? "border-gray-300 dark:border-gray-700 hover:border-blue-400"
                      : "border-blue-300 dark:border-blue-500/40 hover:border-blue-500"
                    : "border-amber-300 dark:border-amber-500/40 hover:border-amber-500";
                return (
                  <div
                    key={i}
                    onClick={() => activateVariant(i)}
                    className={`col-span-1 border-2 rounded-md p-2 cursor-pointer transition relative min-h-[70px] flex flex-col ${cls}`}
                    title={v.active ? "Active — click to deactivate" : "Click to activate"}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span
                        className={
                          "text-[10px] font-bold uppercase " +
                          (v.active
                            ? "text-emerald-700 dark:text-emerald-300"
                            : "text-gray-400")
                        }
                      >
                        {v.active ? "✓ Active" : `Variant ${i + 1}`}
                      </span>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          removeVariant(i);
                        }}
                        className="text-gray-400 hover:text-red-500 text-xs"
                        title="Remove"
                      >
                        ×
                      </button>
                    </div>
                    <div className="text-xs dark:text-gray-200 whitespace-pre-wrap flex-1 overflow-hidden">
                      {v.text}
                    </div>
                    <div className="mt-1 flex items-center gap-1 text-[10px]">
                      <span
                        className={
                          "px-1 py-0.5 rounded font-semibold " +
                          (isGsm7
                            ? "bg-emerald-100 dark:bg-emerald-500/20 text-emerald-700 dark:text-emerald-300"
                            : "bg-amber-100 dark:bg-amber-500/20 text-amber-700 dark:text-amber-300")
                        }
                      >
                        {m.encoding === "gsm7" ? "GSM-7" : "UCS-2"}
                      </span>
                      <span className="text-gray-500">
                        {m.units}/{m.singleCap}, {m.segments}seg
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
            <p className="text-xs text-gray-400 mt-1">
              Generate 5 AI variants, then click one to activate it for sending.
              Use <code>{"{url}"}</code> placeholder. The active variant overrides
              the base text on sends.
            </p>
          </Field>

        </div>
      </div>
      {shareOpen && (
        <div className="fixed inset-0 z-[55] flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/40"
            onClick={() => setShareOpen(false)}
          />
          <div className="relative bg-white dark:bg-gray-900 rounded-lg shadow-2xl w-[600px] max-w-full flex flex-col">
            <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200 dark:border-gray-800">
              <h3 className="text-lg font-semibold dark:text-gray-100">
                Share variants list
              </h3>
              <button
                onClick={() => setShareOpen(false)}
                className="text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 cursor-pointer p-1"
              >
                ✕
              </button>
            </div>
            <div className="p-5 space-y-3">
              <textarea
                readOnly
                value={shareText}
                onFocus={(e) => e.currentTarget.select()}
                className={inputCls + " min-h-[200px] font-mono"}
              />
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Numbered list of all {aiVariants.length} variants — paste this into
                chat/email for moderation review.
              </p>
            </div>
            <div className="px-5 py-3 border-t border-gray-200 dark:border-gray-800 flex justify-end gap-2">
              <button
                onClick={() => setShareOpen(false)}
                className="px-4 py-1.5 rounded-md border border-gray-300 dark:border-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 text-sm cursor-pointer"
              >
                Close
              </button>
              <button
                onClick={async () => {
                  try {
                    await navigator.clipboard.writeText(shareText);
                    setShareCopied(true);
                    setTimeout(() => setShareCopied(false), 2000);
                  } catch {
                    /* clipboard might be unavailable in some contexts */
                  }
                }}
                className="px-4 py-1.5 rounded-md bg-purple-600 hover:bg-purple-700 text-white text-sm font-medium cursor-pointer"
              >
                {shareCopied ? "✓ Copied" : "Copy to clipboard"}
              </button>
            </div>
          </div>
        </div>
      )}
      {sendsOpen && currentCampaign?.id && (
        <CampaignSendsDrawer
          campaign={currentCampaign}
          onClose={() => setSendsOpen(false)}
        />
      )}
      {evalOpen && currentCampaign?.id && (
        <EvaluateDialog
          campaignId={currentCampaign.id}
          onClose={() => setEvalOpen(false)}
        />
      )}
      {phonesPickerOpen && currentCampaign?.id && (
        <PhonesPickerDialog
          campaignId={currentCampaign.id}
          countryIso2={countryIso2 ?? null}
          onClose={() => setPhonesPickerOpen(false)}
          onAdded={(msg) => {
            setPhonesPickerOpen(false);
            setBulkMsg(msg);
            reloadPhonesCount();
            setTimeout(() => setBulkMsg(null), 5000);
          }}
        />
      )}
    </div>
  );
}

type PhoneWithStats = {
  id: number;
  phone: string;
  name: string | null;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  last_sent_at: string | null;
  sends_count: number;
  clicks_count: number;
  last_click_at: string | null;
  leads_count: number;
  last_lead_at: string | null;
  deposits_count: number;
  last_deposit_at: string | null;
  opted_out: boolean;
  created_at: string;
  updated_at: string;
};

function PhonesPickerDialog({
  campaignId,
  countryIso2,
  onClose,
  onAdded,
}: {
  campaignId: number;
  countryIso2: string | null;
  onClose: () => void;
  onAdded: (msg: string) => void;
}) {
  const { data, loading } = useAsync<PhoneWithStats[]>(() =>
    fetch("/api/phones/with-stats").then(
      (r) => r.json() as Promise<PhoneWithStats[]>,
    ),
  );
  // Phones already assigned to THIS campaign — used to mark them in the grid.
  const { data: assigned } = useAsync<CampaignPhone[]>(
    () =>
      fetch(`/api/campaigns-phones?campaign_id=${campaignId}`).then(
        (r) => r.json() as Promise<CampaignPhone[]>,
      ),
    [campaignId],
  );
  const assignedMap = useMemo(() => {
    const m = new Map<number, string>(); // phone_id -> status
    for (const a of assigned ?? []) m.set(a.phone_id, a.status);
    return m;
  }, [assigned]);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [adding, setAdding] = useState(false);
  const [batchFilter, setBatchFilter] = useState<string>(""); // "" = all

  // Distinct import_batch values from the loaded set — sorted, blanks shown
  // as "(no batch)" so the user can pick rows that came in without a label.
  const batchOptions = useMemo(() => {
    const set = new Set<string>();
    for (const p of data ?? []) {
      const b = p.import_batch?.trim() ?? "";
      set.add(b);
    }
    const arr = [...set].sort((a, b) => a.localeCompare(b));
    return arr.map((b) => ({
      value: b,
      label: b === "" ? "— (no batch)" : b,
      searchKey: b || "no batch",
    }));
  }, [data]);

  // Filter phones by country prefix if campaign has one (best-effort match
  // using libphonenumber-js). Phones with hlr_status='bad' are always hidden
  // — they failed HLR verification and sending to them just burns money.
  // Also filter by import_batch when the user picks one in the Combobox.
  const filtered = useMemo(() => {
    let list = (data ?? []).filter((p) => p.hlr_status !== "bad");
    if (countryIso2) {
      list = list.filter((p) => {
        const parsed = parsePhoneNumberFromString("+" + p.phone);
        return parsed?.country === countryIso2;
      });
    }
    if (batchFilter !== "") {
      list = list.filter((p) => (p.import_batch?.trim() ?? "") === batchFilter);
    }
    return list;
  }, [data, countryIso2, batchFilter]);

  async function addSelected() {
    if (selectedIds.size === 0) return;
    setAdding(true);
    try {
      const res = await fetch(`/api/campaigns/${campaignId}/phones`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          phone_ids: [...selectedIds],
          status: "paused",
        }),
      });
      if (!res.ok) {
        onAdded(`Add phones failed: ${await res.text()}`);
        return;
      }
      const r = (await res.json()) as {
        inserted: number;
        updated: number;
        skipped_bad_hlr?: number;
      };
      const skipped = r.skipped_bad_hlr ?? 0;
      onAdded(
        `Added ${r.inserted} new + updated ${r.updated} existing as paused` +
          (skipped > 0 ? ` (skipped ${skipped} bad-HLR)` : ""),
      );
    } catch (e: any) {
      onAdded(String(e?.message ?? e));
    } finally {
      setAdding(false);
    }
  }

  return (
    <div className="fixed inset-0 z-[55] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white dark:bg-gray-900 rounded-lg shadow-2xl w-[95vw] h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200 dark:border-gray-800">
          <div>
            <h3 className="text-lg font-semibold dark:text-gray-100">
              Add Phones to Campaign
            </h3>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Selected phones will be added as <strong>paused</strong>. Click ▶ Start
              afterwards to begin sending.
              {countryIso2 && (
                <>
                  {" "}Filtered to country <strong>{countryIso2}</strong>.
                </>
              )}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex flex-col gap-0.5">
              <span className="text-[10px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                Batch
              </span>
              <Combobox
                value={batchFilter}
                onChange={setBatchFilter}
                placeholder="— all batches —"
                options={batchOptions}
              />
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 cursor-pointer p-1 self-end"
              title="Close"
            >
              ✕
            </button>
          </div>
        </div>
        <div className="flex-1 min-h-0">
          {loading && data === null ? (
            <div className="p-6 text-gray-400">Loading phones...</div>
          ) : (
            <AgTable<PhoneWithStats>
              persistKey="phones_picker"
              hideId
              selection="checkbox"
              onSelectionChange={(sel) =>
                setSelectedIds(new Set(sel.map((s) => s.id)))
              }
              rows={filtered}
              columns={[
                {
                  field: "phone",
                  headerName: "Phone",
                  // Force text filter — the phone field is a string of digits
                  // and ag-grid would otherwise sniff it as a number column,
                  // which doesn't accept partial / substring searches.
                  filter: "agTextColumnFilter",
                  filterParams: {
                    filterOptions: [
                      "contains",
                      "startsWith",
                      "endsWith",
                      "equals",
                    ],
                    defaultOption: "contains",
                  },
                  cellRenderer: (p: ICellRendererParams<PhoneWithStats>) => (
                    <span className="font-mono">{p.value}</span>
                  ),
                },
                {
                  headerName: "In campaign",
                  colId: "in_campaign",
                  width: 150,
                  flex: 0,
                  valueGetter: (p) =>
                    p.data ? (assignedMap.get(p.data.id) ?? "") : "",
                  cellRenderer: (p: ICellRendererParams<PhoneWithStats>) => {
                    const status = p.data ? assignedMap.get(p.data.id) : null;
                    if (!status) return <span className="text-gray-300">—</span>;
                    const cls =
                      status === "sent_provider"
                        ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300"
                        : status === "error" || status === "error_provider"
                          ? "bg-red-100 text-red-700 dark:bg-red-500/15 dark:text-red-300"
                          : status === "paused"
                            ? "bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-300"
                            : "bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300";
                    return (
                      <span className={`px-2 py-0.5 rounded text-xs font-semibold ${cls}`}>
                        ✓ {status}
                      </span>
                    );
                  },
                },
                {
                  headerName: "Name",
                  colId: "name",
                  valueGetter: (p) =>
                    [p.data?.first_name, p.data?.last_name]
                      .filter(Boolean)
                      .join(" ") ||
                    p.data?.name ||
                    "",
                },
                {
                  field: "sends_count",
                  headerName: "Total sends",
                  filter: "agNumberColumnFilter",
                  width: 130,
                  flex: 0,
                  cellRenderer: (p: ICellRendererParams<PhoneWithStats>) =>
                    p.value > 0 ? (
                      <span>{p.value}</span>
                    ) : (
                      <span className="text-gray-400">0</span>
                    ),
                },
                {
                  field: "last_sent_at",
                  headerName: "Last sent",
                  filter: "agDateColumnFilter",
                  valueFormatter: (p) =>
                    p.value ? new Date(p.value).toLocaleString() : "never",
                  cellRenderer: (p: ICellRendererParams<PhoneWithStats>) =>
                    p.value ? (
                      <span>{new Date(p.value).toLocaleString()}</span>
                    ) : (
                      <span className="text-gray-400">never</span>
                    ),
                  width: 170,
                  flex: 0,
                },
                {
                  field: "clicks_count",
                  headerName: "Clicks",
                  filter: "agNumberColumnFilter",
                  width: 100,
                  flex: 0,
                  cellRenderer: (p: ICellRendererParams<PhoneWithStats>) =>
                    p.value > 0 ? (
                      <span className="text-blue-700 dark:text-blue-400 font-medium">
                        {p.value}
                      </span>
                    ) : (
                      <span className="text-gray-400">0</span>
                    ),
                },
                {
                  field: "last_click_at",
                  headerName: "Last click",
                  filter: "agDateColumnFilter",
                  width: 170,
                  flex: 0,
                  valueFormatter: (p) =>
                    p.value ? new Date(p.value).toLocaleString() : "",
                  cellRenderer: (p: ICellRendererParams<PhoneWithStats>) =>
                    p.value ? (
                      <span>{new Date(p.value).toLocaleString()}</span>
                    ) : (
                      <span className="text-gray-400">—</span>
                    ),
                },
                {
                  field: "leads_count",
                  headerName: "Leads",
                  filter: "agNumberColumnFilter",
                  width: 90,
                  flex: 0,
                  cellRenderer: (p: ICellRendererParams<PhoneWithStats>) =>
                    p.value > 0 ? (
                      <span className="text-amber-700 dark:text-amber-400 font-medium">
                        {p.value}
                      </span>
                    ) : (
                      <span className="text-gray-400">0</span>
                    ),
                },
                {
                  field: "last_lead_at",
                  headerName: "Last lead",
                  filter: "agDateColumnFilter",
                  width: 170,
                  flex: 0,
                  valueFormatter: (p) =>
                    p.value ? new Date(p.value).toLocaleString() : "",
                  cellRenderer: (p: ICellRendererParams<PhoneWithStats>) =>
                    p.value ? (
                      <span>{new Date(p.value).toLocaleString()}</span>
                    ) : (
                      <span className="text-gray-400">—</span>
                    ),
                },
                {
                  field: "deposits_count",
                  headerName: "Deposits",
                  filter: "agNumberColumnFilter",
                  width: 110,
                  flex: 0,
                  cellRenderer: (p: ICellRendererParams<PhoneWithStats>) =>
                    p.value > 0 ? (
                      <span className="text-emerald-700 dark:text-emerald-400 font-medium">
                        {p.value}
                      </span>
                    ) : (
                      <span className="text-gray-400">0</span>
                    ),
                },
                {
                  field: "last_deposit_at",
                  headerName: "Last deposit",
                  filter: "agDateColumnFilter",
                  width: 170,
                  flex: 0,
                  valueFormatter: (p) =>
                    p.value ? new Date(p.value).toLocaleString() : "",
                  cellRenderer: (p: ICellRendererParams<PhoneWithStats>) =>
                    p.value ? (
                      <span>{new Date(p.value).toLocaleString()}</span>
                    ) : (
                      <span className="text-gray-400">—</span>
                    ),
                },
                {
                  field: "opted_out",
                  headerName: "Opt-out",
                  width: 110,
                  flex: 0,
                  filter: true,
                  cellRenderer: (p: ICellRendererParams<PhoneWithStats>) =>
                    p.value ? (
                      <span className="px-1.5 py-0.5 rounded text-xs font-medium bg-red-100 dark:bg-red-500/15 text-red-700 dark:text-red-300">
                        opted-out
                      </span>
                    ) : (
                      <span className="text-gray-400">—</span>
                    ),
                },
                {
                  field: "created_at",
                  headerName: "Added",
                  filter: "agDateColumnFilter",
                  valueFormatter: (p) =>
                    p.value ? new Date(p.value).toLocaleString() : "",
                  width: 170,
                  flex: 0,
                },
              ]}
            />
          )}
        </div>
        <div className="px-5 py-3 border-t border-gray-200 dark:border-gray-800 flex items-center justify-between">
          <div className="text-xs text-gray-500 dark:text-gray-400">
            <strong>{selectedIds.size}</strong> selected of{" "}
            <strong>{filtered.length}</strong> shown ({(data ?? []).length} total)
          </div>
          <div className="flex gap-2">
            <button
              onClick={onClose}
              className="px-4 py-1.5 rounded-md border border-gray-300 dark:border-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 text-sm cursor-pointer"
            >
              Cancel
            </button>
            <button
              onClick={addSelected}
              disabled={adding || selectedIds.size === 0}
              className="px-4 py-1.5 rounded-md bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-medium cursor-pointer"
            >
              {adding ? "Adding..." : `Add ${selectedIds.size} phones (paused)`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function AssignPhonesDrawer({
  campaign,
  onClose,
  onSaved,
}: {
  campaign: Campaign;
  onClose: () => void;
  onSaved: () => void;
}) {
  const phones = useAsync<Phone[]>(() =>
    fetch("/api/phones").then((r) => r.json() as Promise<Phone[]>),
  );
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState<"pending" | "ready" | "error">("pending");
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState<string | null>(null);

  const visible = (phones.data ?? []).filter((p) => {
    const q = search.toLowerCase();
    return (
      p.phone.toLowerCase().includes(q) ||
      (p.name ?? "").toLowerCase().includes(q)
    );
  });

  function toggle(id: number) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function toggleAll() {
    if (selected.size === visible.length && visible.length > 0) {
      setSelected(new Set());
    } else {
      setSelected(new Set(visible.map((p) => p.id)));
    }
  }

  async function save() {
    if (selected.size === 0) {
      setResult("Select at least one phone");
      return;
    }
    setSaving(true);
    const res = await fetch(`/api/campaigns/${campaign.id}/phones`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ phone_ids: Array.from(selected), status }),
    });
    setSaving(false);
    if (!res.ok) {
      setResult(await res.text());
      return;
    }
    const data = (await res.json()) as { inserted: number; updated: number };
    setResult(
      `Inserted: ${data.inserted}, updated: ${data.updated} (status → ${status}).`,
    );
    setSelected(new Set());
    // Don't auto-close — keep search/filters intact so user can assign more.
  }

  return (
    <Drawer
      title={`Assign Phones — ${campaign.name}`}
      open={true}
      onClose={onClose}
      onSave={save}
      saving={saving}
    >
      {result && (
        <div className="bg-blue-50 dark:bg-blue-500/10 border border-blue-200 dark:border-blue-500/30 text-blue-700 dark:text-blue-300 text-sm px-3 py-2 rounded">
          {result}
        </div>
      )}
      <Field label="Status to set">
        <select
          className={inputCls}
          value={status}
          onChange={(e) =>
            setStatus(e.target.value as "pending" | "ready" | "error")
          }
        >
          <option value="pending">pending — queue for sending</option>
          <option value="ready">ready — mark as sent</option>
          <option value="error">error</option>
        </select>
      </Field>
      <Field label="Search">
        <input
          className={inputCls}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Filter phones..."
        />
      </Field>
      <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
        <span>
          {selected.size} selected of {visible.length}
        </span>
        <button
          onClick={toggleAll}
          className="text-blue-600 dark:text-blue-400 hover:underline cursor-pointer"
        >
          {selected.size === visible.length && visible.length > 0
            ? "Clear all"
            : "Select all"}
        </button>
      </div>
      <div className="border border-gray-200 dark:border-gray-700 rounded-md max-h-[400px] overflow-auto divide-y divide-gray-100 dark:divide-gray-800">
        {phones.loading && (
          <div className="p-3 text-sm text-gray-400">Loading...</div>
        )}
        {!phones.loading && visible.length === 0 && (
          <div className="p-3 text-sm text-gray-400">No phones</div>
        )}
        {visible.map((p) => (
          <label
            key={p.id}
            className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800"
          >
            <input
              type="checkbox"
              checked={selected.has(p.id)}
              onChange={() => toggle(p.id)}
              className="accent-blue-600"
            />
            <span className="text-sm dark:text-gray-200">{p.phone}</span>
            {p.name && (
              <span className="text-xs text-gray-500 dark:text-gray-400">
                — {p.name}
              </span>
            )}
            <span className="ml-auto text-xs text-gray-400">#{p.id}</span>
          </label>
        ))}
      </div>
    </Drawer>
  );
}

// ---------- Statistic ----------

// ---------- Stage 1: Evaluator dialog ----------

type RouteEvaluation = {
  route_id: number | null;
  route_name: string | null;
  route_external_id: string | null;
  sent: number;
  delivered: number;
  failed: number;
  clicks: number;
  unique_clicks: number;
  leads: number;
  deposits: number;
  cost: number;
  ctr: number | null;
  cpc: number | null;
  cpl: number | null;
  fail_rate: number | null;
  delivery_rate: number | null;
  status:
    | "winner"
    | "backup"
    | "eligible"
    | "keep_testing"
    | "paused_low_ctr"
    | "paused_bad_delivery"
    | "paused_high_optout"
    | "waiting_manual_approval_no_leads"
    | "waiting_manual_approval_high_cpl";
  recommendation: string;
  reason: string;
  wait_complete: boolean;
};
type CampaignEvaluation = {
  campaign_id: number;
  campaign_name: string;
  country_iso2: string | null;
  evaluated_at: string;
  rules: Record<string, unknown>;
  per_route: RouteEvaluation[];
  totals: {
    sent: number;
    delivered: number;
    failed: number;
    clicks: number;
    leads: number;
    cost: number;
  };
};

const STATUS_TONE: Record<RouteEvaluation["status"], string> = {
  winner: "bg-emerald-100 text-emerald-800 dark:bg-emerald-500/20 dark:text-emerald-200",
  backup: "bg-blue-100 text-blue-800 dark:bg-blue-500/20 dark:text-blue-200",
  eligible: "bg-blue-50 text-blue-700 dark:bg-blue-500/10 dark:text-blue-300",
  keep_testing: "bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200",
  paused_low_ctr: "bg-amber-100 text-amber-800 dark:bg-amber-500/15 dark:text-amber-200",
  paused_bad_delivery: "bg-amber-100 text-amber-800 dark:bg-amber-500/15 dark:text-amber-200",
  paused_high_optout: "bg-amber-100 text-amber-800 dark:bg-amber-500/15 dark:text-amber-200",
  waiting_manual_approval_no_leads: "bg-red-100 text-red-800 dark:bg-red-500/20 dark:text-red-200",
  waiting_manual_approval_high_cpl: "bg-red-100 text-red-800 dark:bg-red-500/20 dark:text-red-200",
};

function EvaluateDialog({
  campaignId,
  onClose,
}: {
  campaignId: number;
  onClose: () => void;
}) {
  const { data, loading, reload } = useAsync<CampaignEvaluation & { error?: string }>(
    () =>
      fetch(`/api/campaigns/${campaignId}/evaluate`).then((r) => r.json()),
    [campaignId],
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white dark:bg-gray-900 rounded-lg shadow-xl w-[95vw] max-w-6xl max-h-[90vh] overflow-auto p-6">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h3 className="text-lg font-semibold dark:text-gray-100">
              Evaluator — Campaign #{campaignId}
            </h3>
            {data && !("error" in data && data.error) && (
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Country {data.country_iso2 ?? "—"} · evaluated{" "}
                {new Date(data.evaluated_at).toLocaleString()}
              </p>
            )}
          </div>
          <div className="flex gap-2">
            <button
              onClick={reload}
              disabled={loading}
              className="px-3 py-1.5 text-sm rounded border border-gray-300 dark:border-gray-600 dark:text-gray-200 disabled:opacity-50"
            >
              {loading ? "…" : "↻ Refresh"}
            </button>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 p-1"
            >
              <Icon name="x" />
            </button>
          </div>
        </div>

        {loading && !data ? (
          <div className="p-6 text-gray-400">Loading evaluation…</div>
        ) : (data as any)?.error ? (
          <div className="p-3 text-sm text-red-600 bg-red-50 dark:bg-red-500/10 rounded">
            {(data as any).error}
          </div>
        ) : data ? (
          <>
            {/* Totals */}
            <div className="grid grid-cols-6 gap-2 mb-4 text-xs">
              <Stat label="Sent" value={data.totals.sent} />
              <Stat label="Delivered" value={data.totals.delivered} />
              <Stat label="Failed" value={data.totals.failed} tone="amber" />
              <Stat label="Clicks" value={data.totals.clicks} tone="blue" />
              <Stat label="Leads" value={data.totals.leads} tone="emerald" />
              <Stat label="Cost" value={`$${data.totals.cost.toFixed(2)}`} />
            </div>

            {/* Effective rules */}
            <details className="mb-4 text-xs">
              <summary className="cursor-pointer text-gray-600 dark:text-gray-400">
                Effective rules (country + campaign overrides)
              </summary>
              <pre className="mt-2 p-2 bg-gray-50 dark:bg-gray-800 rounded text-[11px] overflow-auto">
                {JSON.stringify(data.rules, null, 2)}
              </pre>
            </details>

            {/* Per-route table */}
            <table className="w-full text-xs">
              <thead className="bg-gray-50 dark:bg-gray-800 text-left">
                <tr>
                  <th className="px-2 py-1.5 font-medium dark:text-gray-300">Route</th>
                  <th className="px-2 py-1.5 font-medium dark:text-gray-300">Sent</th>
                  <th className="px-2 py-1.5 font-medium dark:text-gray-300">Deliv.</th>
                  <th className="px-2 py-1.5 font-medium dark:text-gray-300">Fail</th>
                  <th className="px-2 py-1.5 font-medium dark:text-gray-300">Clicks</th>
                  <th className="px-2 py-1.5 font-medium dark:text-gray-300">Leads</th>
                  <th className="px-2 py-1.5 font-medium dark:text-gray-300">Cost</th>
                  <th className="px-2 py-1.5 font-medium dark:text-gray-300">CTR</th>
                  <th className="px-2 py-1.5 font-medium dark:text-gray-300">CPC</th>
                  <th className="px-2 py-1.5 font-medium dark:text-gray-300">CPL</th>
                  <th className="px-2 py-1.5 font-medium dark:text-gray-300">Status</th>
                  <th className="px-2 py-1.5 font-medium dark:text-gray-300">Reason</th>
                </tr>
              </thead>
              <tbody>
                {data.per_route.map((r) => (
                  <tr
                    key={r.route_id ?? "default"}
                    className="border-t border-gray-100 dark:border-gray-800"
                  >
                    <td className="px-2 py-1.5 dark:text-gray-200">
                      {r.route_name ? (
                        <span>
                          <span className="font-medium">{r.route_name}</span>
                          {r.route_external_id && (
                            <span className="ml-1 text-gray-400 font-mono">
                              #{r.route_external_id}
                            </span>
                          )}
                        </span>
                      ) : (
                        <span className="text-gray-400 uppercase text-[10px] font-semibold">default</span>
                      )}
                    </td>
                    <td className="px-2 py-1.5 dark:text-gray-300">{r.sent}</td>
                    <td className="px-2 py-1.5 dark:text-gray-300">{r.delivered}</td>
                    <td className="px-2 py-1.5 dark:text-gray-300">{r.failed}</td>
                    <td className="px-2 py-1.5 dark:text-gray-300">{r.clicks}</td>
                    <td className="px-2 py-1.5 dark:text-gray-300">{r.leads}</td>
                    <td className="px-2 py-1.5 dark:text-gray-300">${r.cost.toFixed(2)}</td>
                    <td className="px-2 py-1.5 dark:text-gray-300">{fmtPct(r.ctr)}</td>
                    <td className="px-2 py-1.5 dark:text-gray-300">
                      {r.cpc == null ? "—" : `$${r.cpc.toFixed(3)}`}
                    </td>
                    <td className="px-2 py-1.5 dark:text-gray-300">
                      {r.cpl == null ? "—" : `$${r.cpl.toFixed(2)}`}
                    </td>
                    <td className="px-2 py-1.5">
                      <span
                        className={`inline-block px-2 py-0.5 rounded text-[10px] font-semibold uppercase ${STATUS_TONE[r.status]}`}
                      >
                        {r.status.replace(/_/g, " ")}
                      </span>
                    </td>
                    <td className="px-2 py-1.5 text-gray-600 dark:text-gray-400 max-w-[28ch]">
                      {r.reason}
                    </td>
                  </tr>
                ))}
                {data.per_route.length === 0 && (
                  <tr>
                    <td colSpan={12} className="px-2 py-4 text-center text-gray-400">
                      No sends recorded for this campaign yet.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </>
        ) : null}
      </div>
    </div>
  );
}

function Stat({
  label,
  value,
  tone = "gray",
}: {
  label: string;
  value: number | string;
  tone?: "gray" | "blue" | "amber" | "emerald";
}) {
  const cls =
    tone === "blue"
      ? "bg-blue-50 dark:bg-blue-500/10 text-blue-700 dark:text-blue-300"
      : tone === "amber"
        ? "bg-amber-50 dark:bg-amber-500/10 text-amber-700 dark:text-amber-300"
        : tone === "emerald"
          ? "bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-300"
          : "bg-gray-50 dark:bg-gray-700/50 text-gray-700 dark:text-gray-200";
  return (
    <div className={`rounded p-2 ${cls}`}>
      <div className="opacity-70 uppercase tracking-wide text-[10px]">{label}</div>
      <div className="font-bold text-base">{value}</div>
    </div>
  );
}

function fmtPct(v: number | null): string {
  if (v == null) return "—";
  return `${(v * 100).toFixed(1)}%`;
}

function CampaignSendsDrawer({
  campaign,
  onClose,
}: {
  campaign: Campaign;
  onClose: () => void;
}) {
  const { data, loading, reload } = useAsync<CampaignPhone[]>(
    () =>
      fetch(`/api/campaigns-phones?campaign_id=${campaign.id}`).then(
        (r) => r.json() as Promise<CampaignPhone[]>,
      ),
    [campaign.id],
  );

  // Auto-refresh every 1 min while drawer is open. Long interval on purpose —
  // each reload re-creates the columns array in the parent so ag-grid rebuilds
  // colDefs; persisted state is re-applied via the useEffect in AgTable but
  // frequent rebuilds still feel choppy to the user. Hit the Drawer's Close +
  // re-open for an instant manual refresh.
  useEffect(() => {
    const t = setInterval(reload, 60_000);
    return () => clearInterval(t);
  }, [reload]);

  const counts = useMemo(() => {
    const rows = data ?? [];
    return {
      total: rows.length,
      pending: rows.filter((r) => r.status === "pending").length,
      ready: rows.filter((r) => r.status === "ready").length,
      sent_provider: rows.filter((r) => r.status === "sent_provider").length,
      error: rows.filter((r) => r.status === "error").length,
      error_provider: rows.filter((r) => r.status === "error_provider").length,
    };
  }, [data]);

  return (
    <Drawer
      title={`Sends — ${campaign.name}`}
      open={true}
      onClose={onClose}
      onSave={onClose}
      saving={false}
      width={1700}
      showSave={false}
    >
      <div className="flex flex-wrap gap-2 text-xs">
        <span className="px-2 py-1 rounded bg-gray-100 dark:bg-gray-800 dark:text-gray-200">
          Total: <span className="font-semibold">{counts.total}</span>
        </span>
        <span className="px-2 py-1 rounded bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200">
          Pending: {counts.pending}
        </span>
        <span className="px-2 py-1 rounded bg-amber-100 dark:bg-amber-500/15 text-amber-700 dark:text-amber-300">
          Ready: {counts.ready}
        </span>
        <span className="px-2 py-1 rounded bg-emerald-100 dark:bg-emerald-500/15 text-emerald-700 dark:text-emerald-300">
          Delivered: {counts.sent_provider}
        </span>
        <span className="px-2 py-1 rounded bg-red-100 dark:bg-red-500/15 text-red-700 dark:text-red-300">
          Error: {counts.error}
        </span>
        <span className="px-2 py-1 rounded bg-red-200 dark:bg-red-500/25 text-red-800 dark:text-red-200">
          Provider err: {counts.error_provider}
        </span>
      </div>
      <div className="h-[calc(100vh-220px)] min-h-[400px]">
        {loading && data === null ? (
          <div className="p-6 text-gray-400">Loading...</div>
        ) : (
          <AgTable<CampaignPhone>
            persistKey={`campaign_sends_${campaign.id}`}
            rows={data ?? []}
            columns={[
              {
                field: "phone",
                headerName: "Phone",
                cellRenderer: (p: ICellRendererParams<CampaignPhone>) => (
                  <span className="font-medium">{p.value}</span>
                ),
              },
              {
                field: "status",
                headerName: "Status",
                width: 120,
                flex: 0,
                cellRenderer: (p: ICellRendererParams<CampaignPhone>) => {
                  const status = p.value as CampaignPhone["status"] | undefined;
                  if (!status) return null;
                  const cls = {
                    pending:
                      "bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200",
                    ready:
                      "bg-amber-100 dark:bg-amber-500/15 text-amber-700 dark:text-amber-300",
                    sent_provider:
                      "bg-emerald-100 dark:bg-emerald-500/15 text-emerald-700 dark:text-emerald-300",
                    error:
                      "bg-red-100 dark:bg-red-500/15 text-red-700 dark:text-red-300",
                    error_provider:
                      "bg-red-200 dark:bg-red-500/25 text-red-800 dark:text-red-200",
                  }[status];
                  return (
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${cls}`}>
                      {status}
                    </span>
                  );
                },
              },
              { field: "slug", headerName: "Slug", width: 130, flex: 0 },
              {
                headerName: "Route",
                colId: "route",
                width: 180,
                flex: 0,
                valueGetter: (p) => p.data?.route_name ?? null,
                cellRenderer: (p: ICellRendererParams<CampaignPhone>) => {
                  if (!p.data?.route_name) {
                    return (
                      <span
                        className="px-2 py-0.5 rounded bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 text-xs font-medium uppercase tracking-wide"
                        title="Provider-managed routing"
                      >
                        default
                      </span>
                    );
                  }
                  return (
                    <span
                      className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-700 text-xs"
                      title={`route_id=${p.data.route_id ?? ""}`}
                    >
                      <span>{p.data.route_name}</span>
                      {p.data.route_external_id && (
                        <span className="text-gray-500 font-mono">
                          #{p.data.route_external_id}
                        </span>
                      )}
                    </span>
                  );
                },
              },
              {
                field: "error_message",
                headerName: "Error",
                valueFormatter: (p) => p.value ?? "",
              },
              {
                field: "sent_at",
                headerName: "Sent",
                filter: "agDateColumnFilter",
                valueFormatter: (p) =>
                  p.value ? new Date(p.value).toLocaleString() : "",
              },
              {
                field: "created_at",
                headerName: "Created",
                filter: "agDateColumnFilter",
                valueFormatter: (p) =>
                  p.value ? new Date(p.value).toLocaleString() : "",
              },
            ]}
          />
        )}
      </div>
    </Drawer>
  );
}

// ---------- CSV Import ----------

const PHONE_LIKE = /^[+\d][\d\s()\-]{4,}$/;

function parseCsv(text: string): string[][] {
  // strip BOM
  if (text.charCodeAt(0) === 0xfeff) text = text.slice(1);
  // detect separator from first non-empty line
  const firstLine = text.split(/\r?\n/).find((l) => l.trim().length > 0) ?? "";
  const counts = { ",": 0, ";": 0, "\t": 0, "|": 0 };
  for (const ch of firstLine) if (ch in counts) (counts as any)[ch]++;
  const sep =
    (Object.entries(counts).sort((a, b) => b[1] - a[1])[0]?.[0] as string) || ",";

  const rows: string[][] = [];
  let row: string[] = [];
  let cell = "";
  let inQuotes = false;
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (inQuotes) {
      if (ch === '"') {
        if (text[i + 1] === '"') {
          cell += '"';
          i++;
        } else inQuotes = false;
      } else cell += ch;
    } else if (ch === '"') {
      inQuotes = true;
    } else if (ch === sep) {
      row.push(cell);
      cell = "";
    } else if (ch === "\n" || ch === "\r") {
      if (ch === "\r" && text[i + 1] === "\n") i++;
      row.push(cell);
      cell = "";
      if (row.some((c) => c.trim() !== "")) rows.push(row);
      row = [];
    } else {
      cell += ch;
    }
  }
  if (cell !== "" || row.length > 0) {
    row.push(cell);
    if (row.some((c) => c.trim() !== "")) rows.push(row);
  }
  return rows;
}

function looksLikePhone(s: string): boolean {
  return PHONE_LIKE.test(s.trim());
}

function detectColumns(rows: string[][]): {
  hasHeader: boolean;
  phoneIdx: number;
  nameIdx: number | null;
  firstNameIdx: number | null;
  lastNameIdx: number | null;
  emailIdx: number | null;
} {
  if (rows.length === 0)
    return {
      hasHeader: false,
      phoneIdx: 0,
      nameIdx: null,
      firstNameIdx: null,
      lastNameIdx: null,
      emailIdx: null,
    };
  const cols = rows[0]!.length;

  // count phone-like values per column
  const scores: number[] = Array(cols).fill(0);
  for (let i = 0; i < Math.min(rows.length, 200); i++) {
    const r = rows[i]!;
    for (let c = 0; c < cols; c++) {
      if (looksLikePhone(r[c] ?? "")) scores[c]!++;
    }
  }
  let phoneIdx = 0;
  for (let c = 1; c < cols; c++) if (scores[c]! > scores[phoneIdx]!) phoneIdx = c;

  const hasHeader =
    !looksLikePhone(rows[0]?.[phoneIdx] ?? "") &&
    rows.slice(1, 5).some((r) => looksLikePhone(r[phoneIdx] ?? ""));

  // detect by header names if available
  let firstNameIdx: number | null = null;
  let lastNameIdx: number | null = null;
  let nameIdx: number | null = null;
  let emailIdx: number | null = null;
  if (hasHeader) {
    const header = rows[0]!.map((h) => h.trim().toLowerCase());
    const find = (preds: ((s: string) => boolean)[]) => {
      for (const pred of preds) {
        const idx = header.findIndex((h, i) => i !== phoneIdx && pred(h));
        if (idx !== -1) return idx;
      }
      return null;
    };
    firstNameIdx = find([
      (h) => h === "first_name" || h === "firstname" || h === "first name",
      (h) => h === "fname" || h === "given_name" || h === "given name",
    ]);
    lastNameIdx = find([
      (h) => h === "last_name" || h === "lastname" || h === "last name",
      (h) =>
        h === "lname" ||
        h === "surname" ||
        h === "family_name" ||
        h === "family name",
    ]);
    nameIdx = find([
      (h) =>
        h === "name" || h === "full_name" || h === "full name" || h === "fullname",
    ]);
    emailIdx = find([
      (h) => h === "email" || h === "e-mail" || h === "mail" || h === "email_address",
    ]);
  }

  // fallback: first non-phone column with values → name (if nothing detected)
  if (firstNameIdx === null && lastNameIdx === null && nameIdx === null) {
    for (let c = 0; c < cols; c++) {
      if (c === phoneIdx) continue;
      const sample = rows.slice(hasHeader ? 1 : 0, hasHeader ? 21 : 20);
      const nonEmpty = sample.filter((r) => (r[c] ?? "").trim() !== "").length;
      if (nonEmpty >= Math.min(3, sample.length)) {
        nameIdx = c;
        break;
      }
    }
  }

  return { hasHeader, phoneIdx, nameIdx, firstNameIdx, lastNameIdx, emailIdx };
}

function ImportPhonesCsvDrawer({
  onClose,
  onImported,
}: {
  onClose: () => void;
  onImported: () => void;
}) {
  const [rows, setRows] = useState<string[][]>([]);
  const [hasHeader, setHasHeader] = useState(false);
  const [phoneIdx, setPhoneIdx] = useState(0);
  const [nameIdx, setNameIdx] = useState<number | "">("");
  const [firstNameIdx, setFirstNameIdx] = useState<number | "">("");
  const [lastNameIdx, setLastNameIdx] = useState<number | "">("");
  const [emailIdx, setEmailIdx] = useState<number | "">("");
  const [showInvalidOnly, setShowInvalidOnly] = useState(false);
  const [filename, setFilename] = useState<string | null>(null);
  // Pre-fill a timestamped default. User can overwrite with anything like
  // "crm-uk-june" — label is stored on every imported phone for later filter.
  const [importBatch, setImportBatch] = useState(() => {
    const d = new Date();
    const pad = (n: number) => String(n).padStart(2, "0");
    return `import-${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}-${pad(d.getHours())}${pad(d.getMinutes())}`;
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<string | null>(null);

  async function handleFile(file: File) {
    setError(null);
    setResult(null);
    setFilename(file.name);
    const text = await file.text();
    const parsed = parseCsv(text);
    if (parsed.length === 0) {
      setError("CSV is empty");
      setRows([]);
      return;
    }
    const det = detectColumns(parsed);
    setRows(parsed);
    setHasHeader(det.hasHeader);
    setPhoneIdx(det.phoneIdx);
    setNameIdx(det.nameIdx ?? "");
    setFirstNameIdx(det.firstNameIdx ?? "");
    setLastNameIdx(det.lastNameIdx ?? "");
    setEmailIdx(det.emailIdx ?? "");
  }

  const dataRows = hasHeader ? rows.slice(1) : rows;
  const items = useMemo(
    () =>
      dataRows.map((r) => {
        const raw = (r[phoneIdx] ?? "").trim();
        const normalized = normalizePhone(raw);
        const isMasked = normalized && /0{6,}$/.test(normalized);
        // Reject masked placeholders (≥6 trailing zeros) — anonymised
        // CRM exports that never reach real recipients.
        const phone = isMasked ? null : normalized;
        const invalidReason = !normalized
          ? "unparseable"
          : isMasked
            ? "masked (6+ zeros)"
            : null;
        return {
          raw,
          phone,
          invalidReason,
          name: nameIdx === "" ? null : (r[nameIdx] ?? "").trim() || null,
          first_name:
            firstNameIdx === "" ? null : (r[firstNameIdx] ?? "").trim() || null,
          last_name:
            lastNameIdx === "" ? null : (r[lastNameIdx] ?? "").trim() || null,
          email:
            emailIdx === "" ? null : (r[emailIdx] ?? "").trim() || null,
        };
      }),
    [dataRows, phoneIdx, nameIdx, firstNameIdx, lastNameIdx, emailIdx],
  );
  const validCount = items.filter((i) => i.phone).length;
  const invalidCount = items.length - validCount;

  const cols = rows[0]?.length ?? 0;

  async function save() {
    setError(null);
    setResult(null);
    if (items.length === 0) {
      setError("Nothing to import");
      return;
    }
    setSaving(true);
    const payload = items
      .filter((it) => it.phone)
      .map((it) => ({
        phone: it.phone as string,
        name: it.name,
        first_name: it.first_name,
        last_name: it.last_name,
        email: it.email,
      }));
    const res = await fetch("/api/phones/bulk", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({
        items: payload,
        import_batch: importBatch.trim() || null,
      }),
    });
    setSaving(false);
    if (!res.ok) {
      setError(await res.text());
      return;
    }
    const data = (await res.json()) as {
      inserted: number;
      updated: number;
      skipped: number;
    };
    setResult(
      `Inserted: ${data.inserted}, updated: ${data.updated}, skipped: ${data.skipped}`,
    );
    setTimeout(onImported, 1200);
  }

  return (
    <Drawer
      title="Import Phones from CSV"
      open={true}
      onClose={onClose}
      onSave={save}
      saving={saving}
      saveLabel="Import"
    >
      {error && (
        <div className="bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/30 text-red-700 dark:text-red-300 text-sm px-3 py-2 rounded">
          {error}
        </div>
      )}
      {result && (
        <div className="bg-emerald-50 dark:bg-emerald-500/10 border border-emerald-200 dark:border-emerald-500/30 text-emerald-700 dark:text-emerald-300 text-sm px-3 py-2 rounded">
          {result}
        </div>
      )}

      <Field label="Batch label">
        <input
          className={inputCls}
          value={importBatch}
          onChange={(e) => setImportBatch(e.target.value)}
          placeholder="crm-uk-june, vendor-list-1, etc."
        />
        <p className="text-xs text-gray-400 mt-1">
          Stored on every row to filter/group later. Auto-filled with a
          timestamp — change to something memorable if you want.
        </p>
      </Field>
      <Field label="CSV File" required>
        <input
          type="file"
          accept=".csv,text/csv,text/plain"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) handleFile(f);
          }}
          className="block w-full text-sm text-gray-600 dark:text-gray-300 file:mr-3 file:px-3 file:py-1.5 file:rounded-md file:border file:border-gray-300 dark:file:border-gray-700 file:bg-white dark:file:bg-gray-800 file:text-gray-700 dark:file:text-gray-200 file:cursor-pointer file:text-sm cursor-pointer"
        />
        {filename && (
          <span className="block mt-1 text-xs text-gray-500 dark:text-gray-400">
            {filename} — {rows.length} rows
          </span>
        )}
      </Field>

      {rows.length > 0 && (
        <>
          <label className="flex items-center gap-2 text-sm dark:text-gray-200">
            <input
              type="checkbox"
              checked={hasHeader}
              onChange={(e) => setHasHeader(e.target.checked)}
              className="accent-blue-600"
            />
            First row is a header
          </label>

          <Field label="Phone column" required>
            <select
              className={inputCls}
              value={phoneIdx}
              onChange={(e) => setPhoneIdx(Number(e.target.value))}
            >
              {Array.from({ length: cols }).map((_, i) => (
                <option key={i} value={i}>
                  Column {i + 1}
                  {hasHeader && rows[0]?.[i] ? ` — "${rows[0]![i]}"` : ""}
                </option>
              ))}
            </select>
          </Field>

          <Field label="First Name column">
            <select
              className={inputCls}
              value={firstNameIdx}
              onChange={(e) =>
                setFirstNameIdx(
                  e.target.value === "" ? "" : Number(e.target.value),
                )
              }
            >
              <option value="">— none —</option>
              {Array.from({ length: cols }).map((_, i) => (
                <option key={i} value={i}>
                  Column {i + 1}
                  {hasHeader && rows[0]?.[i] ? ` — "${rows[0]![i]}"` : ""}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Last Name column">
            <select
              className={inputCls}
              value={lastNameIdx}
              onChange={(e) =>
                setLastNameIdx(
                  e.target.value === "" ? "" : Number(e.target.value),
                )
              }
            >
              <option value="">— none —</option>
              {Array.from({ length: cols }).map((_, i) => (
                <option key={i} value={i}>
                  Column {i + 1}
                  {hasHeader && rows[0]?.[i] ? ` — "${rows[0]![i]}"` : ""}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Name column (display)">
            <select
              className={inputCls}
              value={nameIdx}
              onChange={(e) =>
                setNameIdx(e.target.value === "" ? "" : Number(e.target.value))
              }
            >
              <option value="">— none —</option>
              {Array.from({ length: cols }).map((_, i) => (
                <option key={i} value={i}>
                  Column {i + 1}
                  {hasHeader && rows[0]?.[i] ? ` — "${rows[0]![i]}"` : ""}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Email column">
            <select
              className={inputCls}
              value={emailIdx}
              onChange={(e) =>
                setEmailIdx(e.target.value === "" ? "" : Number(e.target.value))
              }
            >
              <option value="">— none —</option>
              {Array.from({ length: cols }).map((_, i) => (
                <option key={i} value={i}>
                  Column {i + 1}
                  {hasHeader && rows[0]?.[i] ? ` — "${rows[0]![i]}"` : ""}
                </option>
              ))}
            </select>
          </Field>

          <div className="text-xs text-gray-500 dark:text-gray-400 flex gap-3 items-center">
            <span>Total: {items.length}</span>
            <span className="text-emerald-600 dark:text-emerald-400">
              Valid: {validCount}
            </span>
            {invalidCount > 0 && (
              <>
                <span className="text-red-600 dark:text-red-400">
                  Invalid: {invalidCount}
                </span>
                <label className="flex items-center gap-1 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={showInvalidOnly}
                    onChange={(e) => setShowInvalidOnly(e.target.checked)}
                  />
                  <span>Show invalid only</span>
                </label>
              </>
            )}
          </div>

          <div className="border border-gray-200 dark:border-gray-700 rounded-md overflow-auto max-h-[300px]">
            <table className="w-full text-xs">
              <thead className="bg-gray-50 dark:bg-gray-800 sticky top-0">
                <tr>
                  <th className="px-2 py-1 text-left font-medium dark:text-gray-300">Phone</th>
                  <th className="px-2 py-1 text-left font-medium dark:text-gray-300">First</th>
                  <th className="px-2 py-1 text-left font-medium dark:text-gray-300">Last</th>
                  <th className="px-2 py-1 text-left font-medium dark:text-gray-300">Email</th>
                  <th className="px-2 py-1 text-left font-medium dark:text-gray-300">Name</th>
                </tr>
              </thead>
              <tbody>
                {(showInvalidOnly
                  ? items.filter((it) => !it.phone)
                  : [...items].sort((a, b) => Number(!!a.phone) - Number(!!b.phone))
                )
                  .slice(0, 50)
                  .map((it, i) => (
                  <tr
                    key={i}
                    className="border-t border-gray-100 dark:border-gray-800"
                  >
                    <td
                      className={
                        "px-2 py-1 " +
                        (it.phone
                          ? "dark:text-gray-200"
                          : "text-red-600 dark:text-red-400")
                      }
                      title={it.raw !== it.phone ? `raw: ${it.raw}` : undefined}
                    >
                      {it.phone ?? (
                        <span>
                          <span className="inline-block px-1.5 py-0.5 rounded bg-red-100 dark:bg-red-500/20 text-red-700 dark:text-red-300 text-[10px] font-semibold uppercase mr-1.5">
                            invalid
                          </span>
                          {it.invalidReason && (
                            <span className="text-amber-700 dark:text-amber-400 mr-1.5">
                              {it.invalidReason}
                            </span>
                          )}
                          <span className="text-gray-400">— {it.raw}</span>
                        </span>
                      )}
                    </td>
                    <td className="px-2 py-1 text-gray-600 dark:text-gray-400">
                      {it.first_name ?? ""}
                    </td>
                    <td className="px-2 py-1 text-gray-600 dark:text-gray-400">
                      {it.last_name ?? ""}
                    </td>
                    <td className="px-2 py-1 text-gray-600 dark:text-gray-400">
                      {it.email ?? ""}
                    </td>
                    <td className="px-2 py-1 text-gray-600 dark:text-gray-400">
                      {it.name ?? ""}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {items.length > 50 && (
              <div className="px-2 py-1 text-xs text-gray-400 border-t border-gray-100 dark:border-gray-800">
                ... and {items.length - 50} more rows
              </div>
            )}
          </div>
        </>
      )}
    </Drawer>
  );
}

// ---------- Domains ----------

function DomainsTab() {
  const [decision, setDecision] = useState<"" | "BUY_CANDIDATE" | "WATCHLIST" | "REJECT">(
    () => (localStorage.getItem("domains.decision") as any) ?? "",
  );
  const [listingType, setListingType] = useState(
    () => localStorage.getItem("domains.listing_type") ?? "",
  );
  const [search, setSearch] = useState(
    () => localStorage.getItem("domains.search") ?? "",
  );
  useEffect(() => {
    localStorage.setItem("domains.decision", decision);
  }, [decision]);
  useEffect(() => {
    localStorage.setItem("domains.listing_type", listingType);
  }, [listingType]);
  useEffect(() => {
    localStorage.setItem("domains.search", search);
  }, [search]);
  const [running, setRunning] = useState(false);
  const [runMessage, setRunMessage] = useState<string | null>(null);
  const [checking, setChecking] = useState(false);
  const [discovering, setDiscovering] = useState(false);
  const [viewing, setViewing] = useState<Domain | null>(null);
  const [deleting, setDeleting] = useState<Domain | null>(null);
  const [filterResetKey, setFilterResetKey] = useState(0);

  const { data, loading, reload } = useAsync<Domain[]>(() => {
    const qs = new URLSearchParams();
    if (decision) qs.set("decision", decision);
    if (listingType) qs.set("listing_type", listingType);
    if (search.trim()) qs.set("search", search.trim());
    qs.set("limit", "1000");
    return fetch(`/api/domains?${qs.toString()}`).then(
      (r) => r.json() as Promise<Domain[]>,
    );
  }, [decision, listingType, search]);

  async function runDaily() {
    setRunning(true);
    setRunMessage(null);
    try {
      const res = await fetch("/api/domains/run-daily", { method: "POST" });
      const summary = await res.json();
      setRunMessage(JSON.stringify(summary));
      reload();
    } catch (e: any) {
      setRunMessage(`Failed: ${e?.message ?? e}`);
    } finally {
      setRunning(false);
    }
  }

  return (
    <PageShell
      title="Domains"
      total={data?.length ?? 0}
      onAdd={() => setChecking(true)}
      addLabel="Check domain"
      extraHeader={
        <>
          <select
            value={decision}
            onChange={(e) => setDecision(e.target.value as any)}
            className="px-2 py-1.5 rounded-md border border-gray-300 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200 text-sm cursor-pointer"
          >
            <option value="">All decisions</option>
            <option value="BUY_CANDIDATE">Buy candidates</option>
            <option value="WATCHLIST">Watchlist</option>
            <option value="REJECT">Rejected</option>
          </select>
          <select
            value={listingType}
            onChange={(e) => setListingType(e.target.value)}
            className="px-2 py-1.5 rounded-md border border-gray-300 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200 text-sm cursor-pointer"
          >
            <option value="">All types</option>
            <option value="closeout">Closeout</option>
            <option value="auction">Auction</option>
            <option value="backorder">Backorder</option>
            <option value="buy">Buy</option>
            <option value="register">Register</option>
            <option value="premium">Premium</option>
          </select>
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search domain..."
            className="px-3 py-1.5 rounded-md border border-gray-300 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200 text-sm w-48"
          />
          <button
            onClick={runDaily}
            disabled={running}
            className="px-3 py-1.5 rounded-md border border-gray-300 dark:border-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 text-sm cursor-pointer disabled:opacity-50"
          >
            {running ? "Running..." : "Run daily"}
          </button>
          <button
            onClick={() => setDiscovering(true)}
            className="px-3 py-1.5 rounded-md border border-gray-300 dark:border-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 text-sm cursor-pointer"
          >
            Discover Dynadot
          </button>
          <button
            onClick={reload}
            disabled={loading}
            className="px-3 py-1.5 rounded-md border border-gray-300 dark:border-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 text-sm cursor-pointer disabled:opacity-50"
            title="Reload data from server"
          >
            {loading ? "..." : "↻ Refresh"}
          </button>
          <ResetFiltersButton onClick={() => setFilterResetKey((n) => n + 1)} />
        </>
      }
    >
      {runMessage && (
        <div className="px-5 py-2 bg-blue-50 dark:bg-blue-500/10 border-b border-blue-200 dark:border-blue-500/30 text-xs text-blue-700 dark:text-blue-300 font-mono">
          {runMessage}
        </div>
      )}
      {loading && data === null ? (
        <div className="p-6 text-gray-400">Loading...</div>
      ) : (
        <AgTable<Domain>
          persistKey="domains"
          filterResetKey={filterResetKey}
          rows={data ?? []}
          columns={[
            {
              field: "domain",
              headerName: "Domain",
              cellRenderer: (p: ICellRendererParams<Domain>) => (
                <span className="font-medium font-mono">{p.value}</span>
              ),
            },
            {
              field: "decision",
              headerName: "Decision",
              cellRenderer: (p: ICellRendererParams<Domain>) => {
                const v = p.value;
                if (!v) return <span className="text-gray-400">—</span>;
                const cls =
                  v === "BUY_CANDIDATE"
                    ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300"
                    : v === "WATCHLIST"
                      ? "bg-yellow-100 text-yellow-700 dark:bg-yellow-500/15 dark:text-yellow-300"
                      : "bg-red-100 text-red-700 dark:bg-red-500/15 dark:text-red-300";
                return (
                  <span className={`px-2 py-0.5 rounded text-xs font-semibold ${cls}`}>
                    {v}
                  </span>
                );
              },
            },
            { field: "last_score", headerName: "Score", filter: "agNumberColumnFilter", width: 100, flex: 0 },
            { field: "status", headerName: "Status" },
            {
              field: "cloudflare_zone_status",
              headerName: "Short",
              width: 95,
              flex: 0,
              cellRenderer: (p: ICellRendererParams<Domain>) => {
                const v = p.value as Domain["cloudflare_zone_status"];
                if (!v) return <span className="text-gray-400">—</span>;
                const cls =
                  v === "active"
                    ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300"
                    : v === "pending" || v === "initializing"
                      ? "bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300"
                      : "bg-red-100 text-red-700 dark:bg-red-500/15 dark:text-red-300";
                return (
                  <span className={`px-2 py-0.5 rounded text-xs font-semibold ${cls}`}>
                    {v}
                  </span>
                );
              },
            },
            {
              field: "purchase_status",
              headerName: "Purchase",
              width: 110,
              flex: 0,
              cellRenderer: (p: ICellRendererParams<Domain>) => {
                const v = p.value as Domain["purchase_status"];
                if (!v || v === "watching") return <span className="text-gray-400">—</span>;
                const cls =
                  v === "armed"
                    ? "bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300"
                    : v === "winning" || v === "won" || v === "bought"
                      ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300"
                      : v === "lost" || v === "failed"
                        ? "bg-red-100 text-red-700 dark:bg-red-500/15 dark:text-red-300"
                        : "bg-blue-100 text-blue-700 dark:bg-blue-500/15 dark:text-blue-300";
                return (
                  <span className={`px-2 py-0.5 rounded text-xs font-semibold ${cls}`}>
                    {v}
                  </span>
                );
              },
            },
            { field: "web_risk_status", headerName: "WebRisk", valueFormatter: (p) => p.value ?? "—" },
            {
              field: "listing_type",
              headerName: "Type",
              valueFormatter: (p) => p.value ?? "—",
              cellRenderer: (p: ICellRendererParams<Domain>) => {
                const v = p.value;
                if (!v) return <span className="text-gray-400">—</span>;
                const cls =
                  v === "closeout" || v === "buy" || v === "buynow"
                    ? "bg-blue-100 text-blue-700 dark:bg-blue-500/15 dark:text-blue-300"
                    : v === "register" || v === "premium"
                      ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300"
                    : v === "auction"
                      ? "bg-purple-100 text-purple-700 dark:bg-purple-500/15 dark:text-purple-300"
                      : "bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300";
                return (
                  <span className={`px-2 py-0.5 rounded text-xs font-semibold ${cls}`}>
                    {v}
                  </span>
                );
              },
              width: 120,
              flex: 0,
            },
            { field: "tf", headerName: "TF", filter: "agNumberColumnFilter", width: 80, flex: 0 },
            { field: "cf", headerName: "CF", filter: "agNumberColumnFilter", width: 80, flex: 0 },
            { field: "backlinks", headerName: "BL", filter: "agNumberColumnFilter", width: 90, flex: 0 },
            {
              field: "last_price",
              headerName: "Price",
              filter: "agNumberColumnFilter",
              valueGetter: (p) =>
                p.data?.last_price == null ? null : Number(p.data.last_price),
              valueFormatter: (p) => (p.value == null ? "—" : `$${p.value}`),
              width: 120,
              flex: 0,
            },
            {
              headerName: "Buy",
              colId: "buy",
              valueGetter: (p) => p.data?.last_purchase_url ?? "",
              cellRenderer: (p: ICellRendererParams<Domain>) =>
                p.data?.last_purchase_url ? (
                  <a
                    href={p.data.last_purchase_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 dark:text-blue-400 hover:underline text-xs"
                  >
                    open
                  </a>
                ) : (
                  <span className="text-gray-400">—</span>
                ),
              width: 90,
              flex: 0,
            },
            {
              field: "last_checked_at",
              headerName: "Checked",
              filter: "agDateColumnFilter",
              valueFormatter: (p) =>
                p.value ? new Date(p.value).toLocaleString() : "—",
            },
          ]}
          onDelete={(r) => setDeleting(r)}
          actionsWidth={140}
          extraActions={(row) => [
            {
              icon: <span className="text-xs font-semibold">↻</span>,
              onClick: async () => {
                await fetch(`/api/domains/${row.id}/recheck`, { method: "POST" });
              },
              title: "Recheck",
              className: "text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-500/10",
            },
            {
              icon: (
                <span className="inline-flex items-center justify-center w-4 h-4 rounded-full border border-current text-[10px] font-bold leading-none">
                  i
                </span>
              ),
              onClick: () => setViewing(row),
              title: "Details",
              className: "text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-800",
            },
          ]}
        />
      )}
      {checking && (
        <CheckDomainDrawer
          onClose={() => setChecking(false)}
        />
      )}
      {discovering && (
        <DynadotDiscoverDialog
          onClose={() => setDiscovering(false)}
          onDone={() => {
            setDiscovering(false);
            reload();
          }}
        />
      )}
      {viewing && (
        <DomainDetailDrawer
          domain={viewing}
          onClose={() => {
            setViewing(null);
            reload();
          }}
        />
      )}
      <ConfirmDialog
        open={!!deleting}
        title="Delete domain"
        message={
          deleting && (
            <>
              Are you sure you want to delete{" "}
              <span className="font-semibold text-gray-900 dark:text-gray-100">{deleting.domain}</span>?
            </>
          )
        }
        onCancel={() => setDeleting(null)}
        onConfirm={async () => {
          if (!deleting) return;
          await fetch(`/api/domains/${deleting.id}`, { method: "DELETE" });
          setDeleting(null);
          reload();
        }}
      />
    </PageShell>
  );
}

function CheckDomainDrawer({ onClose }: { onClose: () => void }) {
  const [domain, setDomain] = useState("");
  const [tf, setTf] = useState("");
  const [cf, setCf] = useState("");
  const [da, setDa] = useState("");
  const [backlinks, setBacklinks] = useState("");
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);

  async function run() {
    setError(null);
    setResult(null);
    if (!domain.trim()) {
      setError("Domain is required");
      return;
    }
    setRunning(true);
    try {
      const res = await fetch("/api/domains/check", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          domain: domain.trim(),
          tf: tf ? Number(tf) : undefined,
          cf: cf ? Number(cf) : undefined,
          da: da ? Number(da) : undefined,
          backlinks: backlinks ? Number(backlinks) : undefined,
        }),
      });
      const data = await res.json();
      if (!res.ok) setError(typeof data === "string" ? data : JSON.stringify(data));
      else setResult(data);
    } catch (e: any) {
      setError(String(e?.message ?? e));
    } finally {
      setRunning(false);
    }
  }

  return (
    <Drawer
      title="Check domain (ad-hoc)"
      open={true}
      onClose={onClose}
      onSave={run}
      saving={running}
    >
      {error && (
        <div className="bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/30 text-red-700 dark:text-red-300 text-sm px-3 py-2 rounded">
          {error}
        </div>
      )}
      <Field label="Domain" required>
        <input
          className={inputCls}
          value={domain}
          onChange={(e) => setDomain(e.target.value)}
          placeholder="example.io"
          autoFocus
        />
      </Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="TF (optional)">
          <input className={inputCls} value={tf} onChange={(e) => setTf(e.target.value)} type="number" />
        </Field>
        <Field label="CF (optional)">
          <input className={inputCls} value={cf} onChange={(e) => setCf(e.target.value)} type="number" />
        </Field>
        <Field label="DA (optional)">
          <input className={inputCls} value={da} onChange={(e) => setDa(e.target.value)} type="number" />
        </Field>
        <Field label="Backlinks (optional)">
          <input className={inputCls} value={backlinks} onChange={(e) => setBacklinks(e.target.value)} type="number" />
        </Field>
      </div>
      {result && (
        <div className="mt-2 space-y-2">
          <div className="flex items-center gap-3">
            <span className="text-sm font-medium dark:text-gray-200">Decision:</span>
            <span
              className={
                "px-2 py-0.5 rounded text-xs font-semibold " +
                (result.effectiveDecision === "BUY_CANDIDATE"
                  ? "bg-emerald-100 text-emerald-700"
                  : result.effectiveDecision === "WATCHLIST"
                    ? "bg-yellow-100 text-yellow-700"
                    : "bg-red-100 text-red-700")
              }
            >
              {result.effectiveDecision}
            </span>
            {result.score?.finalScore != null && (
              <span className="text-sm text-gray-500 dark:text-gray-400">
                score: {result.score.finalScore}
              </span>
            )}
          </div>
          {result.gate && (
            <div className="text-xs text-red-700 dark:text-red-300">
              Rejected at <span className="font-semibold">{result.gate.step}</span>: {result.gate.reason}
            </div>
          )}
          <pre className="text-xs font-mono bg-gray-50 dark:bg-gray-800 p-2 rounded overflow-auto max-h-[400px] dark:text-gray-200">
            {JSON.stringify(result, null, 2)}
          </pre>
        </div>
      )}
    </Drawer>
  );
}

function DynadotDiscoverDialog({
  onClose,
  onDone,
}: {
  onClose: () => void;
  onDone: () => void;
}) {
  const POPULAR_TLDS =
    "com,net,org,info,biz,co,io,me,cc,tv,ai,ly,fm,gg,us,ws,xyz,top,click,shop,store,online,site,tech,digital,cyou,app,dev,space,fun,live,pro";
  const [length, setLength] = useState(3);
  const [tlds, setTlds] = useState(POPULAR_TLDS);
  const [includeDigits, setIncludeDigits] = useState(false);
  const [maxRegPrice, setMaxRegPrice] = useState("10");
  const [maxCandidates, setMaxCandidates] = useState("500");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [report, setReport] = useState<{
    generated: number;
    checked: number;
    available: number;
    inserted: number;
    skippedPriced: number;
    samples: string[];
  } | null>(null);

  const tldList = tlds.split(",").map((t) => t.trim()).filter(Boolean);
  const alphabetSize = (includeDigits ? 36 : 26);
  const estimated = Math.min(
    Math.pow(alphabetSize, length) * tldList.length,
    Number(maxCandidates) || 5000,
  );

  async function run() {
    setBusy(true);
    setError(null);
    setReport(null);
    try {
      const res = await fetch("/api/domains/dynadot-discover", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          length,
          tlds: tldList,
          includeDigits,
          maxRegPrice: Number(maxRegPrice) || undefined,
          maxCandidates: Number(maxCandidates) || undefined,
        }),
      });
      const json = await res.json();
      if (!res.ok || json?.error) throw new Error(json?.error ?? `HTTP ${res.status}`);
      setReport(json);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white dark:bg-gray-900 rounded-lg shadow-xl w-full max-w-lg p-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold dark:text-gray-100">
            Discover available domains on Dynadot
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
            <Icon name="x" />
          </button>
        </div>

        <div className="space-y-3 text-sm">
          <div>
            <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
              Label length (characters)
            </label>
            <input
              type="number"
              min={1}
              max={5}
              value={length}
              onChange={(e) => setLength(Math.max(1, Math.min(5, Number(e.target.value) || 1)))}
              className="w-full px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 dark:text-gray-200"
            />
          </div>
          <div>
            <div className="flex justify-between items-end mb-1">
              <label className="block text-xs text-gray-500 dark:text-gray-400">
                TLDs (comma-separated, no dot)
              </label>
              <div className="flex gap-1 text-xs">
                <button
                  type="button"
                  onClick={() => setTlds("io,cc,me,co")}
                  className="text-blue-600 dark:text-blue-400 hover:underline"
                >
                  defaults
                </button>
                <span className="text-gray-300">·</span>
                <button
                  type="button"
                  onClick={() => setTlds(POPULAR_TLDS)}
                  className="text-blue-600 dark:text-blue-400 hover:underline"
                >
                  all popular ({POPULAR_TLDS.split(",").length})
                </button>
              </div>
            </div>
            <input
              value={tlds}
              onChange={(e) => setTlds(e.target.value)}
              placeholder="io,cc,me,co"
              className="w-full px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 dark:text-gray-200 font-mono"
            />
          </div>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={includeDigits}
              onChange={(e) => setIncludeDigits(e.target.checked)}
            />
            <span className="dark:text-gray-200">Include digits (0-9)</span>
          </label>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
                Max reg price ($)
              </label>
              <input
                type="number"
                value={maxRegPrice}
                onChange={(e) => setMaxRegPrice(e.target.value)}
                className="w-full px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 dark:text-gray-200"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
                Max candidates
              </label>
              <input
                type="number"
                value={maxCandidates}
                onChange={(e) => setMaxCandidates(e.target.value)}
                className="w-full px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 dark:text-gray-200"
              />
            </div>
          </div>
          <div className="text-xs text-gray-500 dark:text-gray-400">
            Will check approximately <b>{estimated.toLocaleString()}</b> candidates
            ({Math.ceil(estimated / 100)} API calls). Hard alphabet limit is{" "}
            {alphabetSize}<sup>{length}</sup> = {Math.pow(alphabetSize, length).toLocaleString()}.
          </div>
          {error && <div className="text-red-600 text-xs">{error}</div>}
          {report && (
            <div className="bg-emerald-50 dark:bg-emerald-900/20 p-3 rounded text-xs space-y-1">
              <div>Generated: <b>{report.generated}</b></div>
              <div>Checked: <b>{report.checked}</b></div>
              <div>Available: <b>{report.available}</b></div>
              <div>Skipped (over price): <b>{report.skippedPriced}</b></div>
              <div>Inserted into DB: <b className="text-emerald-700 dark:text-emerald-300">{report.inserted}</b></div>
              {report.samples.length > 0 && (
                <div className="mt-2">
                  <div className="font-semibold mb-1">Samples:</div>
                  <div className="font-mono text-[10px] leading-tight">
                    {report.samples.join(", ")}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 mt-5">
          <button
            onClick={onClose}
            className="px-3 py-1.5 text-sm rounded border border-gray-300 dark:border-gray-600 dark:text-gray-200"
          >
            Close
          </button>
          {report ? (
            <button
              onClick={onDone}
              className="px-3 py-1.5 text-sm rounded bg-emerald-600 hover:bg-emerald-700 text-white"
            >
              Done — reload table
            </button>
          ) : (
            <button
              onClick={run}
              disabled={busy || tldList.length === 0}
              className="px-3 py-1.5 text-sm rounded bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50"
            >
              {busy ? "Searching…" : "Start"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function ShortDomainProvisionPanel({
  domain,
  onChange,
}: {
  domain: Domain;
  onChange: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<{
    ok: boolean;
    zoneId: string;
    status: string;
    nameServers: string[];
    message: string;
  } | null>(null);

  async function provision() {
    setBusy(true);
    setError(null);
    try {
      const res = await fetch(`/api/domains/${domain.id}/provision-short`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
      });
      const json = await res.json();
      if (!res.ok || json?.error) {
        throw new Error(json?.error ?? `HTTP ${res.status}`);
      }
      setResult(json);
      onChange();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }

  async function enableProxy() {
    setBusy(true);
    setError(null);
    try {
      const res = await fetch(`/api/domains/${domain.id}/cloudflare-proxy`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ proxied: true }),
      });
      const json = await res.json();
      if (!res.ok || json?.error) {
        throw new Error(json?.error ?? `HTTP ${res.status}`);
      }
      onChange();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }

  const zoneStatus = domain.cloudflare_zone_status;
  const hasZone = !!domain.cloudflare_zone_id;

  return (
    <div className="mt-3 p-3 border border-blue-200 dark:border-blue-700/40 rounded bg-blue-50 dark:bg-blue-900/10">
      <h5 className="text-xs font-semibold uppercase text-blue-700 dark:text-blue-300 mb-2">
        Short Domain Provisioning
      </h5>
      <div className="text-xs space-y-1 dark:text-gray-200">
        <div>
          Cloudflare status:{" "}
          <span
            className={
              zoneStatus === "active"
                ? "font-semibold text-emerald-600 dark:text-emerald-400"
                : zoneStatus === "pending"
                  ? "font-semibold text-amber-600 dark:text-amber-400"
                  : zoneStatus === "error"
                    ? "font-semibold text-red-600 dark:text-red-400"
                    : "text-gray-500"
            }
          >
            {zoneStatus ?? "not provisioned"}
          </span>
        </div>
        {domain.cloudflare_zone_id && (
          <div className="text-gray-500 font-mono">zone: {domain.cloudflare_zone_id}</div>
        )}
        {domain.cloudflare_provisioned_at && (
          <div className="text-gray-500">
            ready since {new Date(domain.cloudflare_provisioned_at).toLocaleString()}
          </div>
        )}
        {domain.cloudflare_last_error && (
          <div className="text-red-600 dark:text-red-400 text-[11px]">
            last error: {domain.cloudflare_last_error}
          </div>
        )}
      </div>

      {zoneStatus === "active" && hasZone && (
        <div className="mt-2">
          <button
            onClick={enableProxy}
            disabled={busy}
            className="px-3 py-1.5 text-sm bg-amber-500 hover:bg-amber-600 text-white rounded disabled:opacity-50"
            title="Sets A record to proxied=true so Cloudflare serves HTTPS"
          >
            {busy ? "Enabling…" : "Enable Cloudflare proxy (HTTPS)"}
          </button>
        </div>
      )}

      {zoneStatus !== "active" && (
        <div className="mt-2">
          <button
            onClick={provision}
            disabled={busy}
            className="px-3 py-1.5 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded disabled:opacity-50"
          >
            {busy ? "Provisioning…" : hasZone ? "Re-provision" : "Provision as short domain"}
          </button>
          <div className="mt-1 text-[11px] text-gray-500 dark:text-gray-400">
            Adds zone to Cloudflare, swaps Dynadot NS, creates A record →{" "}
            <span className="font-mono">{result?.ok ? "done" : "SHORT_DOMAIN_IP"}</span>.
            Activation takes 5-60 min, after which the domain auto-appears in Short Domains.
          </div>
        </div>
      )}

      {result && result.nameServers.length > 0 && (
        <div className="mt-2 text-[11px] font-mono text-gray-600 dark:text-gray-400">
          NS: {result.nameServers.join(", ")}
        </div>
      )}

      {error && <div className="mt-2 text-xs text-red-600 dark:text-red-400">{error}</div>}
    </div>
  );
}

function BiddingPanel({
  domain,
  onChange,
}: {
  domain: Domain;
  onChange: () => void;
}) {
  const [maxBid, setMaxBid] = useState<string>(
    domain.auto_bid_max ? String(domain.auto_bid_max) : "",
  );
  const [busy, setBusy] = useState<string | null>(null);
  const [confirmBuy, setConfirmBuy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { data: bids, reload: reloadBids } = useAsync<DomainBid[]>(
    () => fetch(`/api/domains/${domain.id}/bids`).then((r) => r.json()),
    [domain.id],
  );
  const { data: live, loading: liveLoading, reload: reloadLive } = useAsync<{
    found: boolean;
    currentBid?: number;
    minNextBid?: number;
    increment?: number;
    endDate?: string;
    isHighBidder?: boolean;
    currency?: string;
    error?: string;
  }>(
    () => fetch(`/api/domains/${domain.id}/auction-live`).then((r) => r.json()),
    [domain.id],
  );

  const action = async (path: string, body?: unknown) => {
    setBusy(path);
    setError(null);
    try {
      const res = await fetch(`/api/domains/${domain.id}${path}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: body ? JSON.stringify(body) : undefined,
      });
      const json = await res.json();
      if (!res.ok || json?.error) {
        throw new Error(json?.error ?? `HTTP ${res.status}`);
      }
      onChange();
      reloadBids();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setBusy(null);
    }
  };

  const armed = domain.auto_bid_armed;
  const ps = domain.purchase_status;
  const isAuction = domain.listing_type === "auction";
  const isCloseout =
    domain.listing_type === "closeout" ||
    domain.listing_type === "buy" ||
    domain.listing_type === "buynow";
  const isRegister =
    domain.listing_type === "register" || domain.listing_type === "premium";

  return (
    <div className="mt-4 border-t border-gray-200 dark:border-gray-700 pt-4">
      <h4 className="text-xs font-semibold uppercase text-gray-500 dark:text-gray-400 mb-2">
        Purchase / Bidding
      </h4>
      <div className="text-sm dark:text-gray-200 space-y-2">
        <div>
          <span className="text-gray-500 dark:text-gray-400">Status:</span>{" "}
          <span className="font-semibold">{ps}</span>
          {domain.purchase_amount && (
            <span className="ml-2 text-gray-500">
              @ ${domain.purchase_amount}
            </span>
          )}
        </div>
        {domain.auction_end_date && (
          <div className="text-xs text-gray-500 dark:text-gray-400">
            Ends: {new Date(domain.auction_end_date).toLocaleString()}
          </div>
        )}

        {isAuction && (
          <div className="mt-2 p-2 bg-gray-50 dark:bg-gray-800 rounded text-xs">
            <div className="flex items-center justify-between mb-1">
              <span className="font-semibold text-gray-500 dark:text-gray-400 uppercase">
                Live from Dynadot
              </span>
              <button
                onClick={() => reloadLive()}
                disabled={liveLoading}
                className="text-blue-600 dark:text-blue-400 hover:underline disabled:opacity-50"
              >
                {liveLoading ? "loading…" : "refresh"}
              </button>
            </div>
            {liveLoading && !live ? (
              <div className="text-gray-400">fetching…</div>
            ) : live?.error ? (
              <div className="text-red-500">{live.error}</div>
            ) : !live?.found ? (
              <div className="text-gray-400">no live data (check Dynadot API key / IP whitelist)</div>
            ) : (
              <div className="grid grid-cols-2 gap-x-3 gap-y-0.5 font-mono">
                <span className="text-gray-500">Current bid:</span>
                <span>${live.currentBid ?? "—"} {live.currency ?? ""}</span>
                <span className="text-gray-500">Min next bid:</span>
                <span>${live.minNextBid ?? "—"}</span>
                <span className="text-gray-500">Increment:</span>
                <span>${live.increment ?? "—"}</span>
                <span className="text-gray-500">Ends:</span>
                <span>{live.endDate ? new Date(live.endDate).toLocaleString() : "—"}</span>
                <span className="text-gray-500">High bidder:</span>
                <span className={live.isHighBidder ? "text-emerald-600 dark:text-emerald-400 font-semibold" : ""}>
                  {live.isHighBidder ? "YOU" : "someone else"}
                </span>
              </div>
            )}
          </div>
        )}

        {isAuction && (
          <div className="flex gap-2 items-end">
            <div className="flex-1">
              <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
                Max bid ($)
              </label>
              <input
                type="number"
                step="1"
                min="1"
                value={maxBid}
                onChange={(e) => setMaxBid(e.target.value)}
                disabled={armed}
                className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 disabled:opacity-50"
              />
            </div>
            {armed ? (
              <button
                onClick={() => action("/disarm")}
                disabled={busy != null}
                className="px-3 py-1.5 text-sm bg-amber-500 hover:bg-amber-600 text-white rounded disabled:opacity-50"
              >
                Disarm
              </button>
            ) : (
              <button
                onClick={() => {
                  const v = Number(maxBid);
                  if (!Number.isFinite(v) || v <= 0) {
                    setError("Enter a positive max bid");
                    return;
                  }
                  action("/arm", { maxBid: v });
                }}
                disabled={busy != null}
                className="px-3 py-1.5 text-sm bg-emerald-600 hover:bg-emerald-700 text-white rounded disabled:opacity-50"
              >
                Arm auto-bid
              </button>
            )}
          </div>
        )}

        {isRegister && (
          <div>
            {confirmBuy ? (
              <div className="flex gap-2 items-center p-2 bg-blue-50 dark:bg-blue-900/20 rounded">
                <span className="text-sm">
                  Register <span className="font-mono">{domain.domain}</span> for $
                  {domain.last_price ?? "?"}?
                </span>
                <button
                  onClick={async () => {
                    setConfirmBuy(false);
                    await action("/register");
                  }}
                  disabled={busy != null}
                  className="px-3 py-1 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded disabled:opacity-50"
                >
                  Confirm
                </button>
                <button
                  onClick={() => setConfirmBuy(false)}
                  className="px-3 py-1 text-sm bg-gray-200 dark:bg-gray-700 rounded"
                >
                  Cancel
                </button>
              </div>
            ) : (
              <button
                onClick={() => setConfirmBuy(true)}
                disabled={busy != null || ps === "bought"}
                className="px-3 py-1.5 text-sm bg-emerald-600 hover:bg-emerald-700 text-white rounded disabled:opacity-50"
              >
                Register (${domain.last_price ?? "?"})
              </button>
            )}
          </div>
        )}

        {isCloseout && (
          <div>
            {confirmBuy ? (
              <div className="flex gap-2 items-center p-2 bg-red-50 dark:bg-red-900/20 rounded">
                <span className="text-sm">
                  Buy <span className="font-mono">{domain.domain}</span> for $
                  {domain.last_price ?? "?"}?
                </span>
                <button
                  onClick={async () => {
                    setConfirmBuy(false);
                    await action("/buy-now");
                  }}
                  disabled={busy != null}
                  className="px-3 py-1 text-sm bg-red-600 hover:bg-red-700 text-white rounded disabled:opacity-50"
                >
                  Confirm
                </button>
                <button
                  onClick={() => setConfirmBuy(false)}
                  className="px-3 py-1 text-sm bg-gray-200 dark:bg-gray-700 rounded"
                >
                  Cancel
                </button>
              </div>
            ) : (
              <button
                onClick={() => setConfirmBuy(true)}
                disabled={busy != null || ps === "bought"}
                className="px-3 py-1.5 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded disabled:opacity-50"
              >
                Buy now (${domain.last_price ?? "?"})
              </button>
            )}
          </div>
        )}

        {error && (
          <div className="text-xs text-red-600 dark:text-red-400">{error}</div>
        )}
      </div>

      {ps === "bought" && (
        <ShortDomainProvisionPanel domain={domain} onChange={onChange} />
      )}

      <div className="mt-3">
        <h5 className="text-xs font-semibold uppercase text-gray-500 dark:text-gray-400 mb-1">
          Bid History
        </h5>
        {!bids || bids.length === 0 ? (
          <div className="text-xs text-gray-400">No bids yet.</div>
        ) : (
          <div className="text-xs font-mono space-y-1 max-h-[200px] overflow-auto">
            {bids.map((b) => (
              <div key={b.id} className="flex gap-2">
                <span className="text-gray-400">
                  {new Date(b.created_at).toLocaleString()}
                </span>
                <span
                  className={
                    b.status === "PLACED" || b.status === "WON"
                      ? "text-emerald-600 dark:text-emerald-400"
                      : b.status === "FAILED" || b.status === "LOST"
                        ? "text-red-600 dark:text-red-400"
                        : b.status === "DRY_RUN"
                          ? "text-blue-600 dark:text-blue-400"
                          : "text-gray-500"
                  }
                >
                  {b.status}
                </span>
                <span>${b.amount}</span>
                {b.current_high_bid != null && (
                  <span className="text-gray-400">
                    (high: ${b.current_high_bid})
                  </span>
                )}
                {b.message && (
                  <span className="text-gray-500 truncate" title={b.message}>
                    — {b.message}
                  </span>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function DomainDetailDrawer({
  domain,
  onClose,
}: {
  domain: Domain;
  onClose: () => void;
}) {
  const { data, loading, reload } = useAsync<any>(() =>
    fetch(`/api/domains/${domain.id}`).then((r) => r.json()),
  );

  // Auto-refresh every 5s while drawer is open so Cloudflare status,
  // bid history, and purchase status update live without manual reload.
  useEffect(() => {
    const t = setInterval(reload, 5_000);
    return () => clearInterval(t);
  }, [reload]);

  return (
    <Drawer
      title={domain.domain}
      open={true}
      onClose={onClose}
      onSave={onClose}
      saving={false}
    >
      {loading ? (
        <div className="text-gray-400">Loading...</div>
      ) : (
        <>
          <div className="text-sm dark:text-gray-200 space-y-1">
            <div>
              <span className="text-gray-500 dark:text-gray-400">Decision:</span>{" "}
              <span className="font-semibold">{data?.decision ?? "—"}</span>
            </div>
            <div>
              <span className="text-gray-500 dark:text-gray-400">Score:</span>{" "}
              <span className="font-semibold">{data?.last_score ?? "—"}</span>
            </div>
            <div>
              <span className="text-gray-500 dark:text-gray-400">Status:</span>{" "}
              {data?.status}
            </div>
            {data?.last_reason && (
              <div className="mt-2 text-xs text-gray-600 dark:text-gray-400 whitespace-pre-wrap">
                {data.last_reason}
              </div>
            )}
          </div>
          <BiddingPanel domain={data ?? domain} onChange={reload} />
          <div className="mt-4">
            <h4 className="text-xs font-semibold uppercase text-gray-500 dark:text-gray-400 mb-2">
              Recent Checks
            </h4>
            <pre className="text-xs font-mono bg-gray-50 dark:bg-gray-800 p-2 rounded overflow-auto max-h-[280px] dark:text-gray-200">
              {JSON.stringify(data?.checks ?? [], null, 2)}
            </pre>
          </div>
          <div className="mt-4">
            <h4 className="text-xs font-semibold uppercase text-gray-500 dark:text-gray-400 mb-2">
              Metrics History
            </h4>
            <pre className="text-xs font-mono bg-gray-50 dark:bg-gray-800 p-2 rounded overflow-auto max-h-[200px] dark:text-gray-200">
              {JSON.stringify(data?.metrics ?? [], null, 2)}
            </pre>
          </div>
        </>
      )}
    </Drawer>
  );
}

// ---------- Short Domains ----------

type WhiteCandidate = {
  id: number;
  domain: string;
  tld: string;
  age_years: number | null;
  registered_at: string | null;
  expires_at: string | null;
  drops_at_estimate: string | null;
  rdap_status: string | null;
  backorder_status: "NONE" | "REQUESTED" | "WON" | "LOST";
  price: number | null;
  tf: number | null;
  cf: number | null;
  referring_domains: number | null;
  wayback_snapshots: number | null;
  wayback_first_year: number | null;
  wayback_topics: string[] | null;
  web_risk_status: string | null;
  score: number | null;
  reasons: { points: number; reason: string }[] | null;
  review_status: "NEW" | "WATCHING" | "APPROVED" | "REJECTED";
  note: string | null;
};

type WhiteSettings = {
  age_min_years: number;
  age_max_years: number;
  max_price: number;
  expired_days_min: number;
  expired_days_max: number;
  allowed_tlds: string[];
  min_tf: number;
  max_cf_tf_ratio: number;
  min_referring_domains: number;
  min_archive_entries: number;
  max_archive_birth_year: number;
  min_domain_pop: number;
};

/**
 * Подписи статусов. Переводим ТОЛЬКО отображение: в базе значения остаются
 * латиницей, на них стоит CHECK и по ним же идёт фильтрация и API.
 */
const REVIEW_LABELS: Record<string, string> = {
  NEW: "new",
  WATCHING: "watching",
  APPROVED: "approved",
  REJECTED: "rejected",
};

const BACKORDER_LABELS: Record<string, string> = {
  NONE: "—",
  REQUESTED: "requested",
  WON: "won",
  LOST: "lost",
};

/** Дней до отпадения — главная колонка: отвечает, когда за доменом идти. */
function daysUntil(iso: string | null): number | null {
  if (!iso) return null;
  return Math.round((new Date(iso).getTime() - Date.now()) / 86_400_000);
}

function CandidateWhiteDomainsTab() {
  const [status, setStatus] = useState<string>("");
  const { data, loading, reload } = useAsync<WhiteCandidate[]>(
    () =>
      fetch(`/api/white-domains${status ? `?status=${status}` : ""}`).then(
        (r) => r.json() as Promise<WhiteCandidate[]>,
      ),
    [status],
  );
  const { data: settings, reload: reloadSettings } = useAsync<WhiteSettings>(() =>
    fetch("/api/white-domains/settings").then((r) => r.json() as Promise<WhiteSettings>),
  );
  const [draft, setDraft] = useState<WhiteSettings | null>(null);
  const [busy, setBusy] = useState(false);
  const s = draft ?? settings;

  const saveSettings = async () => {
    if (!draft) return;
    setBusy(true);
    try {
      await fetch("/api/white-domains/settings", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(draft),
      });
      setDraft(null);
      reloadSettings();
    } finally {
      setBusy(false);
    }
  };

  const discover = async () => {
    setBusy(true);
    try {
      await fetch("/api/white-domains/discover", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ limit: 200 }),
      });
      reload();
    } finally {
      setBusy(false);
    }
  };

  const review = async (row: WhiteCandidate, next: WhiteCandidate["review_status"]) => {
    await fetch(`/api/white-domains/${row.id}/review`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ status: next }),
    });
    reload();
  };

  const field = (
    key: keyof WhiteSettings,
    label: string,
    step = "1",
  ) => (
    <label className="flex items-center gap-2 text-sm">
      <span className="text-gray-400 whitespace-nowrap">{label}</span>
      <input
        type="number"
        step={step}
        className="w-20 rounded border border-gray-600 bg-transparent px-2 py-1"
        value={String(s?.[key] ?? "")}
        onChange={(e) =>
          s && setDraft({ ...s, [key]: Number(e.target.value) } as WhiteSettings)
        }
      />
    </label>
  );

  return (
    <PageShell title="Candidate White Domains" total={data?.length ?? 0}>
      <div className="mb-4 flex flex-wrap items-center gap-4 rounded border border-gray-700 p-3">
        {field("age_min_years", "Age from, years", "0.5")}
        {field("age_max_years", "to (0 = no cap)", "0.5")}
        {field("max_price", "Price up to, $")}
        {field("min_archive_entries", "Archive snapshots from")}
        {field("max_archive_birth_year", "Archive since year, max")}
        {field("min_domain_pop", "Ref. domains from")}
        <label className="flex items-center gap-2 text-sm">
          <span className="text-gray-400">TLDs</span>
          <input
            className="w-64 rounded border border-gray-600 bg-transparent px-2 py-1 font-mono"
            value={(s?.allowed_tlds ?? []).join(",")}
            onChange={(e) =>
              s &&
              setDraft({
                ...s,
                allowed_tlds: e.target.value.split(",").map((x) => x.trim()).filter(Boolean),
              })
            }
          />
        </label>
        <button
          className="rounded bg-blue-600 px-3 py-1 text-sm disabled:opacity-50"
          onClick={saveSettings}
          disabled={!draft || busy}
        >
          Save criteria
        </button>
        <button
          className="rounded bg-green-700 px-3 py-1 text-sm disabled:opacity-50"
          onClick={discover}
          disabled={busy}
        >
          {busy ? "Searching…" : "Find candidates"}
        </button>
        <select
          className="rounded border border-gray-600 bg-transparent px-2 py-1 text-sm"
          value={status}
          onChange={(e) => setStatus(e.target.value)}
        >
          <option value="">All</option>
          <option value="NEW">New</option>
          <option value="WATCHING">Watching</option>
          <option value="APPROVED">Approved</option>
          <option value="REJECTED">Rejected</option>
        </select>
        <span className="text-xs text-gray-500">
          Nothing is purchased automatically — selection only.
        </span>
      </div>

      {loading && data === null ? (
        <div className="p-6 text-gray-400">Loading...</div>
      ) : (
        <AgTable<WhiteCandidate>
          // Ключ с версией: набор колонок менялся (убрали TF и CF, добавили
          // "Архив с года"), а сохранённая раскладка применяется поверх новых
          // определений и ломает таблицу. Меняем ключ — старое состояние
          // перестаёт подхватываться. При следующей смене колонок поднять v3.
          persistKey="white_domains_v2"
          rows={data ?? []}
          groupable
          columns={[
            {
              field: "domain",
              headerName: "Domain",
              minWidth: 220,
              cellRenderer: (p: ICellRendererParams<WhiteCandidate>) => (
                <a
                  className="font-mono font-medium text-blue-400 hover:underline"
                  href={`https://web.archive.org/web/*/${p.value}`}
                  target="_blank"
                  rel="noreferrer"
                >
                  {p.value}
                </a>
              ),
            },
            {
              headerName: "Drops in",
              colId: "drops_in",
              valueGetter: (p) => daysUntil(p.data?.drops_at_estimate ?? null),
              valueFormatter: (p) => (p.value == null ? "—" : `${p.value} d`),
              cellStyle: (p) =>
                p.value != null && p.value <= 7 ? { color: "#4ade80" } : undefined,
              width: 130,
            },
            { field: "age_years", headerName: "Age", width: 100 },
            { field: "price", headerName: "Price, $", width: 100 },
            { field: "referring_domains", headerName: "Ref. domains", width: 150 },
            { field: "wayback_snapshots", headerName: "Snapshots", width: 150 },
            { field: "wayback_first_year", headerName: "Archive since", width: 130 },
            {
              field: "wayback_topics",
              headerName: "Bad topics",
              valueFormatter: (p) =>
                Array.isArray(p.value) && p.value.length ? p.value.join(", ") : "",
              cellStyle: (p) =>
                Array.isArray(p.value) && p.value.length ? { color: "#f87171" } : undefined,
              minWidth: 140,
            },
            {
              field: "web_risk_status",
              headerName: "Web risk",
              width: 110,
              valueFormatter: (p) =>
                p.value === "CLEAN" ? "clean" : p.value === "UNSAFE" ? "unsafe" : p.value ? "unknown" : "",
            },
            { field: "score", headerName: "Score", width: 90 },
            {
              field: "review_status",
              headerName: "Review",
              width: 130,
              // Переводим только подпись: в базе значения остаются как есть,
              // на них стоит CHECK и по ним идёт фильтрация.
              valueFormatter: (p) => REVIEW_LABELS[p.value as string] ?? p.value ?? "",
            },
            {
              field: "backorder_status",
              headerName: "Backorder",
              width: 120,
              valueFormatter: (p) => BACKORDER_LABELS[p.value as string] ?? p.value ?? "",
            },
            {
              headerName: "Why",
              colId: "why",
              // Защита на случай, если reasons придёт не массивом: исключение
              // в valueGetter роняет отрисовку всей таблицы, а не одной ячейки.
              valueGetter: (p) => {
                const r = p.data?.reasons;
                return Array.isArray(r) ? r.map((x) => x.reason).join(" · ") : "";
              },
              minWidth: 280,
            },
          ]}
          extraActions={() => [
            {
              icon: "👁",
              title: "Watch",
              onClick: (row) => review(row, "WATCHING"),
            },
            {
              icon: "✓",
              title: "Approve",
              className: "text-green-400",
              onClick: (row) => review(row, "APPROVED"),
            },
            {
              icon: "✕",
              title: "Reject",
              className: "text-red-400",
              onClick: (row) => review(row, "REJECTED"),
            },
          ]}
          actionsWidth={200}
        />
      )}
    </PageShell>
  );
}

function ShortDomainsTab() {
  const { data, loading, reload } = useAsync<ShortDomain[]>(() =>
    fetch("/api/short-domains").then((r) => r.json() as Promise<ShortDomain[]>),
  );
  const [editing, setEditing] = useState<ShortDomain | "new" | null>(null);
  const [deleting, setDeleting] = useState<ShortDomain | null>(null);

  return (
    <PageShell
      title="Short Domains"
      total={data?.length ?? 0}
      onAdd={() => setEditing("new")}
    >
      {loading && data === null ? (
        <div className="p-6 text-gray-400">Loading...</div>
      ) : (
        <AgTable<ShortDomain>
          persistKey="short_domains"
          rows={data ?? []}
          columns={[
            {
              field: "domain",
              headerName: "Domain",
              cellRenderer: (p: ICellRendererParams<ShortDomain>) => (
                <span className="font-medium font-mono">{p.value}</span>
              ),
            },
            {
              field: "provider",
              headerName: "Provider",
              valueFormatter: (p) => p.value ?? "—",
            },
            {
              field: "notes",
              headerName: "Notes",
              valueFormatter: (p) => p.value ?? "—",
              flex: 2,
            },
            {
              field: "created_at",
              headerName: "Created",
              filter: "agDateColumnFilter",
              valueFormatter: (p) =>
                p.value ? new Date(p.value).toLocaleString() : "",
            },
          ]}
          onEdit={(r) => setEditing(r)}
          onDelete={(r) => setDeleting(r)}
        />
      )}
      {editing && (
        <ShortDomainDrawer
          shortDomain={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            reload();
          }}
        />
      )}
      <ConfirmDialog
        open={!!deleting}
        title="Delete short domain"
        message={
          deleting && (
            <>
              Delete{" "}
              <span className="font-semibold text-gray-900 dark:text-gray-100">
                {deleting.domain}
              </span>
              ?
            </>
          )
        }
        onCancel={() => setDeleting(null)}
        onConfirm={async () => {
          if (!deleting) return;
          await fetch(`/api/short-domains/${deleting.id}`, { method: "DELETE" });
          setDeleting(null);
          reload();
        }}
      />
    </PageShell>
  );
}

function ShortDomainDrawer({
  shortDomain,
  onClose,
  onSaved,
}: {
  shortDomain: ShortDomain | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [domain, setDomain] = useState(shortDomain?.domain ?? "");
  const [provider, setProvider] = useState(shortDomain?.provider ?? "");
  const [notes, setNotes] = useState(shortDomain?.notes ?? "");
  const [saving, setSaving] = useState(false);
  const [domainError, setDomainError] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  async function save() {
    setFormError(null);
    setDomainError(null);
    const trimmed = domain.trim().toLowerCase();
    if (!trimmed) {
      setDomainError("Domain is required");
      return;
    }
    if (!/^[a-z0-9.-]+\.[a-z]{2,}$/.test(trimmed)) {
      setDomainError("Invalid domain format");
      return;
    }

    setSaving(true);
    const body = JSON.stringify({
      domain: trimmed,
      provider: provider.trim() || null,
      notes: notes.trim() || null,
    });
    const res = shortDomain
      ? await fetch(`/api/short-domains/${shortDomain.id}`, {
          method: "PUT",
          headers: { "content-type": "application/json" },
          body,
        })
      : await fetch("/api/short-domains", {
          method: "POST",
          headers: { "content-type": "application/json" },
          body,
        });
    setSaving(false);
    if (!res.ok) {
      setFormError(await res.text());
      return;
    }
    onSaved();
  }

  return (
    <Drawer
      title={shortDomain ? `Edit Short Domain #${shortDomain.id}` : "New Short Domain"}
      open={true}
      onClose={onClose}
      onSave={save}
      saving={saving}
    >
      {formError && (
        <div className="bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/30 text-red-700 dark:text-red-300 text-sm px-3 py-2 rounded">
          {formError}
        </div>
      )}
      <Field label="Domain" required error={domainError}>
        <input
          className={domainError ? inputErrorCls : inputCls}
          value={domain}
          onChange={(e) => {
            setDomain(e.target.value);
            if (domainError) setDomainError(null);
          }}
          placeholder="example.shop"
          autoFocus
        />
      </Field>
      <Field label="Provider">
        <input
          className={inputCls}
          value={provider}
          onChange={(e) => setProvider(e.target.value)}
          placeholder="e.g. dynadot, cloudflare (optional)"
        />
      </Field>
      <Field label="Notes">
        <textarea
          className={inputCls + " min-h-[80px]"}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Optional"
        />
      </Field>
    </Drawer>
  );
}

// ---------- Actions / Funnel ----------

function pct(num: number, denom: number): string {
  if (denom === 0) return "—";
  return ((num / denom) * 100).toFixed(1) + "%";
}

/**
 * Small button that bumps a counter — pair with `<AgTable filterResetKey={n}>`
 * to clear all in-grid filters from outside the grid. Persisted filter state
 * is also wiped (so the reset survives reload).
 */
function ResetFiltersButton({ onClick }: { onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className="px-3 py-1.5 rounded-md border border-gray-300 dark:border-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 text-sm cursor-pointer"
      title="Clear all column filters in the grid below"
    >
      ⌫ Reset filters
    </button>
  );
}

function ActionsTab() {
  const stats = useAsync<ActionsStats>(() =>
    fetch("/api/actions/stats").then((r) => r.json() as Promise<ActionsStats>),
  );
  // Backend caps at 5000 rows (server-side hard cap is 5000 — see listActions).
  // ag-grid filters client-side over whatever's loaded; rows older than the
  // most-recent 5000 events won't be searchable here. For deeper lookups use
  // /api/actions?slug=<...> directly or filter via the URL bar.
  const ACTIONS_LIMIT = 5000;
  const actions = useAsync<ActionRow[]>(() =>
    fetch(`/api/actions?limit=${ACTIONS_LIMIT}`).then(
      (r) => r.json() as Promise<ActionRow[]>,
    ),
  );
  const [filterResetKey, setFilterResetKey] = useState(0);
  // EvaluateDialog can be opened from any row that has a campaign_id by
  // clicking the 🧪 icon next to the campaign name.
  const [evalCampaignId, setEvalCampaignId] = useState<number | null>(null);

  // NOTE: no auto-refresh — reloading the array props re-renders the AgTable
  // grids (Recent Events + By Route) and drops the user's in-flight filter /
  // sort / column-width changes. Use the ↻ Refresh button in the header
  // instead when you want fresh data.

  const totals = stats.data?.totals ?? {
    sent: 0,
    clicks: 0,
    unique_clicks: 0,
    leads: 0,
    deposits: 0,
    cost_sms: 0,
    cost_hlr: 0,
  };
  const costTotal = totals.cost_sms + totals.cost_hlr;

  // Body view switcher. Always opens on "events" when the tab is mounted —
  // user can switch to "routes" mid-session but next navigation back here
  // starts fresh on Events. No persistence by design.
  const [view, setView] = useState<"events" | "routes">("events");
  const [whenFilter, setWhenFilter] = useState<
    "all" | "1h" | "3h" | "today" | "week"
  >("all");

  const filteredActions = React.useMemo(() => {
    const rows = actions.data ?? [];
    if (whenFilter === "all" || rows.length === 0) return rows;
    const now = new Date();
    let since: Date;
    if (whenFilter === "1h") {
      since = new Date(now.getTime() - 60 * 60 * 1000);
    } else if (whenFilter === "3h") {
      since = new Date(now.getTime() - 3 * 60 * 60 * 1000);
    } else if (whenFilter === "today") {
      since = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    } else {
      since = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
    }
    const t = since.getTime();
    return rows.filter((r) => new Date(r.created_at).getTime() >= t);
  }, [actions.data, whenFilter]);

  // Slug search — when set, bypasses the recent-N feed and queries the API
  // directly for any age of slug. Debounced 300ms to avoid spamming the
  // backend while typing.
  const [slugInput, setSlugInput] = useState("");
  const [slugQuery, setSlugQuery] = useState("");
  useEffect(() => {
    const trimmed = slugInput.trim();
    if (trimmed === slugQuery) return;
    const t = setTimeout(() => setSlugQuery(trimmed), 300);
    return () => clearTimeout(t);
  }, [slugInput, slugQuery]);
  const slugResults = useAsync<ActionRow[]>(
    () => {
      if (!slugQuery) return Promise.resolve([] as ActionRow[]);
      return fetch(`/api/actions?slug=${encodeURIComponent(slugQuery)}`).then(
        (r) => r.json() as Promise<ActionRow[]>,
      );
    },
    [slugQuery],
  );

  const displayedRows = slugQuery ? slugResults.data ?? [] : filteredActions;
  const isSearching = slugQuery !== "" && slugResults.loading;

  return (
    <PageShell
      title="Actions"
      total={displayedRows.length}
      onAdd={() => {
        stats.reload();
        actions.reload();
      }}
      addLabel="↻ Refresh"
      extraHeader={
        <div className="flex items-center gap-1.5 text-xs">
          <div className="inline-flex rounded-md border border-gray-300 dark:border-gray-700 overflow-hidden mr-1">
            <button
              onClick={() => setView("events")}
              className={
                "px-2 py-1 text-xs transition " +
                (view === "events"
                  ? "bg-blue-600 text-white"
                  : "bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700")
              }
              title="Recent events feed"
            >
              Events
            </button>
            <button
              onClick={() => setView("routes")}
              className={
                "px-2 py-1 text-xs transition border-l border-gray-300 dark:border-gray-700 " +
                (view === "routes"
                  ? "bg-blue-600 text-white"
                  : "bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700")
              }
              title="Aggregated stats per route"
            >
              By Route
            </button>
          </div>
          {view === "events" && (
            <div className="inline-flex rounded-md border border-gray-300 dark:border-gray-700 overflow-hidden mr-1">
              {(
                [
                  ["all", "All"],
                  ["1h", "1h"],
                  ["3h", "3h"],
                  ["today", "Today"],
                  ["week", "Week"],
                ] as const
              ).map(([key, label], i) => (
                <button
                  key={key}
                  onClick={() => setWhenFilter(key)}
                  className={
                    "px-2 py-1 text-xs transition " +
                    (i > 0
                      ? "border-l border-gray-300 dark:border-gray-700 "
                      : "") +
                    (whenFilter === key
                      ? "bg-blue-600 text-white"
                      : "bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700")
                  }
                  title={`Filter events by ${label.toLowerCase()}`}
                >
                  {label}
                </button>
              ))}
            </div>
          )}
          {view === "events" && (
            <div className="relative">
              <input
                type="text"
                value={slugInput}
                onChange={(e) => setSlugInput(e.target.value)}
                placeholder="Search slug…"
                className="px-2 py-1 pr-7 rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-xs font-mono w-36 focus:outline-none focus:ring-1 focus:ring-blue-500"
                title="Search any slug directly via the API — bypasses the 5000-row recent limit"
              />
              {slugInput && (
                <button
                  onClick={() => setSlugInput("")}
                  className="absolute right-1 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 w-5 h-5 inline-flex items-center justify-center text-sm cursor-pointer"
                  title="Clear search"
                >
                  ×
                </button>
              )}
            </div>
          )}
          <FunnelChip label="Sent" value={totals.sent} tone="gray" />
          <FunnelChip
            label="Clicks"
            value={totals.clicks}
            tone="blue"
            rate={pct(totals.clicks, totals.sent)}
          />
          <FunnelChip
            label="Uniq"
            value={totals.unique_clicks}
            tone="blue"
            rate={pct(totals.unique_clicks, totals.sent)}
          />
          <FunnelChip
            label="Leads"
            value={totals.leads}
            tone="amber"
            rate={pct(totals.leads, totals.clicks)}
          />
          <FunnelChip
            label="Deposits"
            value={totals.deposits}
            tone="emerald"
            rate={pct(totals.deposits, totals.leads)}
          />
          <FunnelChip
            label="C/S"
            value={pct(totals.clicks, totals.sent)}
            tone="gray"
          />
          <FunnelChip
            label="L/C"
            value={pct(totals.leads, totals.clicks)}
            tone="gray"
          />
          <FunnelChip
            label="D/C"
            value={pct(totals.deposits, totals.clicks)}
            tone="gray"
          />
          <span
            className="inline-flex items-center gap-2 px-2 py-1 rounded-md bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 ml-1"
            title={`SMS $${totals.cost_sms.toFixed(4)} · HLR $${totals.cost_hlr.toFixed(4)}`}
          >
            <span className="font-bold">${costTotal.toFixed(2)}</span>
            <span className="font-bold text-xs">
              sms <span className="font-mono">${totals.cost_sms.toFixed(2)}</span>
              <span className="opacity-50 mx-1">·</span>
              hlr <span className="font-mono">${totals.cost_hlr.toFixed(2)}</span>
            </span>
          </span>
          <ResetFiltersButton onClick={() => setFilterResetKey((n) => n + 1)} />
        </div>
      }
    >
      <div className="h-full flex flex-col">
        {view === "routes" && (
          <div className="flex-1 min-h-0">
            <AgTable<FunnelStatByRoute & { id: number }>
                persistKey="actions_by_route"
                filterResetKey={filterResetKey}
                hideId
                rows={(stats.data?.by_route ?? []).map((r, i) => ({
                  ...r,
                  id: r.route_id ?? -i - 1,
                }))}
                columns={[
                  {
                    field: "route_name",
                    headerName: "Route",
                    cellRenderer: (p: ICellRendererParams<FunnelStatByRoute>) => {
                      if (!p.value)
                        return (
                          <span
                            className="px-2 py-0.5 rounded bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 text-xs font-medium uppercase tracking-wide"
                            title="Provider-managed routing (e.g. MessageWhiz)"
                          >
                            default
                          </span>
                        );
                      return (
                        <span className="inline-flex items-center gap-1">
                          <span className="font-medium">{p.value}</span>
                          {p.data?.route_country_iso2 && (
                            <span className="px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 text-[10px] font-semibold">
                              {p.data.route_country_iso2}
                            </span>
                          )}
                          {p.data?.route_external_id && (
                            <span className="text-gray-500 font-mono text-xs">
                              #{p.data.route_external_id}
                            </span>
                          )}
                        </span>
                      );
                    },
                    flex: 2,
                  },
                  {
                    field: "sent",
                    headerName: "Sent",
                    filter: "agNumberColumnFilter",
                    cellRenderer: (p: ICellRendererParams<FunnelStatByRoute>) =>
                      p.value > 0 ? (
                        <span className="font-medium">{p.value}</span>
                      ) : (
                        <span className="text-gray-400">0</span>
                      ),
                    width: 90,
                    flex: 0,
                  },
                  {
                    field: "clicks",
                    headerName: "Clicks",
                    filter: "agNumberColumnFilter",
                    cellRenderer: (p: ICellRendererParams<FunnelStatByRoute>) =>
                      p.value > 0 ? (
                        <span className="text-blue-700 dark:text-blue-400 font-medium">
                          {p.value}
                        </span>
                      ) : (
                        <span className="text-gray-400">0</span>
                      ),
                    width: 90,
                    flex: 0,
                  },
                  {
                    field: "unique_clicks",
                    headerName: "Uniq",
                    filter: "agNumberColumnFilter",
                    cellRenderer: (p: ICellRendererParams<FunnelStatByRoute>) =>
                      p.value > 0 ? (
                        <span className="text-blue-700 dark:text-blue-400 font-medium">
                          {p.value}
                        </span>
                      ) : (
                        <span className="text-gray-400">0</span>
                      ),
                    width: 80,
                    flex: 0,
                  },
                  {
                    field: "leads",
                    headerName: "Leads",
                    filter: "agNumberColumnFilter",
                    cellRenderer: (p: ICellRendererParams<FunnelStatByRoute>) =>
                      p.value > 0 ? (
                        <span className="text-amber-700 dark:text-amber-400 font-medium">
                          {p.value}
                        </span>
                      ) : (
                        <span className="text-gray-400">0</span>
                      ),
                    width: 90,
                    flex: 0,
                  },
                  {
                    field: "deposits",
                    headerName: "Deposits",
                    filter: "agNumberColumnFilter",
                    cellRenderer: (p: ICellRendererParams<FunnelStatByRoute>) =>
                      p.value > 0 ? (
                        <span className="text-emerald-700 dark:text-emerald-400 font-medium">
                          {p.value}
                        </span>
                      ) : (
                        <span className="text-gray-400">0</span>
                      ),
                    width: 100,
                    flex: 0,
                  },
                  {
                    headerName: "CTR",
                    colId: "ctr",
                    sort: "desc",
                    valueGetter: (p) =>
                      p.data && p.data.sent > 0
                        ? p.data.clicks / p.data.sent
                        : 0,
                    valueFormatter: (p) =>
                      p.data && p.data.sent > 0
                        ? pct(p.data.clicks, p.data.sent)
                        : "—",
                    cellRenderer: (p: ICellRendererParams<FunnelStatByRoute>) => {
                      if (!p.data || p.data.sent === 0)
                        return <span className="text-gray-400">—</span>;
                      const v = pct(p.data.clicks, p.data.sent);
                      return (
                        <span className="font-semibold text-blue-700 dark:text-blue-400">
                          {v}
                        </span>
                      );
                    },
                    width: 90,
                    flex: 0,
                  },
                  {
                    headerName: "Uniq CTR",
                    colId: "uniq_ctr",
                    valueGetter: (p) =>
                      p.data && p.data.sent > 0
                        ? p.data.unique_clicks / p.data.sent
                        : 0,
                    valueFormatter: (p) =>
                      p.data && p.data.sent > 0
                        ? pct(p.data.unique_clicks, p.data.sent)
                        : "—",
                    cellRenderer: (p: ICellRendererParams<FunnelStatByRoute>) => {
                      if (!p.data || p.data.sent === 0)
                        return <span className="text-gray-400">—</span>;
                      const v = pct(p.data.unique_clicks, p.data.sent);
                      return (
                        <span className="font-semibold text-indigo-700 dark:text-indigo-400">
                          {v}
                        </span>
                      );
                    },
                    width: 100,
                    flex: 0,
                  },
                  {
                    field: "cost",
                    headerName: "Cost",
                    filter: "agNumberColumnFilter",
                    valueFormatter: (p) =>
                      p.value == null ? "—" : `$${Number(p.value).toFixed(2)}`,
                    cellRenderer: (p: ICellRendererParams<FunnelStatByRoute>) =>
                      p.value > 0 ? (
                        <span className="font-semibold text-emerald-700 dark:text-emerald-400">
                          ${Number(p.value).toFixed(2)}
                        </span>
                      ) : (
                        <span className="text-gray-400">—</span>
                      ),
                    width: 100,
                    flex: 0,
                  },
                  {
                    headerName: "CPC",
                    colId: "cpc",
                    // CPC is computed against UNIQUE clicks (one click per
                    // recipient), not total click events. Honest visitor cost.
                    valueGetter: (p) =>
                      p.data && p.data.unique_clicks > 0
                        ? p.data.cost / p.data.unique_clicks
                        : 0,
                    valueFormatter: (p) =>
                      p.data && p.data.unique_clicks > 0
                        ? `$${(p.data.cost / p.data.unique_clicks).toFixed(3)}`
                        : "—",
                    width: 90,
                    flex: 0,
                  },
                  {
                    headerName: "CPL",
                    colId: "cpl",
                    valueGetter: (p) =>
                      p.data && p.data.leads > 0 ? p.data.cost / p.data.leads : 0,
                    valueFormatter: (p) =>
                      p.data && p.data.leads > 0
                        ? `$${(p.data.cost / p.data.leads).toFixed(2)}`
                        : "—",
                    width: 90,
                    flex: 0,
                  },
                  {
                    headerName: "L/C",
                    colId: "lc",
                    valueGetter: (p) =>
                      p.data && p.data.clicks > 0
                        ? p.data.leads / p.data.clicks
                        : 0,
                    valueFormatter: (p) =>
                      p.data && p.data.clicks > 0
                        ? pct(p.data.leads, p.data.clicks)
                        : "—",
                    width: 90,
                    flex: 0,
                  },
                  {
                    headerName: "D/C",
                    colId: "dc",
                    valueGetter: (p) =>
                      p.data && p.data.clicks > 0
                        ? p.data.deposits / p.data.clicks
                        : 0,
                    valueFormatter: (p) =>
                      p.data && p.data.clicks > 0
                        ? pct(p.data.deposits, p.data.clicks)
                        : "—",
                    width: 90,
                    flex: 0,
                  },
                ]}
              />
          </div>
        )}
        {view === "events" && (
        <div className="flex-1 min-h-0">
          {(actions.loading && actions.data === null) || isSearching ? (
            <div className="p-6 text-gray-400">Loading...</div>
          ) : (
            <AgTable<ActionRow>
              persistKey="actions_list"
              filterResetKey={filterResetKey}
              hideId
              groupable
              rows={displayedRows}
              columns={[
                {
                  field: "type",
                  headerName: "Type",
                  width: 110,
                  flex: 0,
                  cellRenderer: (p: ICellRendererParams<ActionRow>) => {
                    const v = p.value as ActionRow["type"] | undefined;
                    if (!v) return null;
                    const cls =
                      v === "sent"
                        ? "bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200"
                        : v === "click"
                          ? "bg-blue-100 dark:bg-blue-500/15 text-blue-700 dark:text-blue-300"
                          : v === "lead"
                            ? "bg-amber-100 dark:bg-amber-500/15 text-amber-700 dark:text-amber-300"
                            : "bg-emerald-100 dark:bg-emerald-500/15 text-emerald-700 dark:text-emerald-300";
                    return (
                      <span
                        className={`px-2 py-0.5 rounded text-xs font-semibold uppercase ${cls}`}
                      >
                        {v}
                      </span>
                    );
                  },
                },
                {
                  field: "campaign_name",
                  headerName: "Campaign",
                  cellRenderer: (p: ICellRendererParams<ActionRow>) => {
                    if (!p.value)
                      return <span className="text-gray-400">—</span>;
                    const cid = p.data?.campaign_id;
                    return (
                      <span className="inline-flex items-center gap-1.5">
                        <span>{p.value}</span>
                        {cid != null && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setEvalCampaignId(cid);
                            }}
                            className="inline-flex items-center justify-center w-5 h-5 rounded text-purple-700 dark:text-purple-300 hover:bg-purple-100 dark:hover:bg-purple-500/20 cursor-pointer text-xs"
                            title={`Evaluate campaign #${cid}`}
                          >
                            🧪
                          </button>
                        )}
                      </span>
                    );
                  },
                },
                {
                  field: "phone",
                  headerName: "Phone",
                  valueFormatter: (p) => p.value ?? "—",
                },
                {
                  field: "route_name",
                  headerName: "Route",
                  width: 200,
                  flex: 0,
                  cellRenderer: (p: ICellRendererParams<ActionRow>) => {
                    if (!p.value) {
                      return (
                        <span
                          className="px-2 py-0.5 rounded bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 text-xs font-medium uppercase tracking-wide"
                          title="Provider-managed routing"
                        >
                          default
                        </span>
                      );
                    }
                    return (
                      <span
                        className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-700 text-xs"
                        title={`route_id=${p.data?.route_id ?? ""}`}
                      >
                        <span>{p.value}</span>
                        {p.data?.route_country_iso2 && (
                          <span className="text-gray-700 dark:text-gray-300 text-[10px] font-semibold uppercase">
                            {p.data.route_country_iso2}
                          </span>
                        )}
                        {p.data?.route_external_id && (
                          <span className="text-gray-500 font-mono">
                            #{p.data.route_external_id}
                          </span>
                        )}
                      </span>
                    );
                  },
                },
                {
                  field: "sent_text",
                  headerName: "Text",
                  cellRenderer: (p: ICellRendererParams<ActionRow>) => {
                    if (!p.value) return <span className="text-gray-400">—</span>;
                    const v = p.value as string;
                    const short = v.length > 60 ? v.slice(0, 60) + "…" : v;
                    return (
                      <span className="text-xs" title={v}>
                        {short}
                      </span>
                    );
                  },
                  flex: 2,
                  minWidth: 200,
                },
                {
                  field: "slug",
                  headerName: "Slug",
                  cellRenderer: (p: ICellRendererParams<ActionRow>) =>
                    p.value ? (
                      <code className="text-xs">{p.value}</code>
                    ) : (
                      <span className="text-gray-400">—</span>
                    ),
                  width: 130,
                  flex: 0,
                },
                {
                  field: "domain",
                  headerName: "Domain",
                  cellRenderer: (p: ICellRendererParams<ActionRow>) =>
                    p.value ? (
                      <span className="font-mono text-xs">{p.value}</span>
                    ) : (
                      <span className="text-gray-400">—</span>
                    ),
                  width: 150,
                  flex: 0,
                },
                {
                  field: "url_destination_id",
                  headerName: "URL",
                  width: 130,
                  flex: 0,
                  filter: "agNumberColumnFilter",
                  cellRenderer: (p: ICellRendererParams<ActionRow>) => {
                    if (p.value == null)
                      return <span className="text-gray-400">—</span>;
                    const name = p.data?.url_destination_name;
                    const url = p.data?.url_destination_url;
                    return (
                      <span
                        title={url ?? ""}
                        className="inline-flex items-center gap-1"
                      >
                        <span className="px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 text-xs font-mono">
                          #{p.value}
                        </span>
                        {name && (
                          <span className="text-xs text-gray-600 dark:text-gray-400 truncate">
                            {name}
                          </span>
                        )}
                      </span>
                    );
                  },
                },
                {
                  field: "device",
                  headerName: "Device",
                  valueFormatter: (p) => p.value ?? "—",
                  width: 120,
                  flex: 0,
                },
                {
                  field: "browser",
                  headerName: "Browser",
                  valueFormatter: (p) => p.value ?? "—",
                  width: 120,
                  flex: 0,
                },
                {
                  field: "ip",
                  headerName: "IP",
                  valueFormatter: (p) => p.value ?? "—",
                  width: 140,
                  flex: 0,
                },
                {
                  field: "created_at",
                  headerName: "When",
                  filter: "agDateColumnFilter",
                  valueFormatter: (p) =>
                    p.value ? new Date(p.value).toLocaleString() : "",
                  width: 180,
                  flex: 0,
                  enableRowGroup: false,
                },
                // ─── Aggregation-friendly metric columns ────────────────────
                // Hidden by default. When the user drags any column (Domain,
                // Route, Type, Campaign, …) into the group panel, ag-grid
                // sums these in the group row so you see Sent/Clicks/Leads/
                // Deposits/Cost/CTR/CPC/CPL per group.
                {
                  headerName: "Sent",
                  colId: "agg_sent",
                  width: 80,
                  flex: 0,
                  hide: true,
                  enableValue: true,
                  aggFunc: "sum",
                  valueGetter: (p) =>
                    p.node?.group ? undefined : p.data?.type === "sent" ? 1 : 0,
                },
                {
                  headerName: "Clicks",
                  colId: "agg_clicks",
                  width: 90,
                  flex: 0,
                  hide: true,
                  enableValue: true,
                  aggFunc: "sum",
                  valueGetter: (p) =>
                    p.node?.group ? undefined : p.data?.type === "click" ? 1 : 0,
                },
                {
                  // Unique clickers: per leaf returns campaign_phone_id when
                  // the event is a click, else undefined. Custom 'uniqueCount'
                  // aggregator builds a Set; cell renderer shows its size.
                  headerName: "Uniq",
                  colId: "agg_unique_clicks",
                  width: 80,
                  flex: 0,
                  hide: true,
                  enableValue: true,
                  aggFunc: "uniqueCount",
                  valueGetter: (p) =>
                    p.node?.group
                      ? undefined
                      : p.data?.type === "click"
                        ? p.data?.campaign_phone_id ?? null
                        : null,
                  valueFormatter: (p) =>
                    p.value instanceof Set ? String(p.value.size) : "",
                  cellRenderer: (p: ICellRendererParams<ActionRow>) =>
                    p.value instanceof Set ? String(p.value.size) : "",
                },
                {
                  headerName: "Leads",
                  colId: "agg_leads",
                  width: 90,
                  flex: 0,
                  hide: true,
                  enableValue: true,
                  aggFunc: "sum",
                  valueGetter: (p) =>
                    p.node?.group ? undefined : p.data?.type === "lead" ? 1 : 0,
                },
                {
                  headerName: "Deposits",
                  colId: "agg_deposits",
                  width: 100,
                  flex: 0,
                  hide: true,
                  enableValue: true,
                  aggFunc: "sum",
                  valueGetter: (p) =>
                    p.node?.group ? undefined : p.data?.type === "deposit" ? 1 : 0,
                },
                {
                  headerName: "Cost",
                  colId: "agg_cost",
                  width: 100,
                  flex: 0,
                  hide: true,
                  enableValue: true,
                  aggFunc: "sum",
                  valueGetter: (p) =>
                    p.node?.group ? undefined : (p.data?.cost ?? 0),
                  valueFormatter: (p) =>
                    p.value == null ? "—" : `$${Number(p.value).toFixed(2)}`,
                },
                {
                  headerName: "CTR",
                  colId: "agg_ctr",
                  width: 80,
                  flex: 0,
                  hide: true,
                  // Only meaningful at group level — read sibling aggregates.
                  valueGetter: (p) => {
                    if (!p.node?.group) return undefined;
                    const a: any = p.node.aggData ?? {};
                    const s = Number(a.agg_sent ?? 0);
                    const c = Number(a.agg_clicks ?? 0);
                    return s > 0 ? c / s : null;
                  },
                  valueFormatter: (p) =>
                    p.value == null ? "—" : `${(Number(p.value) * 100).toFixed(1)}%`,
                },
                {
                  headerName: "Uniq CTR",
                  colId: "agg_uniq_ctr",
                  width: 90,
                  flex: 0,
                  hide: true,
                  valueGetter: (p) => {
                    if (!p.node?.group) return undefined;
                    const a: any = p.node.aggData ?? {};
                    const s = Number(a.agg_sent ?? 0);
                    const uniq =
                      a.agg_unique_clicks instanceof Set
                        ? a.agg_unique_clicks.size
                        : Number(a.agg_unique_clicks ?? 0);
                    return s > 0 ? uniq / s : null;
                  },
                  valueFormatter: (p) =>
                    p.value == null ? "—" : `${(Number(p.value) * 100).toFixed(1)}%`,
                },
                {
                  headerName: "CPC",
                  colId: "agg_cpc",
                  width: 90,
                  flex: 0,
                  hide: true,
                  valueGetter: (p) => {
                    if (!p.node?.group) return undefined;
                    const a: any = p.node.aggData ?? {};
                    const cost = Number(a.agg_cost ?? 0);
                    // CPC against UNIQUE clickers (Set built by uniqueCount).
                    const uniq =
                      a.agg_unique_clicks instanceof Set
                        ? a.agg_unique_clicks.size
                        : Number(a.agg_unique_clicks ?? 0);
                    return uniq > 0 ? cost / uniq : null;
                  },
                  valueFormatter: (p) =>
                    p.value == null ? "—" : `$${Number(p.value).toFixed(3)}`,
                },
                {
                  headerName: "CPL",
                  colId: "agg_cpl",
                  width: 90,
                  flex: 0,
                  hide: true,
                  valueGetter: (p) => {
                    if (!p.node?.group) return undefined;
                    const a: any = p.node.aggData ?? {};
                    const cost = Number(a.agg_cost ?? 0);
                    const leads = Number(a.agg_leads ?? 0);
                    return leads > 0 ? cost / leads : null;
                  },
                  valueFormatter: (p) =>
                    p.value == null ? "—" : `$${Number(p.value).toFixed(2)}`,
                },
              ]}
            />
          )}
        </div>
        )}
      </div>
      {evalCampaignId != null && (
        <EvaluateDialog
          campaignId={evalCampaignId}
          onClose={() => setEvalCampaignId(null)}
        />
      )}
    </PageShell>
  );
}

function FunnelChip({
  label,
  value,
  tone,
  rate,
}: {
  label: string;
  value: number | string;
  tone: "blue" | "amber" | "emerald" | "gray";
  rate?: string;
}) {
  const cls =
    tone === "blue"
      ? "bg-blue-100 dark:bg-blue-500/15 text-blue-700 dark:text-blue-300"
      : tone === "amber"
        ? "bg-amber-100 dark:bg-amber-500/15 text-amber-700 dark:text-amber-300"
        : tone === "emerald"
          ? "bg-emerald-100 dark:bg-emerald-500/15 text-emerald-700 dark:text-emerald-300"
          : "bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300";
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-1 rounded-md font-medium ${cls}`}
      title={rate ? `${label}: ${value} (${rate})` : `${label}: ${value}`}
    >
      <span className="opacity-70">{label}</span>
      <span className="font-semibold">{value}</span>
      {rate && <span className="opacity-60 text-[10px]">{rate}</span>}
    </span>
  );
}

function FunnelCard({
  label,
  value,
  tone,
  rate,
  rateLabel,
}: {
  label: string;
  value: number | string;
  tone: "blue" | "amber" | "emerald" | "gray";
  rate?: string;
  rateLabel?: string;
}) {
  const toneCls = {
    blue: "border-blue-200 dark:border-blue-500/30 bg-blue-50/50 dark:bg-blue-500/5",
    amber:
      "border-amber-200 dark:border-amber-500/30 bg-amber-50/50 dark:bg-amber-500/5",
    emerald:
      "border-emerald-200 dark:border-emerald-500/30 bg-emerald-50/50 dark:bg-emerald-500/5",
    gray: "border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-800/30",
  }[tone];
  const valueColor = {
    blue: "text-blue-700 dark:text-blue-300",
    amber: "text-amber-700 dark:text-amber-300",
    emerald: "text-emerald-700 dark:text-emerald-300",
    gray: "text-gray-700 dark:text-gray-200",
  }[tone];
  return (
    <div className={`border rounded-lg px-4 py-2 ${toneCls}`}>
      <div className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
        {label}
      </div>
      <div className={`text-2xl font-semibold ${valueColor}`}>{value}</div>
      {rate && (
        <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
          <span className="font-medium">{rate}</span>{" "}
          <span className="text-gray-400">{rateLabel}</span>
        </div>
      )}
    </div>
  );
}

// ---------- Users (RBAC, admin only) ----------

type ManagedUser = {
  id: number;
  email: string;
  role: "admin" | "analyst";
  labels: string[];
  created_at: string;
  updated_at: string;
};

function UsersTab() {
  const { data, loading, reload } = useAsync<ManagedUser[]>(() =>
    fetch("/api/users").then((r) => r.json() as Promise<ManagedUser[]>),
  );
  const labels = useAsync<string[]>(() =>
    fetch("/api/labels").then((r) => r.json() as Promise<string[]>),
  );
  const [editing, setEditing] = useState<ManagedUser | "new" | null>(null);
  const [deleting, setDeleting] = useState<ManagedUser | null>(null);

  return (
    <PageShell
      title="Users"
      total={data?.length ?? 0}
      onAdd={() => setEditing("new")}
      addLabel="+ User"
    >
      {loading ? (
        <div className="p-6 text-gray-400">Loading...</div>
      ) : (
        <AgTable<ManagedUser>
          rows={data ?? []}
          columns={[
            { field: "email", headerName: "Email", flex: 2 },
            {
              field: "role",
              headerName: "Role",
              width: 130,
              flex: 0,
              cellRenderer: (p: ICellRendererParams<ManagedUser>) => {
                const v = p.value as string;
                const cls =
                  v === "admin"
                    ? "bg-purple-100 dark:bg-purple-500/15 text-purple-700 dark:text-purple-300"
                    : "bg-blue-100 dark:bg-blue-500/15 text-blue-700 dark:text-blue-300";
                return (
                  <span
                    className={`px-2 py-0.5 rounded text-xs font-semibold uppercase ${cls}`}
                  >
                    {v}
                  </span>
                );
              },
            },
            {
              field: "labels",
              headerName: "Labels",
              flex: 3,
              valueGetter: (p) => (p.data?.labels ?? []).join(", "),
              cellRenderer: (p: ICellRendererParams<ManagedUser>) => {
                const list = p.data?.labels ?? [];
                if (list.length === 0)
                  return <span className="text-gray-400 text-xs">— (all)</span>;
                return (
                  <span
                    className="text-xs font-mono"
                    title={list.join(", ")}
                  >
                    {list.slice(0, 5).join(", ")}
                    {list.length > 5 ? ` +${list.length - 5}` : ""}
                  </span>
                );
              },
            },
            {
              field: "created_at",
              headerName: "Created",
              filter: "agDateColumnFilter",
              valueFormatter: (p) =>
                p.value ? new Date(p.value).toLocaleString() : "",
            },
          ]}
          onEdit={(r) => setEditing(r)}
          onDelete={(r) => setDeleting(r)}
        />
      )}
      {editing && (
        <UserDrawer
          user={editing === "new" ? null : editing}
          availableLabels={labels.data ?? []}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            reload();
            labels.reload();
          }}
        />
      )}
      <ConfirmDialog
        open={!!deleting}
        title="Delete user"
        message={
          deleting && (
            <>
              Remove access for{" "}
              <span className="font-semibold text-gray-900 dark:text-gray-100">
                {deleting.email}
              </span>
              ? Their session is revoked within 30s.
            </>
          )
        }
        onCancel={() => setDeleting(null)}
        onConfirm={async () => {
          if (!deleting) return;
          await fetch(`/api/users/${deleting.id}`, { method: "DELETE" });
          setDeleting(null);
          reload();
        }}
      />
    </PageShell>
  );
}

function UserDrawer({
  user,
  availableLabels,
  onClose,
  onSaved,
}: {
  user: ManagedUser | null;
  availableLabels: string[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const [email, setEmail] = useState(user?.email ?? "");
  const [role, setRole] = useState<"admin" | "analyst">(user?.role ?? "analyst");
  const [labels, setLabels] = useState<string[]>(user?.labels ?? []);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const toggleLabel = (l: string) => {
    setLabels((cur) =>
      cur.includes(l) ? cur.filter((x) => x !== l) : [...cur, l].sort(),
    );
  };

  async function save() {
    setError(null);
    const e = email.trim().toLowerCase();
    if (!e || !e.includes("@")) {
      setError("Valid email required");
      return;
    }
    setSaving(true);
    try {
      const body = { email: e, role, labels };
      const res = user
        ? await fetch(`/api/users/${user.id}`, {
            method: "PATCH",
            headers: { "content-type": "application/json" },
            body: JSON.stringify(body),
          })
        : await fetch("/api/users", {
            method: "POST",
            headers: { "content-type": "application/json" },
            body: JSON.stringify(body),
          });
      if (!res.ok) {
        setError(await res.text());
        return;
      }
      onSaved();
    } catch (e: any) {
      setError(String(e?.message ?? e));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Drawer
      title={user ? `Edit user #${user.id}` : "New user"}
      open
      onClose={onClose}
      onSave={save}
      saving={saving}
    >
      {error && (
        <div className="bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/30 text-red-700 dark:text-red-300 text-sm px-3 py-2 rounded mb-3">
          {error}
        </div>
      )}
      <Field label="Email" required>
        <input
          className={inputCls}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="user@example.com"
          autoFocus
        />
      </Field>
      <Field label="Role" required>
        <div className="inline-flex rounded-md border border-gray-300 dark:border-gray-700 overflow-hidden">
          {(["admin", "analyst"] as const).map((r, i) => (
            <button
              key={r}
              type="button"
              onClick={() => setRole(r)}
              className={
                "px-3 py-1.5 text-sm transition " +
                (i > 0 ? "border-l border-gray-300 dark:border-gray-700 " : "") +
                (role === r
                  ? r === "admin"
                    ? "bg-purple-600 text-white"
                    : "bg-blue-600 text-white"
                  : "bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700")
              }
            >
              {r}
            </button>
          ))}
        </div>
        <p className="text-xs text-gray-500 mt-1">
          {role === "admin"
            ? "Full access to all tabs and actions."
            : "Sees only Actions tab; restricted to phones from selected labels (import batches)."}
        </p>
      </Field>
      {role === "analyst" && (
        <Field label="Labels (import batches)">
          {availableLabels.length === 0 ? (
            <p className="text-xs text-gray-500">
              No import batches exist yet. Import phones with a batch name first.
            </p>
          ) : (
            <div className="flex flex-wrap gap-1.5">
              {availableLabels.map((l) => {
                const on = labels.includes(l);
                return (
                  <button
                    key={l}
                    type="button"
                    onClick={() => toggleLabel(l)}
                    className={
                      "px-2 py-1 text-xs rounded border transition font-mono " +
                      (on
                        ? "bg-blue-600 text-white border-blue-600"
                        : "bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700")
                    }
                  >
                    {l}
                  </button>
                );
              })}
            </div>
          )}
          <p className="text-xs text-gray-500 mt-1">
            {labels.length === 0
              ? "No labels selected — this analyst will see NO actions."
              : `${labels.length} selected. Analyst sees actions only for phones with these import_batch values.`}
          </p>
        </Field>
      )}
    </Drawer>
  );
}

// ---------- Provider Callbacks ----------

/**
 * Pull a field out of a callback's body, handling the shapes we get:
 *   1. body is already a JSON object (MessageWhiz)            → body[key]
 *   2. body is null, body_text is form-urlencoded with a      → parse "sms"
 *      `sms=<URL-encoded JSON>` field (SMSEdge DLR webhook)     field as JSON
 *   3. body is an object with a `sms` string field            → JSON.parse it
 *   4. body has a nested `payload` object (Android Gateway)   → body.payload[key]
 *   5. payload also stores fields under alternate names — map them:
 *        phone        ← payload.phoneNumber
 *        status       ← payload.state    (or top-level event "sms:delivered")
 *        dlr_status   ← payload.state
 *        error        ← payload.errorMessage
 */
function extractCallbackField(
  cb: ProviderCallback | undefined,
  key: string,
): string | null {
  if (!cb) return null;
  const pick = (obj: any, name: string): string | null => {
    if (!obj || typeof obj !== "object") return null;
    const v = obj[name];
    return v != null && v !== "" ? String(v) : null;
  };
  // Aliases — try these alternate names in order if the canonical isn't there.
  const aliases: Record<string, string[]> = {
    phone: ["phone", "to", "recipient", "msisdn", "phoneNumber"],
    status: ["status", "state"],
    dlr_status: ["dlr_status", "state"],
    error: ["error", "reason", "error_message", "errorMessage"],
    cost: ["cost", "price"],
  };
  const candidates = aliases[key] ?? [key];

  if (cb.body && typeof cb.body === "object") {
    const b = cb.body as any;
    // Direct
    for (const k of candidates) {
      const v = pick(b, k);
      if (v) return v;
    }
    // Android Gateway nested payload
    if (b.payload && typeof b.payload === "object") {
      for (const k of candidates) {
        const v = pick(b.payload, k);
        if (v) return v;
      }
      // Map top-level event ("sms:delivered") to status / dlr_status when
      // payload.state isn't present.
      if ((key === "status" || key === "dlr_status") && b.event) {
        const ev = String(b.event).toLowerCase();
        if (ev.startsWith("sms:")) return ev.slice(4);
      }
    }
    // SMSEdge nested-sms shape (object form)
    if (b.sms) {
      const nested =
        typeof b.sms === "string" ? safeJson(b.sms) : (b.sms as any);
      if (nested && typeof nested === "object") {
        for (const k of candidates) {
          const v = pick(nested, k);
          if (v) return v;
        }
      }
    }
  }
  if (cb.body_text) {
    try {
      const params = new URLSearchParams(cb.body_text);
      const sms = params.get("sms");
      if (sms) {
        const inner = safeJson(sms);
        if (inner && typeof inner === "object") {
          for (const k of candidates) {
            const v = pick(inner, k);
            if (v) return v;
          }
        }
      }
      for (const k of candidates) {
        const v = params.get(k);
        if (v) return v;
      }
    } catch {
      /* ignore */
    }
  }
  return null;
}

function safeJson(s: string): unknown {
  try {
    return JSON.parse(s);
  } catch {
    return null;
  }
}

function renderStatusCell(v: string) {
  if (!v) return <span className="text-gray-400">—</span>;
  const lower = v.toLowerCase();
  let cls = "bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200";
  if (["delivered", "delivrd", "sent"].includes(lower)) {
    cls =
      "bg-emerald-100 dark:bg-emerald-500/15 text-emerald-700 dark:text-emerald-300";
  } else if (
    [
      "failed",
      "undelivered",
      "undeliverable",
      "rejected",
      "expired",
      "error",
    ].includes(lower)
  ) {
    cls = "bg-red-100 dark:bg-red-500/15 text-red-700 dark:text-red-300";
  } else if (["waiting", "sending", "pending", "queued"].includes(lower)) {
    cls =
      "bg-amber-100 dark:bg-amber-500/15 text-amber-700 dark:text-amber-300";
  }
  return (
    <span className={`px-2 py-0.5 rounded text-xs font-mono ${cls}`}>{v}</span>
  );
}

function CallbacksTab() {
  const [provider, setProvider] = useState(
    () => localStorage.getItem("callbacks.provider") ?? "",
  );
  const [matched, setMatched] = useState(
    () => localStorage.getItem("callbacks.matched") ?? "",
  );
  const [filterResetKey, setFilterResetKey] = useState(0);
  useEffect(() => {
    localStorage.setItem("callbacks.provider", provider);
  }, [provider]);
  useEffect(() => {
    localStorage.setItem("callbacks.matched", matched);
  }, [matched]);

  const { data, loading, reload } = useAsync<ProviderCallback[]>(() => {
    const qs = new URLSearchParams();
    if (provider) qs.set("provider", provider);
    if (matched) qs.set("matched", matched);
    qs.set("limit", "1000");
    return fetch(`/api/provider-callbacks?${qs.toString()}`).then(
      (r) => r.json() as Promise<ProviderCallback[]>,
    );
  }, [provider, matched]);

  // Auto-refresh every 5 min. Long interval on purpose — re-rendering the
  // grid drops in-flight column filter / sort tweaks the user is making,
  // so we only refresh occasionally. Hit ↻ Refresh in the header for a
  // manual reload when you need it sooner.
  useEffect(() => {
    const t = setInterval(reload, 5 * 60_000);
    return () => clearInterval(t);
  }, [reload]);

  const [viewing, setViewing] = useState<ProviderCallback | null>(null);
  const [deleting, setDeleting] = useState<ProviderCallback | null>(null);

  return (
    <PageShell
      title="Callbacks"
      total={data?.length ?? 0}
      onAdd={reload}
      addLabel="↻ Refresh"
      extraHeader={
        <>
          <input
            value={provider}
            onChange={(e) => setProvider(e.target.value)}
            placeholder="Provider..."
            className="px-3 py-1.5 rounded-md border border-gray-300 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200 text-sm w-32"
          />
          <select
            value={matched}
            onChange={(e) => setMatched(e.target.value)}
            className="px-2 py-1.5 rounded-md border border-gray-300 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200 text-sm cursor-pointer"
          >
            <option value="">All</option>
            <option value="true">Matched</option>
            <option value="false">Unmatched</option>
          </select>
          <ResetFiltersButton onClick={() => setFilterResetKey((n) => n + 1)} />
        </>
      }
    >
      {loading && data === null ? (
        <div className="p-6 text-gray-400">Loading...</div>
      ) : (
        <AgTable<ProviderCallback>
          persistKey="provider_callbacks"
          filterResetKey={filterResetKey}
          rows={data ?? []}
          columns={[
            {
              field: "provider",
              headerName: "Provider",
              width: 120,
              flex: 0,
              cellRenderer: (p: ICellRendererParams<ProviderCallback>) => (
                <span className="font-medium">{p.value}</span>
              ),
            },
            {
              field: "method",
              headerName: "Method",
              width: 90,
              flex: 0,
            },
            {
              field: "matched",
              headerName: "Matched",
              width: 110,
              flex: 0,
              cellRenderer: (p: ICellRendererParams<ProviderCallback>) =>
                p.value ? (
                  <span className="px-2 py-0.5 rounded text-xs font-medium bg-emerald-100 dark:bg-emerald-500/15 text-emerald-700 dark:text-emerald-300">
                    matched
                  </span>
                ) : (
                  <span className="px-2 py-0.5 rounded text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300">
                    unmatched
                  </span>
                ),
            },
            {
              field: "matched_status",
              headerName: "Status",
              width: 140,
              flex: 0,
              valueFormatter: (p) => p.value ?? "—",
            },
            {
              headerName: "Body status",
              colId: "body_status",
              width: 130,
              flex: 0,
              valueGetter: (p) =>
                extractCallbackField(p.data, "status") ?? "",
              cellRenderer: (p: ICellRendererParams<ProviderCallback>) =>
                renderStatusCell(p.value as string),
            },
            {
              headerName: "Body dlr_status",
              colId: "body_dlr_status",
              width: 140,
              flex: 0,
              valueGetter: (p) =>
                extractCallbackField(p.data, "dlr_status") ?? "",
              cellRenderer: (p: ICellRendererParams<ProviderCallback>) =>
                renderStatusCell(p.value as string),
            },
            {
              headerName: "Phone",
              colId: "body_phone",
              width: 150,
              flex: 0,
              valueGetter: (p) =>
                extractCallbackField(p.data, "to") ??
                extractCallbackField(p.data, "phone") ??
                extractCallbackField(p.data, "recipient") ??
                extractCallbackField(p.data, "msisdn") ??
                "",
              cellRenderer: (p: ICellRendererParams<ProviderCallback>) => {
                const v = p.value as string;
                if (!v) return <span className="text-gray-400">—</span>;
                const normalized = v.startsWith("+") ? v : "+" + v;
                return <code className="text-xs">{normalized}</code>;
              },
            },
            {
              headerName: "Error",
              colId: "body_error",
              flex: 1,
              minWidth: 180,
              valueGetter: (p) => {
                const e =
                  extractCallbackField(p.data, "error") ??
                  extractCallbackField(p.data, "reason") ??
                  extractCallbackField(p.data, "error_message") ??
                  extractCallbackField(p.data, "errorMessage") ??
                  "";
                const code =
                  extractCallbackField(p.data, "error_code") ??
                  extractCallbackField(p.data, "errorCode") ??
                  "";
                if (e && code) return `[${code}] ${e}`;
                return e || code;
              },
              cellRenderer: (p: ICellRendererParams<ProviderCallback>) => {
                const v = p.value as string;
                if (!v) return <span className="text-gray-400">—</span>;
                return (
                  <span
                    className="text-xs text-red-600 dark:text-red-400"
                    title={v}
                  >
                    {v.length > 60 ? v.slice(0, 60) + "…" : v}
                  </span>
                );
              },
            },
            {
              headerName: "Cost",
              colId: "body_cost",
              width: 100,
              flex: 0,
              type: "rightAligned",
              filter: "agNumberColumnFilter",
              valueGetter: (p) => {
                const raw =
                  extractCallbackField(p.data, "cost") ??
                  extractCallbackField(p.data, "price");
                if (raw == null || raw === "") return null;
                const n = Number(raw);
                return Number.isFinite(n) ? n : null;
              },
              cellRenderer: (p: ICellRendererParams<ProviderCallback>) => {
                const v = p.value as number | null;
                if (v == null) return <span className="text-gray-400">—</span>;
                return (
                  <span className="text-xs font-mono tabular-nums">
                    ${v.toFixed(4)}
                  </span>
                );
              },
            },
            {
              field: "message_id",
              headerName: "Message ID",
              cellRenderer: (p: ICellRendererParams<ProviderCallback>) =>
                p.value ? (
                  <code className="text-xs">{p.value}</code>
                ) : (
                  <span className="text-gray-400">—</span>
                ),
              width: 160,
              flex: 0,
            },
            {
              headerName: "Query",
              colId: "query_preview",
              valueGetter: (p) =>
                p.data?.query ? JSON.stringify(p.data.query) : "",
              cellRenderer: (p: ICellRendererParams<ProviderCallback>) => (
                <code className="text-xs text-gray-600 dark:text-gray-400">
                  {p.value && p.value.length > 80
                    ? p.value.slice(0, 80) + "..."
                    : p.value || "—"}
                </code>
              ),
              flex: 2,
            },
            {
              field: "ip",
              headerName: "IP",
              valueFormatter: (p) => p.value ?? "—",
              width: 140,
              flex: 0,
            },
            {
              field: "created_at",
              headerName: "When",
              filter: "agDateColumnFilter",
              valueFormatter: (p) =>
                p.value ? new Date(p.value).toLocaleString() : "",
              width: 180,
              flex: 0,
            },
          ]}
          onDelete={(r) => setDeleting(r)}
          extraActions={(row) => [
            {
              icon: (
                <span className="inline-flex items-center justify-center w-4 h-4 rounded-full border border-current text-[10px] font-bold leading-none">
                  i
                </span>
              ),
              onClick: () => setViewing(row),
              title: "Details",
              className:
                "text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-800",
            },
          ]}
          actionsWidth={110}
        />
      )}
      {viewing && (
        <CallbackDetailDrawer
          callback={viewing}
          onClose={() => setViewing(null)}
        />
      )}
      <ConfirmDialog
        open={!!deleting}
        title="Delete callback"
        message={`Delete callback #${deleting?.id} from ${deleting?.provider}?`}
        onConfirm={async () => {
          if (!deleting) return;
          await fetch(`/api/provider-callbacks/${deleting.id}`, { method: "DELETE" });
          setDeleting(null);
          reload();
        }}
        onCancel={() => setDeleting(null)}
      />
    </PageShell>
  );
}

function LiveStatusBar({
  status,
}: {
  status: {
    counts: Record<string, number>;
    total: number;
    last: {
      phone: string | null;
      status: string;
      error_message: string | null;
      updated_at: string;
    } | null;
    account_frozen?: {
      balance: number | null;
      threshold: number;
      frozen: boolean;
    } | null;
  };
}) {
  const c = status.counts;
  const sent = (c.sent_provider ?? 0) + (c.ready ?? 0);
  const errors = (c.error ?? 0) + (c.error_provider ?? 0);
  const STATUS_TONE: Record<string, string> = {
    paused: "bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200",
    pending: "bg-amber-100 dark:bg-amber-500/15 text-amber-700 dark:text-amber-300",
    ready: "bg-emerald-100 dark:bg-emerald-500/15 text-emerald-700 dark:text-emerald-300",
    sent_provider:
      "bg-emerald-100 dark:bg-emerald-500/15 text-emerald-700 dark:text-emerald-300",
    error: "bg-red-100 dark:bg-red-500/15 text-red-700 dark:text-red-300",
    error_provider:
      "bg-red-100 dark:bg-red-500/15 text-red-700 dark:text-red-300",
  };
  const last = status.last;
  const ago = last
    ? (() => {
        const diff = Math.max(
          0,
          Math.floor((Date.now() - new Date(last.updated_at).getTime()) / 1000),
        );
        if (diff < 60) return `${diff}s ago`;
        if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
        return `${Math.floor(diff / 3600)}h ago`;
      })()
    : null;
  const af = status.account_frozen;
  return (
    <span className="ml-3 inline-flex items-center gap-1.5 text-xs whitespace-nowrap">
      {af?.frozen && (
        <span
          className="px-2 py-0.5 rounded font-semibold bg-red-100 dark:bg-red-500/15 text-red-700 dark:text-red-300 uppercase tracking-wide"
          title={`Worker is skipping pending phones — account balance $${(af.balance ?? 0).toFixed(2)} is below threshold $${af.threshold.toFixed(2)}. Top up the account in the provider dashboard; sending resumes automatically on next balance refresh.`}
        >
          ⚠ low balance ${(af.balance ?? 0).toFixed(2)}
        </span>
      )}
      {last && (
        <span
          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded font-mono ${
            STATUS_TONE[last.status] ??
            "bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200"
          }`}
          title={
            last.error_message
              ? `${last.phone ?? "—"} · ${last.status} · ${last.error_message}`
              : `${last.phone ?? "—"} · ${last.status} · ${last.updated_at}`
          }
        >
          <span className="opacity-60">→</span>
          <span>{last.phone ?? "—"}</span>
          <span className="opacity-60">·</span>
          <span className="font-semibold uppercase tracking-wide">
            {last.status}
          </span>
          <span className="opacity-50">({ago})</span>
        </span>
      )}
      <span
        className="px-1.5 py-0.5 rounded bg-gray-50 dark:bg-gray-800 text-gray-700 dark:text-gray-300"
        title="Sent or ready for DLR"
      >
        sent <b>{sent}</b>
      </span>
      <span
        className="px-1.5 py-0.5 rounded bg-amber-50 dark:bg-amber-500/10 text-amber-700 dark:text-amber-300"
        title="Pending — worker will pick these"
      >
        pending <b>{c.pending ?? 0}</b>
      </span>
      <span
        className="px-1.5 py-0.5 rounded bg-gray-50 dark:bg-gray-800 text-gray-700 dark:text-gray-300"
        title="Paused — worker ignores"
      >
        paused <b>{c.paused ?? 0}</b>
      </span>
      {errors > 0 && (
        <span
          className="px-1.5 py-0.5 rounded bg-red-50 dark:bg-red-500/10 text-red-700 dark:text-red-300"
          title="Errors (worker + provider)"
        >
          err <b>{errors}</b>
        </span>
      )}
      <span className="text-gray-400">/ {status.total}</span>
    </span>
  );
}

function FormUrlEncodedBody({ text }: { text: string }) {
  // Try to parse as application/x-www-form-urlencoded; show as decoded
  // key/value pairs (JSON-pretty-printing values that look like JSON).
  // Fall back to raw text if it doesn't look like form-encoded.
  const looksFormEncoded = /^[\w.\-\[\]]+=/.test(text) && text.includes("=");
  let pairs: Array<[string, string]> | null = null;
  if (looksFormEncoded) {
    try {
      const params = new URLSearchParams(text);
      const out: Array<[string, string]> = [];
      for (const [k, v] of params) out.push([k, v]);
      if (out.length > 0) pairs = out;
    } catch {}
  }
  if (!pairs) {
    return (
      <pre className="text-xs font-mono whitespace-pre-wrap break-all bg-gray-50 dark:bg-gray-800 p-2 rounded">
        {text}
      </pre>
    );
  }
  return (
    <div className="space-y-2">
      {pairs.map(([k, v], i) => {
        let pretty: string;
        try {
          pretty = JSON.stringify(JSON.parse(v), null, 2);
        } catch {
          pretty = v;
        }
        return (
          <div key={i}>
            <div className="text-[11px] font-mono text-gray-500 mb-0.5">{k}</div>
            <pre className="text-xs font-mono whitespace-pre-wrap break-all bg-gray-50 dark:bg-gray-800 p-2 rounded">
              {pretty}
            </pre>
          </div>
        );
      })}
    </div>
  );
}

function CallbackDetailDrawer({
  callback,
  onClose,
}: {
  callback: ProviderCallback;
  onClose: () => void;
}) {
  return (
    <Drawer
      title={`Callback #${callback.id} — ${callback.provider}`}
      open={true}
      onClose={onClose}
      onSave={onClose}
      saving={false}
      width={720}
      showSave={false}
    >
      <div className="grid grid-cols-2 gap-3 text-xs">
        <Info label="Method" value={callback.method} />
        <Info
          label="When"
          value={new Date(callback.created_at).toLocaleString()}
        />
        <Info
          label="Matched"
          value={callback.matched ? "yes" : "no"}
          tone={callback.matched ? "emerald" : "gray"}
        />
        <Info label="Status" value={callback.matched_status ?? "—"} />
        <Info label="Message ID" value={callback.message_id ?? "—"} mono />
        <Info label="IP" value={callback.ip ?? "—"} mono />
      </div>
      <Section label="URL">
        <code className="block break-all text-xs">{callback.url}</code>
      </Section>
      <Section label="Query">
        <JsonView value={callback.query} />
      </Section>
      <Section label="Body">
        {callback.body !== null ? (
          <JsonView value={callback.body} />
        ) : callback.body_text ? (
          <FormUrlEncodedBody text={callback.body_text} />
        ) : (
          <div className="text-xs text-gray-400">empty</div>
        )}
      </Section>
      <Section label="Headers">
        <JsonView value={callback.headers} />
      </Section>
    </Drawer>
  );
}

function Info({
  label,
  value,
  mono,
  tone,
}: {
  label: string;
  value: string;
  mono?: boolean;
  tone?: "emerald" | "gray";
}) {
  return (
    <div>
      <div className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">
        {label}
      </div>
      <div
        className={
          (mono ? "font-mono " : "") +
          "text-sm dark:text-gray-200 " +
          (tone === "emerald"
            ? "text-emerald-700 dark:text-emerald-300 font-medium"
            : "")
        }
      >
        {value}
      </div>
    </div>
  );
}

function Section({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="mt-4">
      <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1">
        {label}
      </div>
      {children}
    </div>
  );
}

function JsonView({ value }: { value: unknown }) {
  return (
    <pre className="text-xs font-mono whitespace-pre-wrap break-all bg-gray-50 dark:bg-gray-800 p-2 rounded max-h-[300px] overflow-auto">
      {JSON.stringify(value, null, 2)}
    </pre>
  );
}

// ---------- Routes (catalog) ----------

function RoutesTab() {
  const [country, setCountry] = useState(
    () => localStorage.getItem("routes.country") ?? "",
  );
  const [active, setActive] = useState(
    () => localStorage.getItem("routes.active") ?? "true",
  );
  const [syncing, setSyncing] = useState<null | "smsedge" | "google">(null);
  const [syncMsg, setSyncMsg] = useState<string | null>(null);
  const [filterResetKey, setFilterResetKey] = useState(0);

  useEffect(() => {
    localStorage.setItem("routes.country", country);
  }, [country]);
  useEffect(() => {
    localStorage.setItem("routes.active", active);
  }, [active]);

  const { data, loading, reload } = useAsync<Route[]>(() => {
    const qs = new URLSearchParams();
    if (country) qs.set("country", country);
    if (active) qs.set("active", active);
    qs.set("limit", "10000");
    return fetch(`/api/routes?${qs.toString()}`).then(
      (r) => r.json() as Promise<Route[]>,
    );
  }, [country, active]);

  async function syncFrom(source: "smsedge" | "google") {
    setSyncing(source);
    setSyncMsg(null);
    const endpoint =
      source === "smsedge" ? "/api/routes/sync" : "/api/routes/sync-google";
    try {
      const res = await fetch(endpoint, { method: "POST" });
      const body = (await res.json()) as
        | { upserted?: number; deactivated?: number }
        | Record<string, unknown>;
      if (!res.ok) {
        setSyncMsg(`Failed (${source}): ${JSON.stringify(body)}`);
      } else {
        const u = (body as any).upserted ?? 0;
        const d = (body as any).deactivated ?? 0;
        setSyncMsg(
          `[${source}] sync done — upserted ${u}, deactivated ${d}`,
        );
        await reload();
      }
    } catch (e: any) {
      setSyncMsg(`Failed (${source}): ${e?.message ?? e}`);
    } finally {
      setSyncing(null);
    }
  }

  return (
    <PageShell
      title="Routes"
      total={data?.length ?? 0}
      onAdd={reload}
      addLabel="↻ Refresh"
      extraHeader={
        <>
          <input
            value={country}
            onChange={(e) => setCountry(e.target.value.toUpperCase())}
            placeholder="ISO2 (e.g. IL)"
            maxLength={2}
            className="px-3 py-1.5 rounded-md border border-gray-300 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200 text-sm w-28 uppercase"
          />
          <select
            value={active}
            onChange={(e) => setActive(e.target.value)}
            className="px-2 py-1.5 rounded-md border border-gray-300 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200 text-sm cursor-pointer"
          >
            <option value="true">Active only</option>
            <option value="false">Inactive only</option>
            <option value="">All</option>
          </select>
          <ResetFiltersButton onClick={() => setFilterResetKey((n) => n + 1)} />
          <button
            onClick={() => syncFrom("smsedge")}
            disabled={syncing !== null}
            className="px-3 py-1.5 rounded-md bg-blue-600 hover:bg-blue-700 text-white text-sm cursor-pointer disabled:opacity-50 inline-flex items-center gap-1.5"
            title="Pull live route catalog from SMSEdge API"
          >
            {syncing === "smsedge" && (
              <span className="inline-block w-3 h-3 border-2 border-white/40 border-t-white rounded-full animate-spin" />
            )}
            {syncing === "smsedge" ? "Loading…" : "Sync SMSEdge"}
          </button>
          <button
            onClick={() => syncFrom("google")}
            disabled={syncing !== null}
            className="px-3 py-1.5 rounded-md bg-green-600 hover:bg-green-700 text-white text-sm cursor-pointer disabled:opacity-50 inline-flex items-center gap-1.5"
            title="Pull curated SMSEdge routes from the Google Sheet (per-country list)"
          >
            {syncing === "google" && (
              <span className="inline-block w-3 h-3 border-2 border-white/40 border-t-white rounded-full animate-spin" />
            )}
            {syncing === "google" ? "Loading…" : "Sync Google"}
          </button>
        </>
      }
    >
      {syncMsg && (
        <div className="px-5 py-2 bg-blue-50 dark:bg-blue-500/10 border-b border-blue-200 dark:border-blue-500/30 text-xs text-blue-700 dark:text-blue-300">
          {syncMsg}
        </div>
      )}
      {(loading && data === null) || syncing !== null ? (
        <div className="p-6 text-gray-400 inline-flex items-center gap-2">
          <span className="inline-block w-4 h-4 border-2 border-gray-300 border-t-blue-500 rounded-full animate-spin" />
          Loading…
        </div>
      ) : (
        <AgTable<Route>
          persistKey="routes_catalog"
          filterResetKey={filterResetKey}
          rows={data ?? []}
          columns={[
            {
              field: "provider",
              headerName: "Provider",
              width: 110,
              flex: 0,
            },
            {
              field: "external_id",
              headerName: "External ID",
              width: 110,
              flex: 0,
              cellRenderer: (p: ICellRendererParams<Route>) => (
                <code className="text-xs">{p.value}</code>
              ),
            },
            {
              field: "name",
              headerName: "Name",
              cellRenderer: (p: ICellRendererParams<Route>) => (
                <span className="font-medium">{p.value}</span>
              ),
            },
            {
              field: "country_iso2",
              headerName: "ISO2",
              width: 80,
              flex: 0,
              valueFormatter: (p) => p.value ?? "—",
              cellRenderer: (p: ICellRendererParams<Route>) =>
                p.value ? (
                  <span className="px-1.5 py-0.5 rounded bg-blue-100 dark:bg-blue-500/15 text-blue-700 dark:text-blue-300 text-xs font-mono font-semibold">
                    {p.value}
                  </span>
                ) : (
                  <span className="text-gray-400">—</span>
                ),
            },
            {
              field: "external_country_name",
              headerName: "Country (provider)",
              valueFormatter: (p) => p.value ?? "—",
            },
            {
              field: "external_country_id",
              headerName: "Country ID",
              width: 110,
              flex: 0,
              valueFormatter: (p) => p.value ?? "—",
            },
            {
              field: "price",
              headerName: "Price",
              filter: "agNumberColumnFilter",
              valueFormatter: (p) =>
                p.value == null ? "—" : Number(p.value).toFixed(6),
              width: 110,
              flex: 0,
            },
            {
              field: "active",
              headerName: "Active",
              width: 90,
              flex: 0,
              cellRenderer: (p: ICellRendererParams<Route>) =>
                p.value ? (
                  <span className="px-1.5 py-0.5 rounded bg-emerald-100 dark:bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 text-xs font-medium">
                    yes
                  </span>
                ) : (
                  <span className="px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 text-xs font-medium">
                    no
                  </span>
                ),
            },
            {
              field: "updated_at",
              headerName: "Updated",
              filter: "agDateColumnFilter",
              valueFormatter: (p) =>
                p.value ? new Date(p.value).toLocaleString() : "",
              width: 180,
              flex: 0,
            },
          ]}
        />
      )}
    </PageShell>
  );
}

createRoot(document.getElementById("root")!).render(<Root />);
